import React, { useState, useEffect, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import { avecCle } from '../services/cleLecture';
import DocumentViewer from './DocumentViewer';
import { estCvDocument } from '../utils/cvDocument';
import AoInfosContrat from './AoInfosContrat';
import AoRecapSociete from './AoRecapSociete';
import { useConfirm } from './ConfirmDialog';
import AoAssistant, { piecePriority, pieceKind } from './AoAssistant';
import AoSociete, { DEFAULT_SOCIETE_DOCS, isAoAdmin, societeAlerts } from './AoSociete';
import AoOrbe from './AoOrbe';
import AoLivraison from './AoLivraison';
import AoRattacherBesoin from './AoRattacherBesoin';
import AoCartesPhotos from './AoCartesPhotos';
import RevueCvAo from './RevueCvAo';
import StatutAo from './StatutAo';
import CommercialCvAo from './CommercialCvAo';
import { GrilleAo } from './GrilleAdjudication';
import { saveAoCache, readAoCache } from '../services/aoCache';
import htmlSur from '../services/htmlSur';
import { MatchingLancementModal, ReglagesMatchingBanner } from './MatchingReglages';
import AoControleQualite, { estJobControle } from './AoControleQualite';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} />;

// Statuts de pièce (couleur = signal, jetons ERP Shell : vert = acquis,
// corail = une action à faire, ambre = il manque quelque chose, rouge = refus).
const PIECE = {
  present:    { label: 'Présent',    bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)', icon: 'check-circle-fill' },
  // Le document est dans l'ERP mais pas encore dans le dossier kDrive.
  a_uploader: { label: 'À copier sur kDrive', bg: 'var(--erp-accent-soft)', fg: 'var(--accent-soft-fg)', icon: 'cloud-upload' },
  a_generer:  { label: 'À générer',  bg: 'var(--erp-accent-soft)',  fg: 'var(--accent-soft-fg)', icon: 'stars' },
  a_fournir:  { label: 'À fournir',  bg: 'var(--erp-warning-soft)', fg: 'var(--erp-warning)', icon: 'exclamation-triangle' },
  // Cycle de vie de la pièce « Contrat » (générée depuis le wizard AO,
  // statut synchronisé par le back via les webhooks signd)
  brouillon:     { label: 'Brouillon',    bg: 'var(--chip-bg)',          fg: 'var(--text-2)', icon: 'file-earmark' },
  en_signature:  { label: 'En signature', bg: 'var(--erp-accent-soft)',  fg: 'var(--accent-soft-fg)', icon: 'pen' },
  signe:         { label: 'Signé',        bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)', icon: 'patch-check-fill' },
  refuse:        { label: 'Refusé',       bg: 'var(--erp-danger-soft)',  fg: 'var(--erp-danger)', icon: 'x-circle' },
};
const AO_STATUT = {
  draft:  { label: 'Brouillon',    bg: 'var(--chip-bg)',          fg: 'var(--text-2)' },
  synced: { label: 'Synchronisé',  bg: 'var(--erp-success-soft)', fg: 'var(--erp-success)' },
};

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const label = { display: 'block', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '7px' };
const input = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 16px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' };
const btnGhost = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' };
const sectTitle = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '10px' };
// Les boutons de pièce/dossier utilisent les classes ERP Shell
// (erp-btn erp-btn--{primary|secondary|danger} erp-btn--sm) — plus de style inline.

const fmtSize = (b) => { if (b == null || isNaN(b)) return ''; if (b < 1024) return `${b} o`; if (b < 1048576) return `${Math.round(b / 1024)} Ko`; return `${(b / 1048576).toFixed(1)} Mo`; };

// ── Motif « dossier » ──
const DOSSIER_LVL = {
  ao:        { radius: '3px 16px 16px 16px', mt: 22, tab: { top: -14, left: 24, w: 104, h: 16 } },
  lot:       { radius: '3px 14px 14px 14px', mt: 13, tab: { top: -12, left: 16, w: 92, h: 13 } },
  candidate: { radius: '2px 12px 12px 12px', mt: 14, tab: { top: -11, left: -1, w: 104, h: 12 } },
};
function Dossier({ level = 'lot', style, children, className = '', ...rest }) {
  const d = DOSSIER_LVL[level] || DOSSIER_LVL.lot;
  return (
    <div {...rest} className={`ats-fold ${className}`.trim()} style={{ position: 'relative', marginTop: `${d.mt}px`, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: d.radius, ...style }}>
      <div style={{ position: 'absolute', top: `${d.tab.top}px`, left: `${d.tab.left}px`, width: `${d.tab.w}px`, height: `${d.tab.h}px`, background: 'var(--surface)', border: '1px solid var(--border)', borderBottom: 'none', borderRadius: '7px 7px 0 0' }} />
      {children}
    </div>
  );
}

function Monogram({ text, size = 44 }) {
  return (
    <div style={{ position: 'relative', width: `${size}px`, height: `${size}px`, borderRadius: '4px 9px 9px 9px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: size > 44 ? '15px' : '13px', fontWeight: 600, flex: 'none' }}>
      <span style={{ position: 'absolute', top: '-5px', left: '7px', width: '18px', height: '6px', background: 'var(--text-strong)', borderRadius: '3px 3px 0 0' }} />
      {(text || 'AO').slice(0, 2).toUpperCase()}
    </div>
  );
}


const Badge = ({ bg, fg, children, title }) => (
  <span title={title} style={{ fontSize: '11px', fontWeight: 600, padding: '2px 9px', borderRadius: '6px', background: bg, color: fg, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: title ? 'help' : undefined }}>{children}</span>
);

export default function DossiersAO({ showNotification, onOpenCandidat, onOpenBesoin, routeAoId, onOpenAo, onCloseAo }) {
  const [view, setView] = useState('list');
  const authHeaders = { Authorization: `Bearer ${APIService.getToken()}` };
  const { confirm, confirmElement } = useConfirm();

  // ── Liste ──
  const [aoList, setAoList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listLayout, setListLayout] = useState('list');
  const loadList = async () => {
    setLoadingList(true);
    try { const r = await fetch(`${BASE}/ao`, { headers: authHeaders }); const j = await r.json(); if (j.success) setAoList(j.data || []); }
    catch (e) { showNotification?.(`Erreur chargement AO : ${e.message}`, 'error'); }
    finally { setLoadingList(false); }
  };
  useEffect(() => { loadList(); }, []);

  // Suppression d'AO : le geste avait été retiré après une suppression par
  // erreur humaine. Il revient sous les conditions prévues alors — réservé à
  // l'administration ET double confirmation, la seconde rappelant tout ce qui
  // part avec le dossier.
  const deleteAo = async (ao) => {
    const nb = (ao.besoin_ids || []).length;
    if (!(await confirm({ title: 'Supprimer l\'AO', message: `Supprimer l'AO « ${ao.titre} » ?`, detail: `Tout le dossier part avec : ${nb} besoin${nb > 1 ? 's' : ''} rattaché${nb > 1 ? 's' : ''}, lots, suivis de pièces et rapports. Cette action est irréversible.`, confirmLabel: 'Continuer' }))) return;
    if (!(await confirm({ title: 'Dernière vérification', message: `Confirmer la suppression DÉFINITIVE de « ${ao.titre} » ?`, detail: 'Une suppression accidentelle a déjà eu lieu — d\'où cette seconde confirmation. Il n\'y a pas de corbeille.', confirmLabel: 'Supprimer définitivement' }))) return;
    try {
      const r = await fetch(`${BASE}/ao/${ao.id}`, { method: 'DELETE', headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setAoList(prev => prev.filter(a => a.id !== ao.id));
      showNotification?.('AO supprimé', 'success');
      if (String(aoId) === String(ao.id)) { loadList(); if (onCloseAo) onCloseAo(); else setView('list'); }
    } catch (e) { showNotification?.(`Erreur suppression : ${e.message}`, 'error'); }
  };

  // ── Formulaire nouveau ──
  const [besoins, setBesoins] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [aoTitre, setAoTitre] = useState('');
  const [rechercheBesoin, setRechercheBesoin] = useState('');
  const [client, setClient] = useState('');
  const [template, setTemplate] = useState('');
  const [kdriveFolderId, setKdriveFolderId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showContracts, setShowContracts] = useState(false);
  const [showInfosContrat, setShowInfosContrat] = useState(false);
  const [showRecap, setShowRecap] = useState(false);

  const openNew = async () => {
    setSelected(new Set()); setAoTitre(''); setClient(''); setTemplate(''); setKdriveFolderId(''); setRechercheBesoin('');
    setReport(null); setLotsAo([]); setView('new');
    try { const r = await fetch(`${BASE}/besoins?limit=200`, { headers: authHeaders }); const j = await r.json(); if (j.success) setBesoins(j.data || []); } catch (e) {}
  };
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── Rapport / scan ──
  const [report, setReport] = useState(null);
  // GET /ao/:id → `lots` : { besoin_id, titre, reference_interne } — le NOM
  // INTERNE de chaque lot, lu en direct sur le besoin (même sans rapport).
  const [lotsAo, setLotsAo] = useState([]);
  const [aoId, setAoId] = useState(null);
  const [aoMeta, setAoMeta] = useState(null);
  const [aoBesoinIds, setAoBesoinIds] = useState([]);
  const [kdriveUrl, setKdriveUrl] = useState(null);
  const [aoSync, setAoSync] = useState(null);
  const readSync = (src) => {
    if (!src || typeof src !== 'object') return;
    const statut = src.statut || src.status;
    const lastSyncedAt = src.last_synced_at ?? src.lastSyncedAt ?? null;
    if (statut || lastSyncedAt) setAoSync(prev => ({ statut: statut || prev?.statut || 'draft', lastSyncedAt: lastSyncedAt ?? prev?.lastSyncedAt ?? null }));
  };

  // ── Assistant guidé de complétion ──
  const [assistOpen, setAssistOpen] = useState(false);
  // L'assistant a deux modes : compléter les pièces, ou optimiser les CV d'un
  // lot. Ils partagent le dossier animé et l'état des suggestions.
  const [assistIdx, setAssistIdx] = useState(0);
  const [assistDone, setAssistDone] = useState({});

  // ── Administratif société (administrateur uniquement) ──
  // Les pièces sont chargées ici, pas dans l'onglet : le badge d'alerte doit
  // être visible sans avoir à ouvrir l'onglet.
  const isAdmin = isAoAdmin(APIService.getCurrentUser());
  // La pièce « Contrat » d'un dossier candidat est un document contractuel :
  // comme le bouton qui la génère, elle est réservée à l'administration. La
  // masquer seulement dans la liste ne suffirait pas — l'assistant « Compléter
  // le dossier » et les compteurs la réclameraient encore ; tous passent donc
  // par le même filtre.
  const estPieceContrat = (pc) => /contrat/i.test(pc?.label || '') && !/matrice|cadre/i.test(pc?.label || '');
  const piecesVisibles = (f) => (f?.pieces || []).filter(pc => isAdmin || !estPieceContrat(pc));
  // Même règle pour un NOM de fichier : le contrat signé est archivé comme
  // fichier libre (« Contrat_Prenom_NOM.pdf »), hors checklist — le filtrer
  // seulement dans les pièces le laissait visible en fichier libre, dans
  // l'arborescence kDrive et dans le sélecteur de documents.
  const estNomContrat = (nom) => (/contrat/i.test(nom || '') || /(^|[^a-z])(cdi|cdd)[_ -]/i.test(nom || '')) && !/matrice|cadre/i.test(nom || '');
  const estDocContrat = (d) => d?.auto_signe === true || estNomContrat(`${d?.type_piece || d?.type || ''} ${d?.source || ''}`)
    || estNomContrat(`${d?.label || ''} ${d?.name || d?.original_name || d?.file_name || ''}`);
  const [socDocs, setSocDocs] = useState(DEFAULT_SOCIETE_DOCS);
  const [socSaving, setSocSaving] = useState(false);
  const [socStoreKo, setSocStoreKo] = useState(false);
  // 403 = le serveur refuse ces données à ce compte : on retire l'onglet plutôt
  // que d'afficher une coquille vide.
  const [socForbidden, setSocForbidden] = useState(false);
  const socOn = isAdmin && !socForbidden;
  useEffect(() => {
    if (!isAdmin || aoId == null) return;
    let alive = true;
    (async () => {
      try {
        // ao_id obligatoire : les pièces scope 'ao' ont leur date par dossier.
        const r = await fetch(`${BASE}/config/ao_societe?ao_id=${encodeURIComponent(aoId)}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        if (r.status === 403) { if (alive) setSocForbidden(true); return; }
        if (r.status === 404 || r.status === 501) { if (alive) setSocStoreKo(true); return; }
        const j = await r.json().catch(() => ({}));
        const raw = j.data ?? j; const d = raw.value ?? raw;
        if (alive && Array.isArray(d?.docs) && d.docs.length) setSocDocs(d.docs);
      } catch { if (alive) setSocStoreKo(true); }
    })();
    return () => { alive = false; };
  }, [isAdmin, aoId]);
  const persistSocDocs = async (next) => {
    setSocDocs(next);
    if (socStoreKo) return;
    setSocSaving(true);
    try {
      const r = await fetch(`${BASE}/config/ao_societe`, {
        method: 'PUT', headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: { docs: next }, ao_id: aoId }),
      });
      if (r.status === 403) { setSocForbidden(true); return; }
      if (r.status === 404 || r.status === 501) setSocStoreKo(true);
      else if (!r.ok) showNotification?.('Enregistrement des pièces société refusé par le serveur — rechargez la page.', 'error');
    } catch { setSocStoreKo(true); }
    finally { setSocSaving(false); }
  };

  // ── Onglet Pièces / Matching ──
  const [reportTab, setReportTab] = useState('pieces');
  const [pieceQuery, setPieceQuery] = useState('');
  const [piecesMode, setPiecesMode] = useState('dossier');
  const [expandedLot, setExpandedLot] = useState(null);
  // La revue des CV (30.10) : parcours guidé, comme le tutoriel.
  const [revueCv, setRevueCv] = useState(false);   // false | 'revue' | 'machine'
  const [creatingLot, setCreatingLot] = useState({});
  const [deletingLot, setDeletingLot] = useState({});
  const [deletingCand, setDeletingCand] = useState({});
  const [matching, setMatching] = useState(null);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingUnavailable, setMatchingUnavailable] = useState(false);
  const [matchingStatus, setMatchingStatus] = useState(null);
  // Date du dernier scan kDrive (scanned_at) — absente sur les vieux rapports.
  const [scannedAt, setScannedAt] = useState(null);
  // Nomenclature du dossier partagé (ao_dossier.nommage, § 2 bis) : lue dans
  // GET /ao/:id, réglée dans le panneau du contrôle qualité.
  const [aoNommage, setAoNommage] = useState(null);
  const [matchingPonderations, setMatchingPonderations] = useState(null);
  const [matchingProgress, setMatchingProgress] = useState(null);
  const [matchingDupes, setMatchingDupes] = useState([]);
  const [recomputing, setRecomputing] = useState(false);
  const [expandedCand, setExpandedCand] = useState(() => new Set());
  const toggleCand = (k) => setExpandedCand(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const matchingPoll = React.useRef({ token: 0 });

  const loadMatching = async (id) => {
    if (!id) return;
    const myToken = ++matchingPoll.current.token;
    setMatchingUnavailable(false);
    setMatching(prev => { if (prev == null) setMatchingLoading(true); return prev; });
    const tick = async () => {
      try {
        const r = await fetch(`${BASE}/ao/${id}/matching`, { headers: authHeaders });
        if (matchingPoll.current.token !== myToken) return;
        if (r.status === 404 || r.status === 501) { setMatchingUnavailable(true); setMatching(null); setMatchingLoading(false); return; }
        const j = await r.json();
        if (j.success === false) throw new Error(j.error || 'Erreur');
        const arr = j.besoins || j.data?.besoins || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
        setMatching(Array.isArray(arr) ? arr : []);
        // Le serveur fusionne déjà les groupes d'un même poste et signale les
        // fiches besoin en double : on relaie l'info sans bloquer la lecture.
        setMatchingDupes(Array.isArray(j.duplicates) ? j.duplicates : (Array.isArray(j.data?.duplicates) ? j.data.duplicates : []));
        setMatchingStatus(j.status || 'ready');
        setMatchingProgress(j.progress || null);
        // Poids du calcul affiché : seulement si le serveur dit qu'ils étaient
        // ajustés — sinon le bandeau annonce les défauts de l'organisation.
        // Deux écritures serveur tolérées : ponderations_appliquees='ajustees'
        // (+ ponderations), ou ponderations_ajustees (objet de poids, ou true).
        const pondAj = j.ponderations_ajustees;
        const ajuste = j.ponderations_appliquees === 'ajustees' || pondAj === true || (pondAj && typeof pondAj === 'object');
        setMatchingPonderations(ajuste
          ? { poids: (pondAj && typeof pondAj === 'object' ? pondAj : null) || j.ponderations || null,
              auteur: j.ponderations_auteur || null, date: j.ponderations_date || null }
          : null);
        setMatchingLoading(false);
        if (j.status && j.status !== 'ready') {
          setTimeout(() => { if (matchingPoll.current.token === myToken) tick(); }, 2000);
        }
      } catch (e) {
        if (matchingPoll.current.token !== myToken) return;
        setMatchingUnavailable(true); setMatching(null); setMatchingLoading(false);
      }
    };
    tick();
  };
  const recomputeMatching = async (force = false, ponderations = null) => {
    if (!aoId) return;
    if (force && !(await confirm({
      title: 'Re-noter intégralement',
      message: 'Re-noter tous les candidats de tous les lots, en repartant de zéro ?',
      detail: 'Toutes les notes existantes sont jetées et recalculées, ce qui prend plus de temps et engage un coût d\'analyse élevé. Le recalcul standard suffit dans la plupart des cas : il ne reprend que ce qui a changé.',
      confirmLabel: 'Re-noter tout', tone: 'primary', icon: 'stars',
    }))) return;
    setRecomputing(true);
    try {
      // ponderations : poids ajustés pour CE lancement (popup de pré-lancement).
      const r = await fetch(`${BASE}/ao/${aoId}/matching/recompute${force ? '?force=1' : ''}`, {
        method: 'POST',
        headers: ponderations ? { ...authHeaders, 'Content-Type': 'application/json' } : authHeaders,
        ...(ponderations ? { body: JSON.stringify({ ponderations }) } : {}),
      });
      const j = await r.json().catch(() => ({}));
      // Re-notation refusée : une autre a eu lieu il y a moins de 30 minutes.
      // Le message du serveur donne le temps d'attente, on l'affiche tel quel.
      if (r.status === 409) { showNotification?.(j.message || j.error || 'Re-notation déjà lancée récemment — réessayez dans quelques minutes.', 'info'); return; }
      if (!r.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      setMatchingStatus(j.status || 'computing'); setMatchingProgress(j.progress || null);
      if (Array.isArray(j.besoins)) setMatching(j.besoins);
      showNotification?.(force ? 'Re-notation intégrale lancée' : 'Recalcul du matching lancé', 'success');
      loadMatching(aoId);
    } catch (e) { showNotification?.(`Erreur recalcul : ${e.message}`, 'error'); }
    finally { setRecomputing(false); }
  };
  const [recomputingBesoin, setRecomputingBesoin] = useState(null);
  // Popup de pré-lancement du matching : null | {force} | {besoinId}.
  const [matchLancement, setMatchLancement] = useState(null);
  const recomputeBesoin = async (besoinId, ponderations = null) => {
    if (!aoId || besoinId == null) return; setRecomputingBesoin(besoinId);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/matching/recompute?besoin_id=${besoinId}`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: besoinId, ...(ponderations ? { ponderations } : {}) }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.message || j.error || `HTTP ${r.status}`); }
      showNotification?.('Recalcul de ce besoin lancé', 'success');
      loadMatching(aoId);
      setTimeout(() => loadMatching(aoId), 5000);
      setTimeout(() => { loadMatching(aoId); setRecomputingBesoin(null); }, 12000);
    } catch (e) { showNotification?.(`Erreur recalcul : ${e.message}`, 'error'); setRecomputingBesoin(null); }
  };
  useEffect(() => () => { matchingPoll.current.token++; }, []);

  const matchingReport = async (b, c) => {
    try {
      const qs = new URLSearchParams({ besoin_id: String(b.besoin_id ?? b.id ?? ''), candidate_id: String(c.candidate_id ?? '') });
      const r = await fetch(`${BASE}/ao/${aoId}/matching/report?${qs.toString()}`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Matching_${(c.nom || c.name || 'candidat')}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { showNotification?.('Rapport PDF indisponible (endpoint back à activer)', 'error'); }
  };

  // ── Arborescence kDrive ──
  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeUnavailable, setTreeUnavailable] = useState(false);
  const [openFolders, setOpenFolders] = useState(new Set());
  const toggleFolder = (key) => setOpenFolders(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const loadTree = async (id) => {
    if (!id) return;
    setTree(null); setTreeUnavailable(false); setTreeLoading(true);
    try {
      const r = await fetch(`${BASE}/ao/${id}/tree`, { headers: authHeaders });
      if (r.status === 404 || r.status === 501) { setTreeUnavailable(true); return; }
      const j = await r.json();
      if (j.success === false) throw new Error(j.error || 'Erreur');
      const folders = j.folders || j.data?.folders || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      setTree(Array.isArray(folders) ? folders : []);
    } catch (e) { setTreeUnavailable(true); }
    finally { setTreeLoading(false); }
  };

  const scan = async (folderId) => {
    if (!selected.size || !aoTitre.trim()) { showNotification?.('Titre + au moins un besoin requis', 'error'); return; }
    setScanning(true);
    try {
      const body = { besoinIds: [...selected], aoTitre: aoTitre.trim() };
      if (client.trim()) body.client = client.trim();
      if (template.trim()) body.template = template;
      if (folderId || kdriveFolderId.trim()) body.kdriveFolderId = (folderId || kdriveFolderId).toString().trim();
      const r = await fetch(`${BASE}/ao/scan`, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Erreur scan');
      setReport(j.report || null); setAoId(j.ao_id); setAoMeta({ titre: aoTitre.trim(), client: client.trim() }); setAoBesoinIds([...selected]); setKdriveUrl(j.report?.kdrive?.url || null);
      readSync(j);
      setView('report'); loadList();
      if (j.ao_id != null) onOpenAo?.(j.ao_id);
    } catch (e) { showNotification?.(`Erreur : ${e.message}`, 'error'); }
    finally { setScanning(false); }
  };

  const openDetail = async (ao) => {
    const cached = readAoCache(ao.id);
    // Rafraîchir le MÊME AO (après une action) ne doit pas replier le lot
    // ouvert, vider la recherche ni jeter le matching déjà chargé.
    const sameAo = view === 'report' && String(aoId) === String(ao.id);
    setAoId(ao.id); setView('report');
    if (!sameAo) {
      setReportTab('pieces'); setPieceQuery(''); setExpandedLot(null);
      matchingPoll.current.token++; setMatching(null); setMatchingStatus(null); setMatchingProgress(null); setExpandedCand(new Set());
    }
    setAoSync(ao.statut ? { statut: ao.statut, lastSyncedAt: ao.last_synced_at ?? null } : null);
    setReport(cached?.report || null);
    setAoMeta(cached?.aoMeta || { titre: ao.titre, client: ao.client });
    setAoBesoinIds(cached?.aoBesoinIds || ao.besoin_ids || []);
    setKdriveUrl(cached?.kdriveUrl || ao.kdrive_url || null);
    try {
      const r = await fetch(`${BASE}/ao/${ao.id}`, { headers: authHeaders }); const j = await r.json();
      if (j.success === false) throw new Error(j.error || 'Erreur');
      const data = j.data || j.ao || j;
      let rep = j.report ?? data?.report ?? data?.last_report ?? j.last_report ?? data?.rapport ?? data?.report_json ?? null;
      if (typeof rep === 'string') { try { rep = JSON.parse(rep); } catch { rep = null; } }
      const finalReport = rep || cached?.report || null;
      readSync(data); readSync(j);
      setReport(finalReport);
      setLotsAo(Array.isArray(data?.lots) ? data.lots : (Array.isArray(j.lots) ? j.lots : []));
      setAoMeta({ titre: ao.titre || data?.titre, client: ao.client || data?.client });
      const besoinIds = ao.besoin_ids || data?.besoin_ids || cached?.aoBesoinIds || [];
      setAoBesoinIds(besoinIds);
      setKdriveUrl(ao.kdrive_url || data?.kdrive_url || rep?.kdrive?.url || cached?.kdriveUrl || null);
      setScannedAt(j.scanned_at ?? data?.scanned_at ?? rep?.scanned_at ?? null);
      setAoNommage(data?.nommage ?? j.nommage ?? null);
      loadTree(ao.id);
      if (!finalReport && besoinIds.length && autoScanRef.current !== String(ao.id)) {
        autoScanRef.current = String(ao.id);
        scanExisting(ao.id, besoinIds, ao.titre || data?.titre || '', ao.client || data?.client || '');
      } else if (finalReport && besoinIds.length && !sameAo) {
        // Rescan silencieux à l'ouverture : le rapport persisté s'affiche tout
        // de suite, kDrive n'est relu que s'il a plus de 10 minutes (TTL et
        // verrou côté serveur) — l'écran cesse de mentir entre deux scans
        // volontaires, sans matraquer kDrive.
        scanExisting(ao.id, besoinIds, ao.titre || data?.titre || '', ao.client || data?.client || '', { silent: true, siPlusVieuxQue: 10 });
      }
    } catch (e) {
      if (!cached) showNotification?.(`Erreur : ${e.message}`, 'error');
      loadTree(ao.id);
    }
  };
  const autoScanRef = React.useRef(null);

  const goDetail = (ao) => { if (onOpenAo) onOpenAo(ao.id); else openDetail(ao); };

  useEffect(() => {
    if (routeAoId) {
      if (String(aoId) !== String(routeAoId) || view !== 'report') openDetail({ id: routeAoId });
    } else if (view === 'report') {
      setView('list'); loadList();
    }
  }, [routeAoId]);

  useEffect(() => {
    if (aoId != null && report != null) saveAoCache(aoId, { report, aoMeta, aoBesoinIds, kdriveUrl });
  }, [aoId, report, aoMeta, aoBesoinIds, kdriveUrl]);

  const scanExisting = async (idArg, besoinIds, titre, client, { silent = false, siPlusVieuxQue = null } = {}) => {
    if (!besoinIds.length) { if (!silent) showNotification?.('Aucun besoin rattaché à cet AO — recrée-le via « Nouvel AO ».', 'error'); return; }
    setScanning(true);
    try {
      const body = { besoinIds, aoTitre: titre || '' };
      if (idArg) body.aoId = idArg;
      if (client) body.client = client;
      // TTL serveur : rapport encore frais → servi tel quel, sans appels kDrive
      // (rescan: false). Verrou côté serveur : une analyse à la fois par AO.
      if (siPlusVieuxQue != null) body.si_plus_vieux_que = siPlusVieuxQue;
      const r = await fetch(`${BASE}/ao/scan`, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Erreur scan');
      setReport(j.report || null); if (j.ao_id) setAoId(j.ao_id);
      setKdriveUrl(j.report?.kdrive?.url || kdriveUrl);
      setScannedAt(j.scanned_at || j.report?.scanned_at || null);
      readSync(j);
      if (!silent) showNotification?.(j.rescan === false ? 'Rapport encore frais — kDrive n\'a pas été relu.' : 'Analyse rafraîchie depuis kDrive', 'success');
      loadList(); loadTree(j.ao_id || idArg);
      return j.report || null;
    } catch (e) { if (!silent) showNotification?.(`Erreur : ${e.message}`, 'error'); return null; }
    finally { setScanning(false); }
  };
  const rescan = () => scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client);

  // ── Validation des lots, lisible depuis la grille ──────────────────────────
  // Les boutons de validation appartiennent au lot : c'est là que le
  // collaborateur regarde. L'onglet « Validation & livraison » reste le lieu des
  // emplacements kDrive et de la copie finale.
  const [livr, setLivr] = useState(null);
  const [livrLot, setLivrLot] = useState(null);
  const loadLivraison = async (id = aoId) => {
    if (id == null) return;
    try {
      const r = await fetch(`${BASE}/ao/${id}/livraison`, { headers: authHeaders });
      if (!r.ok) { setLivr(null); return; }
      const j = await r.json().catch(() => ({}));
      setLivr(j.data || j);
    } catch (e) { setLivr(null); }
  };
  const lotBesoinId = (lot) => folderIds(lot?.folder || {}).besoin_id
    ?? folderIds((report?.folders || []).find(f => f.bind !== 'candidate' && f.path === lot?.path) || {}).besoin_id
    ?? null;
  const validationDuLot = (besoinId) => (livr?.lots || [])
    .find(l => String(l.besoin_id) === String(besoinId))?.validation || null;
  const validerLotDeGrille = async (besoinId, retirer) => {
    setLivrLot(String(besoinId));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison/lots/${besoinId}/valider`, {
        method: retirer ? 'DELETE' : 'POST', headers: authHeaders,
      });
      if (r.status === 404 || r.status === 501) {
        showNotification?.('La validation arrive côté serveur — rien n\'a été enregistré.', 'info');
      } else {
        const j = await r.json().catch(() => ({}));
        // 409 « déjà validé par… » et 403 « retrait interdit » nomment la
        // personne : le message du serveur est meilleur que le nôtre.
        if (!r.ok || j.success === false) showNotification?.(j.message || j.error || 'Validation refusée.', r.status === 409 ? 'info' : 'error');
        else showNotification?.(retirer ? 'Validation retirée.' : 'Lot validé.', 'success');
      }
      await loadLivraison();
    } catch (e) { showNotification?.('Validation impossible — réessayez.', 'error'); }
    setLivrLot(null);
  };

  // ── Batchs en tâche de fond ────────────────────────────────────────────────
  // On lance, on reçoit un job_id, on rend la main : le suivi se fait par
  // sondage toutes les 2 s tant qu'un traitement est actif, et rien sinon.
  const JOB_ACTIVE = (st) => st === 'queued' || st === 'running';
  const [jobs, setJobs] = useState([]);
  const jobsRef = React.useRef([]);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);
  const jobId = (j) => j?.id ?? j?.job_id;
  // Compteur des tâches terminées : un panneau (relecture des cartes) s'y
  // abonne pour se relire à la fin d'une tâche qu'il a lancée.
  const [jobsFinis, setJobsFinis] = useState(0);
  const ajouterJob = (j) => { const job = { ...(j.job || {}), id: j.job_id }; setJobs(prev => [...prev.filter(x => jobId(x) !== jobId(job)), job]); };

  // Au chargement de l'écran : un batch lancé avant un rafraîchissement doit
  // se réafficher tout seul.
  const loadActiveJobs = async () => {
    try {
      const r = await fetch(`${BASE}/jobs?actifs=1`, { headers: authHeaders });
      if (!r.ok) return;
      const j = await r.json().catch(() => ({}));
      const list = j.jobs || j.data?.jobs || (Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []));
      if (Array.isArray(list)) setJobs(list.filter(x => JOB_ACTIVE(x.statut || x.status)).map(x => ({ ...x, id: jobId(x) })));
    } catch {}
  };
  useEffect(() => {
    if (view === 'report' && aoId != null) { loadActiveJobs(); loadLivraison(aoId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aoId, view]);

  const hasJobs = jobs.length > 0;
  useEffect(() => {
    if (!hasJobs) return undefined;
    const tick = async () => {
      const next = [];
      let finished = false;
      for (const jb of jobsRef.current) {
        const id = jobId(jb);
        try {
          const r = await fetch(`${BASE}/jobs/${id}`, { headers: authHeaders });
          if (!r.ok) { next.push(jb); continue; }
          const d = await r.json().catch(() => ({}));
          const job = d.job || d.data || d;
          const st = job.statut || job.status;
          if (JOB_ACTIVE(st)) { next.push({ ...jb, ...job, id }); continue; }
          // Le contrôle qualité ne touche à rien : pas de rescan derrière lui
          // (un rescan rendrait aussitôt son rapport « à relancer »).
          if (!estJobControle(jb) && !estJobControle(job)) finished = true;
          const ok = job.ok_count ?? 0, ko = job.ko_count ?? 0;
          // Les échecs sont nommés, sans masquer les réussites.
          const rates = (job.result?.resultats || []).filter(x => x && x.ok === false);
          const detail = rates.length ? ` — en échec : ${rates.slice(0, 3).map(x => x.nom || x.candidat || x.candidate_id).join(', ')}${rates.length > 3 ? '…' : ''}` : '';
          if (st === 'cancelled') showNotification?.(`Traitement annulé — ${ok} élément(s) déjà traité(s)`, 'success');
          else if (st === 'error') showNotification?.(`Traitement interrompu : ${job.error || 'erreur serveur'}`, 'error');
          else if (job.result?.bilan?.message) showNotification?.(job.result.bilan.message, (job.result.bilan.echecs || ko) ? 'error' : 'success');
          else showNotification?.(`Traitement terminé — ${ok} réussite(s)${ko ? `, ${ko} en échec` : ''}${detail}`, ko ? 'error' : 'success');
          // Amélioration de CV : le matching du lot se recalcule derrière.
          if (job.result?.matching_relance) {
            showNotification?.('Les scores de matching se recalculent — l\'onglet Matching se met à jour dans quelques secondes.', 'success');
            setTimeout(() => { if (aoId) loadMatching(aoId); }, 4000);
          }
        } catch { next.push(jb); }
      }
      setJobs(next);
      if (finished) setJobsFinis(n => n + 1);
      // Les statuts de pièces ne bougent qu'après un scan.
      if (finished) scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    };
    const t = setInterval(tick, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJobs, aoId]);

  // « Générer N pièces » (tout, en fond, sans rien voir) a laissé la place à
  // la Machine CV (RevueCvAo, machine) — 30.10.

  const cancelJob = async (id) => {
    try {
      const r = await fetch(`${BASE}/jobs/${id}/cancel`, { method: 'POST', headers: authHeaders });
      if (r.status === 409) { showNotification?.('Ce traitement est déjà terminé.', 'error'); loadActiveJobs(); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('Arrêt demandé — la pièce en cours sera terminée avant l\'arrêt.', 'success');
    } catch (e) { showNotification?.(`Annulation impossible : ${e.message}`, 'error'); }
  };


  // ── Actions par pièce ──
  const [pieceBusy, setPieceBusy] = useState({});
  const [viewer, setViewer] = useState(null);
  const [canonView, setCanonView] = useState(null);
  const closeViewer = () => setViewer(null);
  const fileTargetRef = React.useRef(null);
  const pieceFileInputRef = React.useRef(null);
  const [dropLot, setDropLot] = useState(null); // f.path du lot survolé en glisser-déposer
  const cvTargetRef = React.useRef(null);
  const cvFileInputRef = React.useRef(null);
  const pieceKey = (f, p) => `${f.path || f.candidate_id}|${p.label}`;
  // Marque blanche : le scan nomme encore la pièce « CV TAT » — on AFFICHE
  // « CV entreprise », mais label reste la clé envoyée au serveur, intacte.
  const nomPiece = (x) => String((x && x.label) || x || '').replace(/\bCV[_ ]?TAT\b/gi, 'CV entreprise') || 'Document';
  const setBusy = (key, v) => setPieceBusy(prev => ({ ...prev, [key]: v }));
  const folderIds = (f) => ({ besoin_id: f.besoin_id ?? f.besoinId, candidate_id: f.candidate_id ?? f.candidat_id ?? f.candidateId });
  // Le NOM INTERNE d'un lot (31.10 : « sur l'AO il faut mettre le nom interne
  // du lot, récupéré de là » — la Référence du besoin) : servi par le serveur
  // sur le dossier de lot du rapport et, en direct, dans `lots` de GET /ao/:id.
  // Jamais déduit du nom kDrive, jamais recomposé ici.
  const nomInterneDuBesoin = (bid) => {
    if (bid == null) return null;
    const lf = (report?.folders || []).find(x => x.bind !== 'candidate' && String(folderIds(x).besoin_id) === String(bid) && x.reference_interne);
    return lf?.reference_interne || lotsAo.find(l => String(l.besoin_id) === String(bid))?.reference_interne || null;
  };
  const nomInterneDuLot = (f) => (f ? (f.reference_interne || nomInterneDuBesoin(folderIds(f).besoin_id)) : null);
  // Le nom interne se DONNE aussi d'ici (31.10 : « je veux voir le titre
  // interne ici ») : la même route que la fiche du besoin (PUT /besoins/:id
  // { reference_interne }) ; l'écran reprend ce que le serveur a enregistré.
  const [refLot, setRefLot] = useState(null); // { besoinId, value } pendant la saisie
  const enregistrerNomInterne = async (bid, valeur) => {
    const v = String(valeur ?? '').replace(/\s+/g, ' ').trim();
    setRefLot(null);
    try {
      const r = await fetch(`${BASE}/besoins/${bid}`, { method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ reference_interne: v || null }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.data && !('reference_interne' in j.data)) { showNotification?.("Le nom interne arrive côté serveur : il n'a pas été enregistré.", 'info'); return; }
      const ref = j.data?.reference_interne ?? (v || null);
      setLotsAo(prev => (prev.some(l => String(l.besoin_id) === String(bid))
        ? prev.map(l => (String(l.besoin_id) === String(bid) ? { ...l, reference_interne: ref } : l))
        : [...prev, { besoin_id: Number(bid), titre: null, reference_interne: ref }]));
      setReport(prev => (prev ? { ...prev, folders: (prev.folders || []).map(x => (x.bind !== 'candidate' && String(folderIds(x).besoin_id) === String(bid) ? { ...x, reference_interne: ref } : x)) } : prev));
      showNotification?.(ref ? `Nom interne du lot enregistré : ${ref}` : 'Nom interne du lot effacé', 'success');
    } catch (e) { showNotification?.(`Nom interne non enregistré : ${e.message}`, 'error'); }
  };

  /**
   * Les personnes à compléter, dans l'ordre de la grille : lot par lot, et à
   * l'intérieur d'un lot, candidat par candidat. Un candidat positionné sur
   * deux lots apparaît deux fois — la fonction et le salaire proposé ne sont
   * pas les mêmes d'un lot à l'autre.
   */
  // useMemo, et pas une simple fonction : appelée dans le JSX, elle renvoyait
  // un tableau neuf à chaque rendu. L'assistant, qui dépend de cette liste,
  // relançait alors son chargement en boucle et remettait la saisie à zéro.
  const personnesAContacter = React.useMemo(() => {
    const dossiers = report?.folders || [];
    const lots = dossiers.filter(f => f.bind !== 'candidate');
    const nomLot = (chemin) => {
      const l = lots.find(x => x.path === chemin);
      return l?.label || (chemin || '').split('/').filter(Boolean).pop() || '';
    };
    return dossiers
      .filter(f => f.bind === 'candidate')
      .sort((a, b) => (a.path || '').localeCompare(b.path || '', 'fr'))
      .map(f => {
        const { besoin_id, candidate_id } = folderIds(f);
        const parent = (f.path || '').split('/').slice(0, -1).join('/');
        return {
          candidateId: candidate_id,
          besoinId: besoin_id,
          nom: f.candidat || f.nom || f.label || (f.path || '').split('/').pop(),
          lot: nomLot(parent),
          poste: nomLot(parent),
        };
      })
      .filter(x => x.candidateId != null);
  }, [report]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDetail = () => { if (aoId) { openDetail({ id: aoId, titre: aoMeta?.titre, client: aoMeta?.client }); } };

  const createLot = async (lot) => {
    const k = lot.path;
    setCreatingLot(m => ({ ...m, [k]: true }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/besoins?rescan=1`, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre: lot.label }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const created = j.created === true || (j.created !== false && r.status === 201);
      // Ce qui s'est VRAIMENT passé sur kDrive vient du serveur (kdrive :
      // created / existed / nom / error) — plus de phrase devinée d'après
      // l'état de la tuile (vécu 31.10 : « Dossier kDrive créé — le besoin
      // existait déjà » alors que rien n'avait été créé).
      const kd = j.kdrive && typeof j.kdrive === 'object' ? j.kdrive : null;
      const bid = j.besoin_id ?? j.besoin?.id ?? null;
      const encoreACreer = kd?.existed && bid != null && (j.report?.folders || []).some(x => x.bind !== 'candidate' && String(folderIds(x).besoin_id) === String(bid) && x.exists === false);
      let message, type = 'success';
      if (kd?.created) message = created ? `Lot « ${lot.label} » créé : le besoin dans l'ERP et son dossier kDrive « ${kd.nom || lot.label} »` : `Dossier kDrive « ${kd.nom || lot.label} » créé pour le lot « ${lot.label} »`;
      else if (kd?.existed) message = `Le dossier kDrive du lot « ${lot.label} » existe déjà (« ${kd.nom || lot.label} ») : rien créé, dossier rescanné${created ? ' — besoin créé et rattaché' : ''}${encoreACreer ? '. Le scan ne le reconnaît pas sous ce nom : « Renommer » aligne le nom du dossier sur le lot.' : ''}`;
      else if (kd?.error) { message = `${created ? `Besoin « ${lot.label} » créé et rattaché` : `Besoin « ${lot.label} » rattaché`}, mais le dossier kDrive n'a pas pu être créé : ${kd.error}`; type = 'error'; }
      else message = created ? `Besoin « ${lot.label} » créé et rattaché à l'AO` : `Besoin « ${lot.label} » déjà existant — rattaché à l'AO`;
      showNotification?.(message, type);
      readSync(j);
      if (j.report) { setReport(j.report); loadTree(aoId); } else refreshDetail();
    } catch (e) { showNotification?.(`Erreur création du besoin : ${e.message}`, 'error'); }
    finally { setCreatingLot(m => ({ ...m, [k]: false })); }
  };

  const deleteLot = async (lot) => {
    const besoinId = folderIds(lot.folder || {}).besoin_id;
    if (besoinId == null) return;
    if (!(await confirm({ title: 'Supprimer le lot', message: `Supprimer le lot « ${lot.label} » ?`, detail: 'Le besoin ERP et son dossier kDrive (candidats et pièces du lot inclus) seront supprimés. Cette action est irréversible.', confirmLabel: 'Supprimer' }))) return;
    const k = lot.path; setDeletingLot(m => ({ ...m, [k]: true }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/besoins/${besoinId}?rescan=1`, { method: 'DELETE', headers: authHeaders });
      if (!r.ok) {
        const je = await r.json().catch(() => ({}));
        throw new Error(je.error || (r.status === 404 ? 'Besoin non rattaché à cet AO — rien n\'a été supprimé.' : `HTTP ${r.status}`));
      }
      const j = await r.json().catch(() => ({}));
      if (j.kdrive?.deleted === false) {
        showNotification?.(`Besoin « ${lot.label} » supprimé de l'ERP — dossier kDrive non supprimé${j.kdrive.error ? ` : ${j.kdrive.error}` : ''}`, 'error');
      } else {
        showNotification?.(`Lot « ${lot.label} » supprimé (besoin + dossier kDrive)`, 'success');
      }
      readSync(j);
      if (j.report) { setReport(j.report); loadTree(aoId); } else refreshDetail();
      loadList();
    } catch (e) { showNotification?.(`Erreur suppression du lot : ${e.message}`, 'error'); }
    finally { setDeletingLot(m => { const n = { ...m }; delete n[k]; return n; }); }
  };

  // Détacher (≠ supprimer) : retire le besoin de CET AO sans toucher au besoin
  // ERP ni à son dossier kDrive — le besoin reste dans la liste des besoins.
  const [detachingLot, setDetachingLot] = useState({});
  const detachLot = async (lot) => {
    const besoinId = folderIds(lot.folder || {}).besoin_id;
    if (besoinId == null) return;
    if (!(await confirm({ title: 'Détacher le besoin', message: `Détacher « ${lot.label} » de cet AO ?`, detail: 'Le besoin et son dossier kDrive sont conservés — seul le rattachement à cet AO est retiré. Vous pourrez le rattacher à nouveau plus tard.', confirmLabel: 'Détacher' }))) return;
    const k = lot.path; setDetachingLot(m => ({ ...m, [k]: true }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/besoins/${besoinId}?detach=1&rescan=1`, { method: 'DELETE', headers: authHeaders });
      if (!r.ok) {
        const je = await r.json().catch(() => ({}));
        throw new Error(je.error || (r.status === 404 ? 'Besoin non rattaché à cet AO.' : `HTTP ${r.status}`));
      }
      const j = await r.json().catch(() => ({}));
      showNotification?.(`« ${lot.label} » détaché de l'AO — le besoin reste dans l'ERP.`, 'success');
      readSync(j);
      if (j.report) { setReport(j.report); loadTree(aoId); } else refreshDetail();
      loadList();
    } catch (e) { showNotification?.(`Erreur détachement : ${e.message}`, 'error'); }
    finally { setDetachingLot(m => { const n = { ...m }; delete n[k]; return n; }); }
  };

  // Rattacher un besoin EXISTANT à ce dossier (27.10).
  const [rattacherOuvert, setRattacherOuvert] = useState(false);

  const [renameLot, setRenameLot] = useState(null);
  const [renamingLot, setRenamingLot] = useState(false);
  const confirmRenameLot = async () => {
    const lot = renameLot?.lot;
    // kdriveOnly : dossier présent sur kDrive mais rattaché à aucun besoin ERP —
    // on renomme directement le dossier kDrive (le nom est gardé tel quel).
    if (renameLot?.kdriveOnly) {
      const name = (renameLot?.value || '').trim();
      if (!name) return;
      setRenamingLot(true);
      try {
        const r = await fetch(`${BASE}/ao/${aoId}/folders/rename?rescan=1`, {
          method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder_id: lot?.folder?.folder_id ?? null, path: lot?.path, name }),
        });
        if (r.status === 404 || r.status === 501) {
          showNotification?.('Le renommage d\'un dossier kDrive non rattaché arrive côté serveur — en attendant, renommez-le directement dans kDrive.', 'error');
          setRenameLot(null); return;
        }
        const j = await r.json().catch(() => ({}));
        // 502 = échec réel côté Infomaniak : la raison est relayée.
        if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
        // kDrive peut ajuster le nom en cas de conflit (« X (1) ») : on affiche
        // le nom réellement posé, pas celui demandé.
        const real = j.name || j.data?.name || name;
        showNotification?.(real === name ? `Dossier kDrive renommé « ${real} »` : `Dossier kDrive renommé « ${real} » (nom ajusté par kDrive)`, 'success');
        setRenameLot(null);
        readSync(j);
        if (j.report) { setReport(j.report); loadTree(aoId); } else refreshDetail();
      } catch (e) { showNotification?.(`Erreur renommage : ${e.message}`, 'error'); }
      finally { setRenamingLot(false); }
      return;
    }
    const titre = (renameLot?.value || '').trim().replace(/^lot\s*\d+\s*:\s*/i, '').trim();
    const besoinId = folderIds(lot?.folder || {}).besoin_id;
    if (!titre || besoinId == null) return;
    setRenamingLot(true);
    try {
      // Le serveur refuse parfois de renommer en croyant à une ambiguïté alors
      // que le report ne connaît qu'un seul dossier : on lui désigne
      // explicitement celui-là pour lever le doute.
      const knownIds = [...new Set([
        ...(lot?.dupes || [lot]).map(d => d?.folder?.folder_id ?? d?.folder?.id),
        lot?.folder?.folder_id ?? lot?.folder?.id,
      ].filter(v => v != null).map(String))];
      const folderHint = renameLot?.pickFolderId ?? (knownIds.length === 1 ? knownIds[0] : null);
      const r = await fetch(`${BASE}/ao/${aoId}/besoins/${besoinId}?rescan=1`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre, ...(folderHint ? { kdrive_folder_id: folderHint } : {}) }),
      });
      if (!r.ok) {
        const je = await r.json().catch(() => ({}));
        throw new Error(je.error || (r.status === 404 ? 'Besoin non rattaché à cet AO.' : `HTTP ${r.status}`));
      }
      const j = await r.json().catch(() => ({}));
      if (j.kdrive?.renamed === false) {
        // Doublon sur kDrive : le serveur ne sait pas quel dossier renommer.
        // S'il nous liste les candidats, on laisse l'utilisateur trancher.
        // À défaut de liste renvoyée, on propose les dossiers que le report
        // connaît (le serveur en voit peut-être plusieurs là où on n'en voit
        // qu'un : on lui laisse quand même désigner explicitement le bon).
        const matches = Array.isArray(j.kdrive.matches) && j.kdrive.matches.length > 1
          ? j.kdrive.matches
          : (lot?.dupes || []).filter(d => (d?.folder?.folder_id ?? d?.folder?.id) != null)
              .map(d => ({ folder_id: d.folder.folder_id ?? d.folder.id, name: (d.path || '').split('/').pop(), path: d.path, url: d.folder.url || d.folder.kdrive_url }));
        if (matches && matches.length > 1 && !renameLot?.pickFolderId) {
          readSync(j);
          if (j.report) { setReport(j.report); loadTree(aoId); }
          setRenameLot(t => ({ ...t, value: titre, matches, pickFolderId: null }));
          showNotification?.(`Besoin renommé « ${j.titre || titre} » — plusieurs dossiers kDrive correspondent : choisissez celui à renommer.`, 'error');
          return;
        }
        showNotification?.(`Besoin renommé « ${j.titre || titre} » — dossier kDrive inchangé${j.kdrive.error ? ` : ${j.kdrive.error}` : ''}. Ouvrez kDrive pour vérifier s'il existe un doublon, supprimez-le, puis « Rescanner ».`, 'error');
      } else {
        const real = j.kdrive?.name || j.name || null;
        showNotification?.(real && real !== titre
          ? `Lot renommé « ${j.titre || titre} » — dossier kDrive nommé « ${real} » (ajusté par kDrive)`
          : `Lot renommé « ${j.titre || titre} » (besoin + dossier kDrive)`, 'success');
      }
      setRenameLot(null);
      readSync(j);
      if (j.report) { setReport(j.report); loadTree(aoId); } else refreshDetail();
      loadList();
    } catch (e) { showNotification?.(`Erreur renommage : ${e.message}`, 'error'); }
    finally { setRenamingLot(false); }
  };

  // Suppression unique : on retire ce que l'ERP connaît ET, s'il existe, le
  // dossier kDrive. Un dossier absent n'est pas une erreur — le but est de ne
  // plus voir ce candidat sur le lot, quel que soit l'état de départ.
  const [deletingFolder, setDeletingFolder] = useState({});
  const deleteCandidateEverywhere = async (f) => {
    const candidateId = folderIds(f).candidate_id;
    const besoinId = folderIds(f).besoin_id ?? besoinOfPath(f.path);
    const label = (f.path || '').split('/').pop();
    const onKdrive = !!f.path && f.exists !== false;   // « À créer » = pas de dossier
    const inErp = candidateId != null;
    if (!inErp && !onKdrive) return;

    // Retirer du lot ≠ supprimer le candidat. Le geste courant enlève le
    // dossier kDrive du lot et le positionnement sur ce besoin — la fiche est
    // CONSERVÉE, avec ses documents, son historique et ses autres besoins.
    // La suppression de la fiche reste possible, mais comme un second choix
    // explicite, confirmé une seconde fois.
    const choix = await confirm({
      title: 'Retirer du lot',
      message: `Retirer « ${label} » de ce lot ?`,
      detail: inErp
        ? 'Le dossier kDrive du lot et le positionnement sur ce besoin sont supprimés. La fiche candidat est conservée : documents, historique et autres besoins ne bougent pas.'
        : 'Le dossier est supprimé sur kDrive. Aucune fiche candidat de l\'ERP ne lui correspond.',
      confirmLabel: 'Retirer du lot',
      ...(inErp ? { extraLabel: 'Supprimer aussi la fiche', extraIcon: 'trash3' } : {}),
    });
    if (!choix) return;
    const supprimerFiche = choix === 'extra';
    if (supprimerFiche && !(await confirm({
      title: 'Supprimer la fiche candidat',
      message: `Supprimer définitivement la fiche de « ${label} » ?`,
      detail: 'La fiche disparaît de l\'ERP avec ses documents, son historique et ses positionnements sur TOUS les besoins — pas seulement ce lot. Cette action est irréversible.',
      confirmLabel: 'Supprimer définitivement',
    }))) return;

    setDeletingFolder(m => ({ ...m, [f.path]: true }));
    if (inErp) setDeletingCand(m => ({ ...m, [candidateId]: true }));
    const done = [];
    try {
      // Le positionnement d'abord : un refus (préparation de CV en cours)
      // doit arrêter le geste AVANT que le dossier kDrive ait disparu.
      if (inErp && !supprimerFiche && besoinId != null) {
        const r = await fetch(`${BASE}/candidates/${candidateId}/besoins/${besoinId}?rescan=1`, { method: 'DELETE', headers: authHeaders });
        const j = await r.json().catch(() => ({}));
        if (r.status === 409) { showNotification?.(j.message || j.error || 'Retrait impossible pour le moment — une préparation de CV est en cours.', 'info'); return; }
        if (r.status === 501 || (r.status === 404 && !j.error && !j.message)) {
          showNotification?.('Le retrait du positionnement arrive côté serveur — rien n\'a été supprimé.', 'info');
          return;
        }
        // 404 avec message = déjà retiré : le résultat voulu.
        if (!r.ok && r.status !== 404) throw new Error(j.message || j.error || `HTTP ${r.status}`);
        done.push('positionnement retiré');
      }
      if (onKdrive) {
        const qs = new URLSearchParams({ path: f.path || '', rescan: '1' });
        if (f.folder_id != null) qs.set('folder_id', String(f.folder_id));
        const r = await fetch(`${BASE}/ao/${aoId}/folders?${qs.toString()}`, { method: 'DELETE', headers: authHeaders });
        if (r.status === 501) {
          showNotification?.('La suppression d\'un dossier kDrive depuis l\'ERP arrive côté serveur — en attendant, supprimez-le directement dans kDrive puis « Rescanner ».', 'error');
          return;
        }
        // 404 = déjà absent de kDrive : c'est le résultat voulu, pas une erreur.
        if (r.status !== 404) {
          const j = await r.json().catch(() => ({}));
          if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
          readSync(j);
          done.push('dossier kDrive');
        }
      }
      if (inErp && supprimerFiche) {
        let r = await fetch(`${BASE}/candidates/${candidateId}`, { method: 'DELETE', headers: authHeaders });
        if (r.status === 404 || r.status === 405) r = await fetch(`${BASE}/candidats/${candidateId}`, { method: 'DELETE', headers: authHeaders });
        if (!r.ok && r.status !== 404) throw new Error(`HTTP ${r.status}`);
        done.push('fiche ERP supprimée');
      }
      showNotification?.(done.length
        ? (supprimerFiche
          ? `« ${label} » supprimé (${done.join(' + ')})`
          : `« ${label} » retiré du lot (${done.join(' + ')}) — sa fiche candidat est conservée`)
        : `« ${label} » n'existait plus`, 'success');
      // Retrait local immédiat pour ne pas replier la vue, puis resynchro.
      setReport(prev => prev ? { ...prev, folders: (prev.folders || []).filter(x => x.path !== f.path) } : prev);
      scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    } catch (e) { showNotification?.(`Erreur : ${e.message}`, 'error'); }
    finally {
      setDeletingFolder(m => { const n = { ...m }; delete n[f.path]; return n; });
      if (inErp) setDeletingCand(m => { const n = { ...m }; delete n[candidateId]; return n; });
    }
  };

  // ── Présenter un candidat sur un autre lot ────────────────────────────────
  // Cas courant : le candidat a déjà toutes ses pièces sur un lot et on veut le
  // proposer sur un second. Plutôt que de tout refaire, on duplique le
  // rattachement et ses documents vers le lot cible.
  // Après « Ajouter au besoin » ou une copie vers un autre lot, le serveur
  // rafraîchit le rapport stocké en tâche de fond (quelques secondes). Celui
  // affiché est alors en retard : on le relit à la prochaine ouverture d'un
  // lot, une seule fois, plutôt que d'imposer un rescan à l'utilisateur.
  const rapportPerime = React.useRef(false);

  const [copyCand, setCopyCand] = useState(null);   // { f, toBesoinId, withDocs }
  const [copying, setCopying] = useState(false);

  const lotOptions = () => (report?.folders || [])
    .filter(f => f.bind !== 'candidate' && folderIds(f).besoin_id != null)
    .map(f => ({ besoin_id: folderIds(f).besoin_id, path: f.path, label: nomInterneDuLot(f) ? `${nomInterneDuLot(f)} — ${(f.path || '').split('/').pop()}` : (f.path || '').split('/').pop() }));

  const askCopyCandidate = (f) => {
    const parent = (f.path || '').split('/').slice(0, -1).join('/');
    const opts = lotOptions().filter(o => o.path !== parent);
    if (!opts.length) { showNotification?.('Aucun autre lot rattaché à cet AO — créez-le d\'abord.', 'error'); return; }
    setCopyCand({ f, fromPath: parent, toBesoinId: String(opts[0].besoin_id), withDocs: true });
  };

  const confirmCopyCandidate = async () => {
    const { f, fromPath, toBesoinId, withDocs } = copyCand || {};
    const candidateId = folderIds(f || {}).candidate_id;
    if (candidateId == null || !toBesoinId) return;
    const fromBesoinId = lotOptions().find(o => o.path === fromPath)?.besoin_id ?? null;
    setCopying(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/candidates/${candidateId}/copy?rescan=1`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_besoin_id: fromBesoinId, to_besoin_id: Number(toBesoinId), with_documents: !!withDocs }),
      });
      if (r.status === 501) {
        showNotification?.('La présentation d\'un candidat sur un autre lot arrive côté serveur.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 409 déjà positionné (message nominatif), 404 hors de cet AO, 400 même
      // lot, 502 kDrive : la raison du serveur est affichée telle quelle.
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const copied = j.copied ?? 0;
      const label = (f.path || '').split('/').pop();
      const cible = lotOptions().find(o => String(o.besoin_id) === String(toBesoinId))?.label || 'l\'autre lot';
      const bouts = [`${copied} document${copied > 1 ? 's' : ''} repris`];
      // Le CV déposé dans le lot cible mérite d'être nommé : c'est le document
      // que le client verra.
      if (j.cv_depose) bouts.push(`CV déposé : ${j.cv_depose}`);
      // « ignorees » n'est pas un échec : chaque entrée porte sa raison, on la
      // montre regroupée plutôt qu'un compte qui ressemble à une erreur.
      const parRaison = new Map();
      (j.ignorees || []).forEach(x => {
        const raison = (x.raison || x.reason || 'non repris').trim();
        parRaison.set(raison, (parRaison.get(raison) || 0) + 1);
      });
      parRaison.forEach((n, raison) => bouts.push(n > 1 ? `${raison} (×${n})` : raison));
      // Les vraies erreurs restent des erreurs, avec leur motif.
      const erreurs = j.erreurs || [];
      erreurs.slice(0, 3).forEach(e => bouts.push(typeof e === 'string' ? e : (e.message || e.erreur || 'échec de copie')));
      if (erreurs.length > 3) bouts.push(`et ${erreurs.length - 3} autre${erreurs.length - 3 > 1 ? 's' : ''} échec${erreurs.length - 3 > 1 ? 's' : ''}`);
      showNotification?.(`« ${label} » copié sur « ${cible} » — ${bouts.join('. ')}.`, erreurs.length ? 'error' : 'success');
      setCopyCand(null);
      readSync(j);
      rapportPerime.current = true;
      // Le rapport renvoyé est déjà à jour : pas de scan supplémentaire.
      if (j.report) { setReport(j.report); loadTree(aoId); }
    } catch (e) { showNotification?.(`Duplication impossible : ${e.message}`, 'error'); }
    finally { setCopying(false); }
  };

  // ── Retirer un positionnement depuis la vue Matching ──────────────────────
  // L'inverse exact de POST /candidates/:id/besoins : seul le lien
  // candidat-besoin disparaît — la fiche et le dossier kDrive restent.
  const retirerDuBesoin = async (candidateId, besoinId, nom, poste) => {
    if (candidateId == null || besoinId == null) return;
    if (!(await confirm({
      title: 'Retirer du besoin',
      message: `Retirer ${nom || 'ce candidat'} du besoin « ${poste || 'ce poste'} » ?`,
      detail: 'Ni la fiche candidat, ni ses documents, ni le dossier kDrive ne sont touchés — le serveur le garantit. Les documents préparés pour ce besoin sont conservés. Le candidat sortira du scoring de ce poste au prochain calcul.',
      confirmLabel: 'Retirer',
    }))) return;
    try {
      const r = await fetch(`${BASE}/candidates/${candidateId}/besoins/${besoinId}?rescan=1`, { method: 'DELETE', headers: authHeaders });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || (r.status === 404 && !j.error && !j.message)) {
        showNotification?.('Le retrait d\'un positionnement arrive côté serveur — rien n\'a été modifié.', 'info');
        return;
      }
      // 409 = une préparation de CV est en cours sur ce couple : le message du
      // serveur dit quoi faire, on l'affiche tel quel sans en faire une panne.
      if (r.status === 409) { showNotification?.(j.message || j.error || 'Retrait impossible pour le moment — une préparation de CV est en cours.', 'info'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(`${j.candidat || nom || 'Candidat'} retiré de « ${j.besoin_titre || poste || 'ce besoin'} » — fiche, documents et dossier kDrive conservés`, 'success');
      // Le serveur a confirmé : la ligne disparaît tout de suite. La relecture
      // du matching peut resservir un état pas encore recalculé — attendre
      // qu'elle rattrape donnerait l'impression que le clic n'a rien fait.
      setMatching(prev => Array.isArray(prev) ? prev.map(b => {
        if (String(b.besoin_id ?? b.id) !== String(besoinId)) return b;
        const sans = (liste) => (liste || []).filter(x => String(x.candidate_id ?? '') !== String(candidateId));
        return { ...b, ...(b.candidats ? { candidats: sans(b.candidats) } : {}), ...(b.candidates ? { candidates: sans(b.candidates) } : {}) };
      }) : prev);
      rapportPerime.current = true;
      loadMatching(aoId);
      if (j.report) { setReport(j.report); loadTree(aoId); }
    } catch (e) { showNotification?.(`Retrait impossible : ${e.message}`, 'error'); }
  };

  // ── Dossier présent sur kDrive, absent du besoin ERP ──────────────────────
  // Le dossier du candidat a été créé côté kDrive (dépôt manuel, migration)
  // sans que personne ne soit positionné sur le besoin. On le rattache au lieu
  // de recréer un dossier en double.
  const [attaching, setAttaching] = useState({});
  const besoinOfPath = (path) => {
    const parent = (path || '').split('/').slice(0, -1).join('/');
    const lot = (report?.folders || []).find(f => f.bind !== 'candidate' && f.path === parent);
    return folderIds(lot || {}).besoin_id ?? null;
  };
  const attachFolder = async (f) => {
    const besoinId = besoinOfPath(f.path);
    const nom = (f.path || '').split('/').pop();
    if (besoinId == null) { showNotification?.('Le lot parent n\'est rattaché à aucun besoin — créez d\'abord le besoin du lot.', 'error'); return; }
    if (!(await confirm({
      title: 'Ajouter au besoin',
      message: `Ajouter « ${nom} » au besoin de ce lot ?`,
      detail: 'La fiche candidat est créée dans l\'ERP si elle n\'existe pas, puis positionnée sur le besoin. Le dossier kDrive existant est réutilisé — aucun document n\'est déplacé ni dupliqué.',
      confirmLabel: 'Ajouter', tone: 'primary',
    }))) return;
    setAttaching(m => ({ ...m, [f.path]: true }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/besoins/${besoinId}/candidates/attach?rescan=1`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: f.path, folder_id: f.folder_id ?? null, nom }),
      });
      if (r.status === 404 || r.status === 501) {
        showNotification?.('L\'ajout d\'un dossier existant au besoin arrive côté serveur — en attendant, positionnez le candidat depuis le besoin puis « Rescanner ».', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 409 : un AUTRE dossier kDrive est déjà relié à ce candidat sur ce lot.
      // Le message du serveur nomme ce dossier — c'est l'information utile,
      // on l'affiche telle quelle. (Le cas « déjà positionné sans dossier
      // relié » n'est plus un refus : le serveur relie et répond 200.)
      if (r.status === 409) {
        showNotification?.(j.error || j.message || 'Un autre dossier kDrive est déjà relié à ce candidat sur ce lot.', 'info');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(
        j.relie ? `Dossier relié : « ${nom} » était déjà positionné, son dossier kDrive est maintenant rattaché à sa fiche`
          : j.created ? `« ${nom} » ajouté au besoin — fiche candidat créée, dossier kDrive conservé`
          : `« ${nom} » ajouté au besoin — son dossier kDrive est conservé`, 'success');
      readSync(j);
      rapportPerime.current = true;
      if (j.report) { setReport(j.report); loadTree(aoId); }
      else scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    } catch (e) { showNotification?.(`Ajout impossible : ${e.message}`, 'error'); }
    finally { setAttaching(m => { const n = { ...m }; delete n[f.path]; return n; }); }
  };

  const folderPieces = (f) => [
    ...piecesVisibles(f),
    ...(f.autres_fichiers || []).filter(name => isAdmin || !estNomContrat(name))
      .map(name => ({ label: name, original_name: name, status: 'present', _libre: true })),
  ];
  // Complétude d'un dossier candidat : uniquement les pièces de la checklist
  // (les fichiers libres ne comptent pas).
  // La carte profil (03.11) a sa ligne sous le candidat, mais elle est
  // facultative et se dépose à la demande : elle ne compte ni dans « N/M
  // pièces » ni dans l'assistant.
  const piecesDossier = (f) => piecesVisibles(f).filter(pc => !pc.carte_profil);
  const folderProgress = (f) => {
    const req = piecesDossier(f);
    const done = req.filter(p => p.status === 'present').length;
    return { done, total: req.length, complete: req.length > 0 && done === req.length };
  };

  // ── Assistant : file d'attente + curseur UNIQUE ──
  // L'affichage et les actions passent tous par aoCursor() : impossible que le
  // bouton agisse sur une autre pièce que celle affichée.
  const assistStoreKey = (id) => `erpia.ao.assistant.${id}`;
  useEffect(() => {
    if (aoId == null) return;
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(assistStoreKey(aoId)) || '{}') || {}; } catch { saved = {}; }
    setAssistDone(saved); setAssistIdx(0);
  }, [aoId]);
  const markAssist = (key, action) => setAssistDone(prev => {
    const next = { ...prev, [key]: action };
    try { localStorage.setItem(assistStoreKey(aoId), JSON.stringify(next)); } catch {}
    return next;
  });

  const aoCursor = () => {
    const items = [];
    (report?.folders || []).filter(f => f.bind === 'candidate').forEach(f => {
      const parts = (f.path || '').split('/').filter(Boolean);
      const candidat = parts[parts.length - 1] || '—';
      const lotRef = parts[parts.length - 2] || '';
      piecesDossier(f).forEach(p => {
        if (p.status === 'present') return;
        items.push({ key: `${aoId}|${candidat}|${p.label}`, f, p, candidat, candidateId: folderIds(f).candidate_id ?? null, besoinId: folderIds(f).besoin_id ?? null, lotRef, label: p.label, status: p.status, requestedAt: p.requested_at || null, prio: piecePriority(p.label) });
      });
    });
    items.sort((a, b) => a.prio - b.prio || a.candidat.localeCompare(b.candidat, 'fr') || a.label.localeCompare(b.label, 'fr'));
    const pend = items.filter(i => !assistDone[i.key]);
    const idx = Math.min(Math.max(assistIdx, 0), Math.max(pend.length - 1, 0));
    const cur = pend[idx] || null;
    // Le dossier du candidat courant, toutes pièces confondues : l'assistant le
    // montre s'ouvrir, avec l'état de chaque ligne.
    const docs = cur ? piecesDossier(cur.f).map(p => ({
      label: p.label,
      state: p.status === 'present' ? 'present'
        : assistDone[`${aoId}|${cur.candidat}|${p.label}`] ? 'done'
        : p.label === cur.label ? 'current' : 'pending',
    })) : [];
    return { queue: items, pend, idx, cur, docs, total: items.length, doneCount: items.length - pend.length };
  };

  // ── Orbe : une part par lot, deux métriques ───────────────────────────────
  const [orbeMetric, setOrbeMetric] = useState('pieces');
  const [orbeLot, setOrbeLot] = useState(null);
  // Les scores viennent du matching : on le charge à la demande, quand
  // l'utilisateur bascule sur cette métrique.
  useEffect(() => {
    if (orbeMetric === 'score' && matching == null && aoId) loadMatching(aoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbeMetric, aoId]);

  const orbeLots = () => {
    const folders = report?.folders || [];
    // Rapprochement par candidate_id : le nom produisait des faux positifs sur
    // les homonymes et les fiches au libellé pollué.
    const scoreOf = (besoinId, candidateId) => {
      if (candidateId == null) return null;
      const grp = (matching || []).find(b => String(b.besoin_id ?? b.id) === String(besoinId));
      const c = (grp?.candidats || grp?.candidates || []).find(x => String(x.candidate_id ?? '') === String(candidateId));
      const v = Number(c?.score);
      return Number.isFinite(v) ? Math.round(v) : null;
    };
    return folders.filter(f => f.bind !== 'candidate' && folderIds(f).besoin_id != null).map(lot => {
      const besoinId = folderIds(lot).besoin_id;
      const cands = folders.filter(f => f.bind === 'candidate' && (f.path || '').startsWith(`${lot.path}/`));
      const detail = cands.map(c => {
        const nom = (c.path || '').split('/').pop();
        const req = (c.pieces || []).filter(pc => !pc.carte_profil);
        // « ok » compte aussi ce que l'assistant vient de traiter.
        const ok = req.filter(p => p.status === 'present' || assistDone[`${aoId}|${nom}|${p.label}`]).length;
        return { nom, ok, tot: req.length, score: scoreOf(besoinId, folderIds(c).candidate_id) };
      });
      const tot = detail.reduce((a, c) => a + c.tot, 0);
      const ok = detail.reduce((a, c) => a + c.ok, 0);
      const notes = detail.map(c => c.score).filter(v => v != null);
      return {
        path: lot.path, besoinId, label: nomInterneDuLot(lot) || (lot.path || '').split('/').pop(),
        tot, ok, pct: tot ? Math.round((ok / tot) * 100) : 0,
        avg: notes.length ? Math.round(notes.reduce((a, v) => a + v, 0) / notes.length) : null,
        cands: detail,
      };
    });
  };

  // Le CTA dépend de la métrique : compléter les pièces, ou améliorer les CV.
  const orbeCta = (lot, metric) => {
    // Score : la Revue des CV (l'optimiseur), lot ouvert — plus d'ancien mode « améliorer le score ».
    if (metric === 'score') { setAssistOpen(false); setReportTab('pieces'); setRevueCv('revue'); if (lot.path) setExpandedLot(lot.path); return; }
    const { pend } = aoCursor();
    const at = pend.findIndex(it => (it.f.path || '').startsWith(`${lot.path}/`));
    setAssistIdx(at >= 0 ? at : 0);
    setAssistOpen(true);
    setReportTab('pieces');
    if (at < 0) showNotification?.('Toutes les pièces de ce lot sont déjà fournies.', 'success');
  };


  // Plus tard / Précédente bouclent sur la file — jamais de bouton mort.
  const assistMove = (delta) => {
    const { pend, idx } = aoCursor();
    if (pend.length < 2) return;
    setAssistIdx((idx + delta + pend.length) % pend.length);
  };
  // Les actions réelles (upload / génération) font sortir la pièce de la file
  // dès que le rapport est rafraîchi ; le curseur garde sa position, bornée.
  const assistUpload = (it) => { if (it.status === 'a_uploader') uploadPieceCopy(it.f, it.p); else askPieceFile(it.f, it.p); };
  const assistGenerate = (it) => { if (isGeneratable(it.p)) generatePiece(it.f, it.p); else askPieceFile(it.f, it.p); };
  const assistAsk = async (it) => {
    const { besoin_id, candidate_id } = folderIds(it.f);
    if (besoin_id == null || candidate_id == null) { showNotification?.('Dossier non rattaché à un candidat/besoin — relance l\'analyse.', 'error'); return; }
    const key = pieceKey(it.f, it.p); setBusy(key, true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/piece/request`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, label: it.label }),
      });
      if (r.status === 501) { showNotification?.('La relance par email arrive côté serveur.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      // 422 = pas d'e-mail sur la fiche candidat, 502 = échec d'envoi : le
      // message du serveur explique quoi faire, on l'affiche tel quel.
      if (!r.ok || j.success === false) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      markAssist(it.key, 'ask');
      showNotification?.(j.message || `Demande envoyée à ${it.candidat}${j.email ? ` (${j.email})` : ''}`, 'success');
      scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    } catch (e) { showNotification?.(`Demande non envoyée : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };
  const folderMatches = (f) => {
    const all = folderPieces(f);
    const q = pieceQuery.trim().toLowerCase();
    if (!q) return all;
    if ((f.path || '').toLowerCase().includes(q)) return all;
    const pcs = all.filter(p => (p.label || '').toLowerCase().includes(q) || (p.original_name || '').toLowerCase().includes(q) || (PIECE[p.status]?.label || '').toLowerCase().includes(q));
    return pcs.length ? pcs : null;
  };

  // Règle serveur : preview_url (route back, affichable) D'ABORD — url peut
  // être un lien d'app web kDrive, qui rend « Javascript seems to be
  // disabled » hors session. Les routes back reçoivent la CLÉ DE LECTURE en
  // query — jamais le jeton de session (28.09).
  const pieceHref = (p) => {
    const u = p.preview_url || p.doc_url || p.file_url || p.download_url || p.url || null;
    if (u && /\/(preview|documents)\b/.test(u) && !/[?&](token|cle)=/.test(u) && !u.includes('kdrive.infomaniak')) {
      return avecCle(u);
    }
    return u;
  };

  const pieceFileUrl = (f, p, { preview = false, download = false } = {}) => {
    const { besoin_id, candidate_id } = folderIds(f);
    const qs = new URLSearchParams({ besoin_id: String(besoin_id), candidate_id: String(candidate_id), label: p.label, token: APIService.getToken() });
    if (preview) qs.set('preview', '1');
    if (download) qs.set('download', '1');
    return `${BASE}/ao/${aoId}/piece/file?${qs.toString()}`;
  };

  const openViewer = async (f, p) => {
    const here = p.label;
    const { besoin_id, candidate_id } = folderIds(f);
    const estCv = pieceKind(p.label) === 'cv';
    // CV désigné dans cette session : le document est connu, on l'ouvre direct.
    const choisi = estCv ? cvChoisi[cvChoisiKey(f)] : null;
    if (choisi?.documentId != null && candidate_id != null) {
      setCanonView({ candidateId: candidate_id, docId: choisi.documentId, label: choisi.nom || p.label, besoinId: besoin_id, nom: f.candidat || f.nom, estCv: true });
      return;
    }
    // Le scan résout et répare les désignations, y compris historiques : une
    // ligne CV sans cv_designe n'a pas de désignation valide (celle qui visait
    // un document supprimé a été retirée volontairement). Le doc_id du rapport
    // est donc toujours le bon document à montrer — plus de détour par la fiche.
    const docId = p.doc_id ?? p.document_id ?? p.documentId ?? p.candidate_document_id ?? p.candidateDocumentId;
    if (docId != null && candidate_id != null) { setCanonView({ candidateId: candidate_id, docId, label: p.original_name || p.file_name || p.label, besoinId: besoin_id, nom: f.candidat || f.nom, estCv }); return; }
    if (besoin_id == null || candidate_id == null) { setViewer({ label: here, status: 'error', error: 'Pièce non localisable (ids absents).', href: pieceHref(p) }); return; }
    const name = (p.original_name || p.file_name || p.label || '').toLowerCase();
    const href = pieceHref(p);
    const dl = pieceFileUrl(f, p, { download: true });
    if (/\.(jpe?g|png|gif|webp)$/.test(name)) { setViewer({ label: here, status: 'ready', kind: 'image', url: pieceFileUrl(f, p), dl, href }); return; }
    if (name.endsWith('.pdf')) { setViewer({ label: here, status: 'ready', kind: 'pdf', url: pieceFileUrl(f, p), dl, href }); return; }
    if (name.endsWith('.docx')) {
      setViewer({ label: here, status: 'loading', href, dl });
      try {
        const r = await fetch(pieceFileUrl(f, p), { headers: authHeaders });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const buf = await r.arrayBuffer();
        const mod = await import('mammoth');
        const mammoth = mod.default || mod;
        const res = await mammoth.convertToHtml({ arrayBuffer: buf });
        setViewer(v => v && v.label === here ? { ...v, status: 'ready', kind: 'docx', html: res.value || '<p><em>Document vide</em></p>' } : v);
      } catch (e) {
        setViewer(v => v && v.label === here ? { ...v, status: 'ready', kind: 'pdf', url: pieceFileUrl(f, p, { preview: true }) } : v);
      }
      return;
    }
    setViewer({ label: here, status: 'ready', kind: 'pdf', url: pieceFileUrl(f, p, { preview: true }), dl, href });
  };

  const docIdDeCv = (f, p) => {
    if (pieceKind(p.label) !== 'cv') return null;
    const choisi = cvChoisi[cvChoisiKey(f)];
    return choisi?.documentId ?? p.doc_id ?? p.document_id ?? p.documentId ?? p.candidate_document_id ?? p.candidateDocumentId ?? null;
  };
  const downloadCvPdf = (f, p) => {
    const { candidate_id } = folderIds(f);
    const docId = docIdDeCv(f, p);
    if (candidate_id == null || docId == null) return;
    const a = document.createElement('a');
    a.href = avecCle(`${BASE}/candidates/${candidate_id}/documents/${docId}/preview?download=1&format=pdf`);
    a.download = ''; a.target = '_blank'; a.click();
  };

  const downloadPiece = (f, p) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) { const h = pieceHref(p); if (h) window.open(h, '_blank', 'noopener'); else showNotification?.('Pièce non localisable.', 'error'); return; }
    const a = document.createElement('a'); a.href = pieceFileUrl(f, p, { download: true }); a.download = p.original_name || p.label || 'piece'; a.target = '_blank'; a.click();
  };

  const piecePost = async (f, p, body, okMsg) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) { showNotification?.('Dossier non rattaché à un candidat/besoin — relance l\'analyse.', 'error'); return; }
    // Identité du document quand la pièce la porte : la copie résout alors par
    // id au lieu de deviner par libellé — l'œil trouvait le document, la copie
    // répondait « aucun document "CNI" » (même motif que la suppression #419,
    // consommé côté serveur). body garde le dernier mot (le picker envoie déjà
    // son propre document_id).
    const docFiche = p.doc_id ?? p.document_id ?? p.documentId ?? p.candidate_document_id ?? p.candidateDocumentId ?? null;
    const key = pieceKey(f, p); setBusy(key, true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/piece?rescan=1`, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, label: p.label, ...(docFiche != null ? { document_id: docFiche } : {}), ...body }) });
      const j = await r.json().catch(() => ({}));
      const nomDossier = (f.path || '').split('/').pop();
      // 409 : la pièce est DÉJÀ dans le dossier kDrive. Ce n'est pas une panne,
      // c'est l'état recherché — on classe la pièce comme réglée au lieu de
      // laisser le bouton « Copier sur kDrive » narguer l'utilisateur.
      if (r.status === 409) {
        markAssist(`${aoId}|${nomDossier}|${p.label}`, 'upload');
        showNotification?.(`« ${nomPiece(p)} » est déjà dans le dossier kDrive — rien à copier.`, 'info');
        scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // okMsg peut être une fonction de la réponse : la génération affiche le
      // nom de fichier réellement posé (`filename`, après assainissement back).
      const okTxt = typeof okMsg === 'function' ? okMsg(j) : okMsg;
      // document_id : la pièce est aussi enregistrée sur la fiche candidat, donc
      // réutilisable sur un autre lot via « Depuis la fiche ».
      showNotification?.(j.document_id != null ? `${okTxt} — également ajouté aux documents du candidat` : okTxt, 'success');
      readSync(j);
      // Le fichier est sur kDrive (confirmé) même quand le scan ne reconnaît
      // pas son nom : sans ce marquage, la pièce resterait « à copier » pour
      // toujours et l'assistant tournerait en rond.
      const ligne = (j.report?.folders || []).find(x => x.path === f.path)?.pieces?.find(x => x.label === p.label);
      if (j.report && ligne && ligne.status !== 'present') {
        markAssist(`${aoId}|${nomDossier}|${p.label}`, 'upload');
      }
      if (j.report) { setReport(j.report); if (j.kdrive_url) setKdriveUrl(j.kdrive_url); loadTree(aoId); }
      else refreshDetail();
      return true;
    } catch (e) {
      // Référence morte : la pièce pointe un document qui n'existe plus
      // (supprimé de la fiche). Le rapport est périmé — on rescanne pour que
      // les boutons redisent la vérité (« à fournir ») au lieu de re-échouer.
      if (/non trouv|introuvable/i.test(e.message || '')) {
        showNotification?.(`« ${nomPiece(p)} » pointe vers un fichier qui n'existe plus — le dossier est rescanné pour remettre la pièce « à fournir ».`, 'error');
        scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
      } else {
        showNotification?.(`Erreur : ${e.message}`, 'error');
      }
    }
    finally { setBusy(key, false); }
  };

  // Le scan ne reconnaît pas toujours le nom du fichier (ex. « CI_Dupont.pdf »
  // pour la pièce « CNI ») : il reste alors en fichier libre. On laisse
  // l'utilisateur le classer lui-même dans la checklist.
  const assignPiece = async (f, freeFile, targetLabel) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) { showNotification?.('Dossier non rattaché à un candidat/besoin — relance l\'analyse.', 'error'); return; }
    const key = pieceKey(f, freeFile); setBusy(key, true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/piece?rescan=1`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, label: targetLabel, action: 'assign', source_file: freeFile.original_name || freeFile.label }),
      });
      if (r.status === 501) {
        showNotification?.(`Le classement manuel d'un fichier arrive côté serveur — en attendant, renommez le fichier en « ${targetLabel} » dans kDrive puis « Rescanner ».`, 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 404 = fichier introuvable dans le dossier, 409 = pièce déjà pourvue,
      // 502 = échec kDrive : on affiche la raison renvoyée par le serveur.
      if (!r.ok || j.success === false) throw new Error(j.error || (r.status === 404 ? 'Fichier introuvable dans le dossier — relancez « Rescanner ».' : `HTTP ${r.status}`));
      const src = j.source_file || freeFile.original_name || freeFile.label;
      showNotification?.(
        j.renamed && j.name
          ? `« ${src} » classé comme « ${j.label || targetLabel} » et renommé « ${j.name} » sur kDrive`
          : `« ${src} » classé comme « ${j.label || targetLabel} »`,
        'success');
      readSync(j);
      if (j.report) { setReport(j.report); loadTree(aoId); }
      else scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    } catch (e) { showNotification?.(`Erreur classement : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };

  // ── Copier un document de la fiche candidat vers kDrive ───────────────────
  // Une pièce peut manquer sur kDrive alors que le document existe déjà sur la
  // fiche : plutôt que de le re-téléverser depuis le poste, on le recopie.
  const [pickDoc, setPickDoc] = useState(null);   // { f, p, docs | null, mode }
  // Nationalité demandée au dépôt d'une pièce d'identité ou d'un permis (04.11) :
  // elle part sur la fiche, d'où le récapitulatif Excel la lit (et en déduit
  // le permis par défaut de l'organisation, jamais pour une nationalité suisse).
  const [natDemande, setNatDemande] = useState(null); // { candidate_id, nom, valeur, busy }
  const [genCvNom, setGenCvNom] = useState(null); // { f, p, nom, nomInitial } — nom du CV à générer
  const [carteAo, setCarteAo] = useState(null);     // { f, photo, regenerer } — carte profil à déposer
  const [carteBusy, setCarteBusy] = useState({});   // chemin du dossier → dépôt en cours
  // Filtre levé à la demande : si aucun document n'est marqué comme CV, on
  // laisse voir la liste complète plutôt que d'afficher un écran vide.
  const [cvTousDocs, setCvTousDocs] = useState(false);
  // Choix envoyés et acceptés par le serveur, en attendant que le scan les
  // renvoie en cv_designe. Sans ça, la recruteuse ne sait pas si son clic a pris.
  const [cvChoisi, setCvChoisi] = useState({});
  const cvChoisiKey = (f) => { const { besoin_id, candidate_id } = folderIds(f); return `${besoin_id}|${candidate_id}`; };

  /**
   * Trace locale des désignations CONFIRMÉES par le serveur, avec leur date.
   *
   * Elle ne sert jamais à afficher un succès : après reconnexion, seule la
   * réponse du scan fait foi. Elle sert à détecter la DIVERGENCE — le serveur a
   * confirmé une désignation, et son propre scan ne la renvoie plus. Sans cette
   * trace, la perte est silencieuse : le badge retombe sur « par défaut » et
   * personne ne sait qu'un choix a existé, encore moins qu'il a disparu.
   */
  const CLE_DESIGNATIONS = 'ao_cv_designations';
  const lireDesignations = () => { try { return JSON.parse(localStorage.getItem(CLE_DESIGNATIONS) || '{}'); } catch (e) { return {}; } };
  const memoriserDesignation = (cle, d) => {
    try { localStorage.setItem(CLE_DESIGNATIONS, JSON.stringify({ ...lireDesignations(), [cle]: d })); } catch (e) { /* stockage plein : tant pis pour la trace */ }
  };
  const oublierDesignation = (cle) => {
    try { const m = lireDesignations(); delete m[cle]; localStorage.setItem(CLE_DESIGNATIONS, JSON.stringify(m)); } catch (e) { /* noop */ }
  };
  const designationPerdue = (f, p2) => {
    if (pieceKind(p2.label) !== 'cv' || p2.cv_designe === true) return null;
    if (cvChoisi[cvChoisiKey(f)]?.designe) return null;   // confirmée dans cette session
    return lireDesignations()[cvChoisiKey(f)] || null;
  };

  // Un CV désigné vient d'une décision de la recruteuse, pas d'un nom de fichier.
  const estCvDoc = (d) => d?.est_cv === true || d?.source === 'cv_prep'
    || /(^|[^a-z])cv([^a-z]|$)/i.test(`${d?.label || ''} ${d?.filename || ''}`);
  // Reconnaissance souple du format entreprise : sert seulement à décider si on
  // propose la génération, jamais à qualifier un document.
  const estCvEntreprise = (d) => d?.source === 'cv_prep'
    || /format\s*entreprise/i.test(`${d?.label || ''} ${d?.filename || ''}`);

  const openDocPicker = async (f, p, mode = 'copie') => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) { showNotification?.('Dossier non rattaché à un candidat/besoin — relance l\'analyse.', 'error'); return; }
    setPickDoc({ f, p, docs: null, mode });
    setCvTousDocs(false);
    try {
      // Source complète : y compris les documents que le scan n'a pas rapprochés.
      let r = await fetch(`${BASE}/candidates/${candidate_id}/documents`, { headers: authHeaders });
      if (!r.ok) r = await fetch(`${BASE}/besoins/${besoin_id}/candidates/${candidate_id}/documents`, { headers: authHeaders });
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j.documents || (Array.isArray(j) ? j : []);
      const actifs = (Array.isArray(arr) ? arr : [])
        .filter(d => (d.status || 'active') === 'active' && (d.file_id != null || d.download_url || d.id != null))
        .filter(d => isAdmin || !estDocContrat(d));
      // Documents préparés POUR CE BESOIN : ils vivent dans une section à part
      // (position_documents), groupée par besoin, et n'apparaissaient donc pas.
      const pos = j.position_documents || j.data?.position_documents || null;
      const pourBesoin = (() => {
        if (!pos) return [];
        const grp = Array.isArray(pos)
          ? (pos.find(g => String(g.besoin_id ?? g.besoinId) === String(besoin_id))?.documents || [])
          : (pos[besoin_id] ?? pos[String(besoin_id)] ?? []);
        return (Array.isArray(grp) ? grp : []).map(d => {
          // Deux identifiants distincts : celui du document de la fiche (s'il
          // existe) et celui de la pièce du positionnement. Le premier sert à
          // désigner et à visualiser ; le second ne sait que se visualiser, par
          // sa propre route — les confondre donnait un aperçu introuvable.
          const cdId = d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id ?? null;
          const pdId = d.id ?? d.position_document_id ?? null;
          return { ...d, id: cdId ?? pdId, _cdId: cdId, _pdId: pdId, _duBesoin: true };
        });
      })();
      // Deux passes de dédoublonnage. Sur l'identifiant d'abord : un même
      // document figure dans les deux sections. Puis sur le libellé et la date,
      // parce que le même CV peut arriver sous deux identifiants distincts et
      // qu'afficher deux lignes identiques n'aide personne à choisir.
      const vus = new Set(); const vusNom = new Set();
      const tous = [...pourBesoin, ...actifs].filter(d => {
        const k = String(d.id ?? d.document_id ?? d.file_id ?? '');
        if (!k || vus.has(k)) return false;
        const nk = `${(d.label || d.filename || '').trim().toLowerCase()}|${(d.created_at || d.updated_at || '').slice(0, 10)}`;
        if (nk !== '|' && vusNom.has(nk)) return false;
        vus.add(k); vusNom.add(nk); return true;
      });
      // Le plus récent en tête : on vient presque toujours chercher le dernier
      // CV généré, pas celui de juin. Les documents sans date passent en fin —
      // une date inconnue ne doit pas se faire passer pour récente.
      const dateDe = (d) => {
        const t2 = Date.parse(d.updated_at || d.created_at || d.uploaded_at || '');
        return Number.isNaN(t2) ? -Infinity : t2;
      };
      tous.sort((d1, d2) => dateDe(d2) - dateDe(d1));
      setPickDoc(t => (t ? { ...t, docs: tous } : t));
    } catch { setPickDoc(t => (t ? { ...t, docs: [] } : t)); }
  };
  const copyDocToKdrive = async (doc) => {
    const { f, p } = pickDoc || {};
    if (!f || !p || !doc) return;
    const { besoin_id, candidate_id } = folderIds(f);
    setPickDoc(null);
    const key = pieceKey(f, p); setBusy(key, true);
    try {
      const docId = doc.id ?? doc.document_id ?? doc.doc_id ?? null;
      const r = await fetch(`${BASE}/ao/${aoId}/piece?rescan=1`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, label: p.label, action: 'upload',
          ...(docId != null ? { document_id: docId } : { file_id: doc.file_id ?? null }) }),
      });
      const j = await r.json().catch(() => ({}));
      // 409 : la pièce est déjà pourvue — remplacer est une décision, pas un
      // réessai ; on propose donc explicitement le remplacement.
      if (r.status === 409) {
        if (await confirm({ title: 'Pièce déjà pourvue', message: j.error || j.message || `La pièce « ${nomPiece(p)} » est déjà pourvue.`, detail: 'Remplacer le document actuellement dans le dossier kDrive par celui que vous venez de choisir ?', confirmLabel: 'Remplacer' })) {
          await deletePiece(f, p);
          setPickDoc({ f, p, docs: null }); openDocPicker(f, p);
        }
        return;
      }
      // 404 (document hors de cette fiche) et 502 : messages affichables tels quels.
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      // Le back renomme parfois pour que le scan reconnaisse la pièce.
      showNotification?.(`« ${j.file || doc.label || p.label} » copié depuis la fiche dans le dossier kDrive`, 'success');
      readSync(j);
      // Le rapport renvoyé est à jour : pas de scan supplémentaire.
      if (j.report) { setReport(j.report); if (j.kdrive_url) setKdriveUrl(j.kdrive_url); loadTree(aoId); }
    } catch (e) { showNotification?.(`Copie impossible : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };

  // ── Désigner LE CV du candidat pour ce besoin ─────────────────────────────
  // Un CV reconnu par son nom de fichier n'engage personne. Désigné, il devient
  // le CV de référence pour ce besoin — et le scan le renvoie en cv_designe.
  const designerCv = async (doc, cible = null) => {
    const { f, p } = cible || pickDoc || {};
    if (!f || !p || !doc) return;
    const { besoin_id, candidate_id } = folderIds(f);
    const docId = doc.id ?? doc.document_id ?? doc.doc_id ?? null;
    if (besoin_id == null || candidate_id == null || docId == null) {
      showNotification?.('Document ou rattachement incomplet — relance l\'analyse.', 'error'); return;
    }
    setPickDoc(null);
    const key = pieceKey(f, p); setBusy(key, true);
    try {
      const r = await fetch(`${BASE}/candidates/${candidate_id}/besoins/${besoin_id}/cv`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: docId, doc_id: docId }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501) {
        showNotification?.('Le choix du CV arrive côté serveur — rien n\'a été enregistré.', 'info');
        return;
      }
      // 404 : identifiant refusé, avec le motif. Plus de faux succès à filtrer.
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);

      // La réponse porte la désignation : le badge s'affiche sans attendre de
      // scan. L'identifiant retenu peut différer de celui envoyé — c'est celui
      // que le rapport portera, donc c'est LUI qu'on garde.
      const idRetenu = j.document_id ?? j.doc_id ?? docId;
      const nom = j.label || doc.label || doc.filename || 'CV';
      if (j.cv_designe === true) {
        setCvChoisi(m => ({ ...m, [cvChoisiKey(f)]: { nom, documentId: idRetenu, designe: true } }));
        memoriserDesignation(cvChoisiKey(f), { nom, documentId: idRetenu, date: new Date().toISOString() });
      }

      // Le dépôt remplace directement le CV du dossier : plus de suppression
      // préalable, donc plus d'instant où la pièce n'existe pas.
      const rc = await fetch(`${BASE}/ao/${aoId}/piece?rescan=1`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, label: p.label, action: 'upload', document_id: idRetenu }),
      });
      const jc = await rc.json().catch(() => ({}));
      const pose = rc.ok && jc.success !== false;
      if (jc.report) { setReport(jc.report); if (jc.kdrive_url) setKdriveUrl(jc.kdrive_url); loadTree(aoId); }

      if (pose) {
        const ancien = jc.remplace === true
          ? ` — remplace ${jc.ancien?.label || jc.ancien?.file || jc.ancien?.filename || 'le CV précédent'}`
          : '';
        showNotification?.(`« ${nom} » est le CV de ce besoin et il est dans le dossier kDrive${ancien}`, 'success');
      } else {
        showNotification?.(`« ${nom} » est bien le CV de ce besoin, mais sa copie sur kDrive a échoué : ${jc.error || jc.message || `HTTP ${rc.status}`}`, 'error');
      }
    } catch (e) { showNotification?.(`Choix impossible : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };

  /**
   * Téléverser un CV depuis le poste, directement sur la ligne du candidat.
   *
   * Deux temps, et le second compte autant que le premier : le fichier entre
   * d'abord dans les documents du candidat — sinon il n'existerait que sur
   * kDrive, invisible de la fiche et du matching —, puis il est désigné CV de
   * ce besoin par le même chemin que le sélecteur. Un document déposé sans
   * être désigné ne servirait à rien ici : c'est bien « le CV de ce lot »
   * qu'on veut, pas « un fichier de plus ».
   */
  const televerserCv = async (f, p, file) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (!file || candidate_id == null) { showNotification?.('Candidat non rattaché — relance l\'analyse.', 'error'); return; }
    const key = pieceKey(f, p); setBusy(key, true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', (file.name || 'CV').replace(/\.[^.]+$/, '').trim() || 'CV');
      fd.append('type_piece', 'cv');
      const r = await fetch(`${BASE}/candidates/${candidate_id}/documents`, { method: 'POST', headers: authHeaders, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const cree = j.data || j.document || j;
      const docId = cree.id ?? cree.document_id ?? cree.doc_id ?? null;
      if (docId == null) throw new Error('le serveur n\'a pas renvoyé l\'identifiant du document');
      showNotification?.(`« ${file.name} » ajouté aux documents du candidat`, 'success');
      if (besoin_id == null) return;   // pas de besoin : le document reste sur la fiche
      await designerCv({ id: docId, label: cree.label || cree.name || file.name }, { f, p });
    } catch (e) { showNotification?.(`Téléversement impossible : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };
  const askCvFile = (f, p) => { cvTargetRef.current = { f, p }; cvFileInputRef.current?.click(); };

  const uploadPieceCopy = (f, p) => piecePost(f, p, { action: 'upload' }, `« ${nomPiece(p)} » copié dans le dossier`);
  // Génération. Pour un CV, un petit volet propose d'abord de choisir (ou de
  // garder) le nom du fichier — contrat back #380 : `nom_fichier` facultatif,
  // sans extension ; le nom d'un couple candidat×besoin est mémorisé côté
  // serveur, donc on ne l'envoie QUE si l'utilisateur l'a modifié (le renvoyer
  // = le changer). La réponse expose `filename`, le nom réellement posé.
  const generatePieceDirect = (f, p, nomFichier = null) => piecePost(f, p,
    { action: 'generate', ...(nomFichier ? { nom_fichier: nomFichier } : {}) },
    (j) => {
      // Ce que la génération n'a pas pu remplir sans inventer (langues vides,
      // mission sans tâches, dates en années seules…) : dit tout de suite,
      // pour corriger avant que le CV parte (30.10, expert AO).
      if (Array.isArray(j?.a_verifier) && j.a_verifier.length) {
        setTimeout(() => showNotification?.(`À vérifier sur ce CV : ${j.a_verifier.join(' · ')}`, 'warning'), 600);
      }
      return `« ${nomPiece(p)} » généré${j?.filename ? ` — fichier « ${j.filename} »` : ''}`;
    });
  const generatePiece = (f, p) => {
    if (pieceKind(p.label) !== 'cv') { generatePieceDirect(f, p); return; }
    const actuel = String(p.original_name || p.file_name || p.filename || '').replace(/\.[^.]+$/, '');
    setGenCvNom({ f, p, nom: actuel, nomInitial: actuel });
  };
  // ── Contrôle qualité : les gestes existants que ses constats désignent ──
  // Le rapport nomme un geste ({ outil, entree }) ; on le joue par le bouton
  // qui existe déjà ici — mêmes routes, mêmes contrôles.
  const lotDuBesoin = (bid) => (report?.folders || [])
    .find(f => f.bind !== 'candidate' && String(folderIds(f).besoin_id) === String(bid)) || null;
  const lotsControle = () => (report?.folders || [])
    .filter(f => f.bind !== 'candidate' && folderIds(f).besoin_id != null)
    .map(f => ({ besoin_id: folderIds(f).besoin_id, titre: f.reference_interne || f.label || f.name || String(f.path || '').split('/').pop() }));
  const gestesControle = {
    generer: async (besoinId, candidateId, piece) => {
      const f = (report?.folders || []).find(x => x.bind === 'candidate'
        && String(folderIds(x).besoin_id) === String(besoinId) && String(folderIds(x).candidate_id) === String(candidateId));
      const voulu = pieceKind(String(piece || '').replace(/_tat$/i, ''));
      const p = (f?.pieces || []).find(x => pieceKind(x.label) === voulu)
        || (f?.pieces || []).find(x => String(x.label || '').toLowerCase() === String(piece || '').toLowerCase());
      if (!f || !p) { showNotification?.('Cette pièce n\'apparaît pas dans le dossier du candidat : rescannez le dossier, puis réessayez.', 'error'); return; }
      await generatePieceDirect(f, p);
    },
    ouvrirFiche: onOpenCandidat ? (cid) => onOpenCandidat(cid) : null,
    ouvrirBesoin: onOpenBesoin ? (bid) => onOpenBesoin(bid) : null,
    retirer: (cid, bid, nom, poste) => retirerDuBesoin(cid, bid, nom, poste),
    renommerLot: (bid, nom) => {
      const lf = lotDuBesoin(bid);
      if (!lf) { showNotification?.('Ce lot n\'apparaît pas dans le rapport du dossier : rescannez, puis réessayez.', 'error'); return; }
      const label = lf.label || lf.name || String(lf.path || '').split('/').pop();
      setRenameLot({ lot: { ...lf, folder: lf, path: lf.path, label }, value: nom || String(label || '').replace(/^lot\s*\d+\s*:\s*/i, '') });
    },
    ouvrirLot: (bid) => {
      const lf = lotDuBesoin(bid);
      setAssistOpen(false); setReportTab('pieces'); setPieceQuery('');
      if (lf?.path) setExpandedLot(lf.path);
    },
    rescanner: () => rescan(),
  };

  // Carte profil (03.11) : la carte de CE lot, générée et déposée dans le
  // dossier du candidat par le serveur (argumentaire rédigé s'il manque, PDF
  // de l'export, ancienne carte remplacée). Le message du serveur fait foi.
  const deposerCarte = async ({ f, photo, regenerer }) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) return;
    setCarteBusy(m => ({ ...m, [f.path]: true }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/carte-profil`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id, candidate_id, photo: !!photo, regenerer: !!regenerer }),
      });
      if (r.status === 501 || (r.status === 404 && !(r.headers.get('content-type') || '').includes('json'))) {
        showNotification?.('Le dépôt de la carte profil dans le dossier arrive côté serveur.', 'info');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Carte profil déposée dans le dossier du candidat.', j.sans_photo ? 'warning' : 'success');
      // La carte est un fichier libre du dossier : le rapport relu la montre
      // sous le candidat (œil, téléchargement), sans attendre un rescan.
      await scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true });
    } catch (e) { showNotification?.(`Carte profil : ${e.message}`, 'error'); }
    finally { setCarteBusy(m => { const n = { ...m }; delete n[f.path]; return n; }); }
  };
  const demanderNationalite = async (f) => {
    const { candidate_id } = folderIds(f);
    if (candidate_id == null) return;
    const nom = String(f.candidat || f.nom || (f.path || '').split('/').pop() || '').trim();
    setNatDemande({ candidate_id, nom, valeur: '', busy: false });
    try {
      const r = await fetch(`${BASE}/candidats/vue/${candidate_id}`, { headers: authHeaders });
      const j = await r.json().catch(() => ({}));
      const fiche = j.data || j.candidat || j;
      const v = String(fiche?.nationalite || fiche?.nationality || '').trim();
      if (v) setNatDemande(d => (d && d.candidate_id === candidate_id && !d.valeur ? { ...d, valeur: v } : d));
    } catch (e) { /* la saisie reste possible */ }
  };
  const enregistrerNationalite = async () => {
    const d = natDemande; if (!d) return;
    const v = String(d.valeur || '').trim();
    if (!v) { setNatDemande(null); return; }
    setNatDemande({ ...d, busy: true });
    try {
      const r = await fetch(`${BASE}/candidats/vue/${d.candidate_id}`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ nationalite: v }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(`Nationalité enregistrée sur la fiche${d.nom ? ` de ${d.nom}` : ''} : ${v}`, 'success');
      setNatDemande(null);
    } catch (e) {
      showNotification?.(`Nationalité non enregistrée : ${e.message}`, 'error');
      setNatDemande(x => (x ? { ...x, busy: false } : x));
    }
  };
  const askPieceFile = (f, p) => { fileTargetRef.current = { f, p }; pieceFileInputRef.current?.click(); };
  const askNewDoc = (f) => { fileTargetRef.current = { f, p: null }; pieceFileInputRef.current?.click(); };
  const televerserUn = async (f, p, file) => {
    const piece = p || { label: (file.name || 'Document').replace(/\.[^.]+$/, '').trim() || 'Document' };
    const key = pieceKey(f, piece); setBusy(key, true);
    if (!p) showNotification?.(`Envoi de « ${nomPiece(piece)} »…`, 'success');
    try {
      const base64 = await new Promise((res, rej) => { const rd = new FileReader(); rd.onload = () => res(String(rd.result).split(',')[1]); rd.onerror = rej; rd.readAsDataURL(file); });
      const ok = await piecePost(f, piece, { action: 'upload', base64, original_name: file.name, mime_type: file.type || 'application/octet-stream' }, `« ${nomPiece(piece)} » ${p ? 'fourni' : 'ajouté au dossier'}`);
      if (ok && PIECE_IDENTITE.test(`${piece.label || ''} ${file.name || ''}`)) demanderNationalite(f);
    } catch (e) { showNotification?.(`Erreur lecture fichier : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };
  const onPieceFiles = async (fichiers) => {
    const target = fileTargetRef.current; fileTargetRef.current = null;
    const liste = Array.from(fichiers || []).filter(Boolean);
    if (!liste.length || !target) return;
    const { f, p } = target;
    // Une pièce précise (fournir/remplacer) ne prend qu'UN fichier ; l'ajout
    // libre les prend tous, un envoi par fichier — chacun a son verdict.
    if (p) { await televerserUn(f, p, liste[0]); return; }
    for (const file of liste) await televerserUn(f, null, file);
  };
  // Glisser-déposer sur un lot : mêmes envois que le bouton « Ajouter ».
  const deposerFichiers = async (f, fichiers) => {
    const { besoin_id, candidate_id } = folderIds(f);
    if (besoin_id == null || candidate_id == null) { showNotification?.('Ce dossier n\'est pas rattaché à un candidat/besoin — déposez sur un lot rattaché.', 'error'); return; }
    for (const file of Array.from(fichiers || []).filter(Boolean)) await televerserUn(f, null, file);
  };

  const deletePiece = async (f, p) => {
    // Une pièce « à copier » n'est pas sur kDrive : la supprimer détache le
    // document côté ERP et la pièce repasse « à fournir » — c'est l'issue de
    // secours quand la copie tourne en rond.
    const surKdrive = p.status === 'present';
    // La pièce peut être servie par un document de la FICHE candidat (import
    // Boond, upload fiche). Décision métier serveur : le supprimer, c'est le
    // supprimer DE LA FICHE, tous dossiers confondus — la confirmation le dit
    // avant le clic, jamais après.
    const docFiche = p.doc_id ?? p.document_id ?? p.documentId ?? p.candidate_document_id ?? p.candidateDocumentId ?? null;
    if (!(await confirm(surKdrive
      ? { title: 'Supprimer la pièce', message: `Supprimer « ${nomPiece(p)} » du dossier kDrive ?`, confirmLabel: 'Supprimer' }
      : docFiche != null
        ? { title: 'Supprimer de la fiche candidat', message: `« ${nomPiece(p)} » vient de la fiche du candidat.`, detail: 'Le supprimer ici le supprime DE LA FICHE, pour tous les dossiers qui s\'en servent. Le dossier kDrive n\'est pas touché (le fichier n\'y est pas). Cette action est irréversible.', confirmLabel: 'Supprimer de la fiche' }
        : { title: 'Détacher la pièce', message: `Détacher le document de « ${nomPiece(p)} » ?`, detail: 'Le dossier kDrive n\'est pas touché (le fichier n\'y est pas). La pièce repassera « à fournir » et vous pourrez repartir d\'un fichier propre.', confirmLabel: 'Détacher' }))) return;
    const { besoin_id, candidate_id } = folderIds(f);
    const key = pieceKey(f, p); setBusy(key, true);
    try {
      const qs = new URLSearchParams({ besoin_id, candidate_id, label: p.label, rescan: '1' });
      if (docFiche != null) qs.set('document_id', String(docFiche));
      const r = await fetch(`${BASE}/ao/${aoId}/piece/file?${qs.toString()}`, { method: 'DELETE', headers: authHeaders });
      const j = await r.json().catch(() => ({}));
      // 409 : la pièce est servie par le CV entreprise de la fiche — il n'y a
      // rien à supprimer ici, le message du serveur explique quoi faire.
      if (r.status === 409) { showNotification?.(j.message || j.error || 'Cette pièce est servie par le CV de la fiche — rien à supprimer ici.', 'info'); return; }
      // « Introuvable » alors que la pièce vient d'un document de la fiche : le
      // serveur ne gère pas encore ce cas — on offre le geste qui marche.
      if (!r.ok && docFiche != null && candidate_id != null && /introuvable/i.test(j.message || j.error || '')) {
        setBusy(key, false);
        if (!(await confirm({
          title: 'Supprimer de la fiche candidat',
          message: `« ${nomPiece(p)} » n'est pas dans le dossier kDrive : ce fichier vit sur la fiche du candidat.`,
          detail: 'Le supprimer de la fiche ? Il disparaîtra aussi de ce dossier au prochain scan. Cette action est irréversible.',
          confirmLabel: 'Supprimer de la fiche',
        }))) return;
        setBusy(key, true);
        const r2 = await fetch(`${BASE}/candidates/${candidate_id}/documents/${docFiche}`, { method: 'DELETE', headers: authHeaders });
        if (!r2.ok) { const j2 = await r2.json().catch(() => ({})); throw new Error(j2.message || j2.error || `HTTP ${r2.status}`); }
        showNotification?.(`« ${nomPiece(p)} » supprimé de la fiche candidat`, 'success');
        refreshDetail();
        return;
      }
      if (!r.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      // Le serveur dit ce qu'il a réellement fait (portee/cas) : le message suit.
      showNotification?.(
        j.portee === 'fiche' ? `« ${nomPiece(p)} » supprimé de la fiche candidat — il disparaît de tous les dossiers`
          : j.cas === 'designation' ? `Le CV n'est plus désigné pour ce besoin — le document reste sur la fiche`
          : j.cas === 'erp' ? `« ${nomPiece(p)} » détaché — la pièce est à fournir, le document reste sur la fiche`
          : j.deleted ? `« ${nomPiece(p)} » supprimé du dossier kDrive`
          : (surKdrive ? `« ${nomPiece(p)} » supprimé` : `« ${nomPiece(p)} » détaché — la pièce est à fournir`),
        'success');
      if (pieceKind(p.label) === 'cv') {
        setCvChoisi(m => { const n = { ...m }; delete n[cvChoisiKey(f)]; return n; });
        oublierDesignation(cvChoisiKey(f));
      }
      readSync(j);
      if (j.report) { setReport(j.report); if (j.kdrive_url) setKdriveUrl(j.kdrive_url); loadTree(aoId); }
      else refreshDetail();
    } catch (e) { showNotification?.(`Erreur suppression : ${e.message}`, 'error'); }
    finally { setBusy(key, false); }
  };

  const isGeneratable = (p) => p.generatable === true || /cv\s*tat|cv\s*entreprise|matrice/i.test(p.label || '');

  /**
   * Le CV du dossier kDrive n'est plus la dernière version connue de l'ERP.
   *
   * La comparaison est faite par le scan — lui seul voit les deux côtés. Tant
   * que le dossier porte ce drapeau, tout ce qui part de kDrive (livraison au
   * client en tête) part avec un CV périmé : c'est LE cas à rendre impossible
   * à rater, d'où le rouge sur la pièce ET sur l'en-tête du dossier.
   */
  const cvObsolete = (p) => p?.cv_obsolete === true || p?.cv_outdated === true || p?.obsolete === true;
  const dossierCvObsolete = (f) => piecesVisibles(f).some(pc => pieceKind(pc.label) === 'cv' && cvObsolete(pc));

  // ════════════════════════ RENDER ════════════════════════
  const Header = ({ children }) => (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
      <div>
        <h1 className="ats-h1">Dossiers AO<span className="ats-dot">.</span></h1>
        <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: 'var(--text-2)' }}>Analyse des pièces par candidat et synchronisation kDrive.</p>
      </div>
      {children}
    </div>
  );

  // Un dossier de candidat (rattaché, ou présent sur le drive sans l'être) :
  // le dernier segment du chemin est son nom (« MYC LEMOIGNE audrey »).
  const estLigneCandidat = (f) => f.bind === 'candidate' || (f._extra && String(f.path || '').split('/').filter(Boolean).length >= 3);
  const nomDuDossier = (f) => {
    const seg = String(f.path || '').split('/').filter(Boolean);
    return seg[seg.length - 1] || f.folder_name || f.path;
  };

  const renderFolder = (f, shownPieces, k) => (
    <Dossier key={k} level={f.bind === 'candidate' ? 'candidate' : 'lot'}
      data-revue-cv={f.bind === 'candidate' ? `${folderIds(f).besoin_id}:${folderIds(f).candidate_id}` : undefined}
      style={{ marginBottom: '12px', ...(dropLot === f.path ? { outline: '2px dashed var(--erp-accent)', outlineOffset: '-2px', background: 'var(--erp-accent-soft)' } : {}) }}
      onDragOver={e => { if (folderIds(f).candidate_id != null && folderIds(f).besoin_id != null && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDropLot(f.path); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropLot(prev => (prev === f.path ? null : prev)); }}
      onDrop={e => { if (folderIds(f).candidate_id == null || folderIds(f).besoin_id == null) return; e.preventDefault(); setDropLot(null); deposerFichiers(f, e.dataTransfer?.files); }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: shownPieces.length ? '1px solid var(--hair)' : 'none' }}>
        <BI icon={f.bind === 'candidate' ? 'person' : 'folder'} style={{ color: 'var(--text-3)', flex: 'none' }} />
        {/* Une ligne de candidat dit QUI : le nom du dossier (Nom prénom), pas le
            chemin kDrive — le lot est déjà nommé en tête, sa référence avec lui.
            Le chemin complet reste en info-bulle. */}
        {estLigneCandidat(f)
          ? <span title={f.path} style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', wordBreak: 'break-word' }}>{nomDuDossier(f)}</span>
          : <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)', wordBreak: 'break-word' }}>{f.path}</span>}
        {f.bind === 'candidate' && dossierCvObsolete(f) && (
          <Badge bg="var(--erp-danger-soft)" fg="var(--erp-danger)" title="Le CV présent dans le dossier kDrive n'est pas la dernière version — ce qui partirait au client est périmé.">
            <BI icon="exclamation-octagon-fill" /> CV pas à jour
          </Badge>
        )}
        {/* Dossier complet : toutes les pièces de la checklist sont présentes.
            Sauf si le CV est périmé — complet avec un mauvais CV n'est pas prêt. */}
        {f.bind === 'candidate' && !dossierCvObsolete(f) && (() => {
          const { done, total, complete } = folderProgress(f);
          if (!total) return null;
          return complete
            ? <Badge bg="var(--erp-success-soft)" fg="var(--erp-success)" title="Toutes les pièces de la checklist sont présentes — dossier prêt à être envoyé au client."><BI icon="patch-check-fill" /> Complet — prêt</Badge>
            : <Badge bg="var(--chip-bg)" fg="var(--text-2)" title={`${total - done} pièce(s) manquante(s) sur ${total}`}><BI icon="hourglass-split" /> {done}/{total} pièces</Badge>;
        })()}
        {f._extra && <Badge bg="var(--erp-warning-soft)" fg="var(--erp-warning)" title="Dossier présent sur kDrive mais non rattaché à un besoin de l'ERP (nom non reconnu : doublon, faute de frappe, ou candidat non identifié)."><BI icon="question-circle" /> Non rattaché</Badge>}
        {f.ambiguous && <Badge bg="var(--erp-warning-soft)" fg="var(--erp-warning)"><BI icon="exclamation-triangle" /> À arbitrer</Badge>}
        {f.exists === false && <Badge bg="var(--chip-bg)" fg="var(--text-2)"><BI icon="plus-lg" /> À créer</Badge>}
        {f.exists === true && !f._extra && <Badge bg="var(--erp-success-soft)" fg="var(--erp-success)"><BI icon="check2" /> Existe</Badge>}
        {folderIds(f).besoin_id != null && folderIds(f).candidate_id != null && (
          <button onClick={() => askNewDoc(f)} title="Ajouter un document libre à ce dossier (uploadé sur kDrive + classé dans le dossier AO)" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="paperclip" /> Ajouter</button>
        )}
        {folderIds(f).besoin_id != null && folderIds(f).candidate_id != null && (() => {
          const busy = !!carteBusy[f.path];
          return (
            <button onClick={() => setCarteAo({ f, photo: false, regenerer: false })} disabled={busy}
              title="Générer la carte profil de ce candidat pour ce lot et la déposer dans son dossier"
              className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
              <BI icon={busy ? 'arrow-repeat' : 'person-vcard'} style={busy ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {busy ? 'Carte…' : 'Carte profil'}
            </button>
          );
        })()}
        {onOpenCandidat && folderIds(f).candidate_id != null && (
          <button onClick={() => onOpenCandidat(folderIds(f).candidate_id)} title="Ouvrir la fiche candidat" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="person-badge" /> Fiche</button>
        )}
        {/* Réutiliser un dossier déjà complet sur un autre lot de l'AO. */}
        {folderIds(f).candidate_id != null && (
          <button onClick={() => askCopyCandidate(f)} title="Présenter ce candidat sur un autre lot de l'AO, en reprenant ses documents"
            className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="files" /> Autre lot</button>
        )}
        {/* Dossier sur kDrive sans candidat positionné sur le besoin. */}
        {f.bind === 'candidate' && folderIds(f).candidate_id == null && f.exists !== false && (() => {
          const busy = !!attaching[f.path];
          // Sans besoin sur le lot parent, il n'y a rien à quoi rattacher :
          // on le dit sur le bouton plutôt que d'attendre le clic.
          const sansBesoin = besoinOfPath(f.path) == null;
          return (
            <button onClick={() => attachFolder(f)} disabled={busy || sansBesoin}
              title={sansBesoin
                ? 'Ce lot n\'a pas encore de besoin dans l\'ERP — utilisez « Créer le besoin » sur le lot, puis revenez ajouter ce candidat'
                : 'Ajouter ce dossier au besoin du lot : la fiche candidat est créée si besoin et positionnée, le dossier kDrive existant est réutilisé'}
              className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
              <BI icon={busy ? 'arrow-repeat' : 'plus-lg'} style={busy ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {busy ? 'Ajout…' : 'Ajouter au besoin'}
            </button>
          );
        })()}
        {/* Retire le candidat DU LOT : dossier kDrive + positionnement. La
            fiche candidat est conservée — sa suppression est un second choix
            explicite dans la modale, confirmé deux fois. */}
        {f.bind === 'candidate' && (() => {
          const cid = folderIds(f).candidate_id;
          const busy = !!deletingFolder[f.path] || (cid != null && !!deletingCand[cid]);
          const onKdrive = f.exists !== false;
          return (
            <button onClick={() => deleteCandidateEverywhere(f)} disabled={busy}
              title={cid != null
                ? (onKdrive ? 'Retirer ce candidat du lot (dossier kDrive + positionnement) — sa fiche candidat est conservée' : 'Retirer ce candidat du lot (positionnement) — sa fiche candidat est conservée')
                : 'Supprimer ce dossier sur kDrive (aucune fiche candidat ne lui correspond)'}
              className="erp-btn erp-btn--danger erp-btn--sm" style={{ flex: 'none' }}>
              <BI icon={busy ? 'arrow-repeat' : 'person-dash'} style={busy ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {busy ? 'Retrait…' : 'Retirer du lot'}
            </button>
          );
        })()}
        {(f.url || f.kdrive_url) && <a href={f.url || f.kdrive_url} target="_blank" rel="noreferrer" title="Ouvrir dans kDrive" style={{ flex: 'none', color: 'var(--text-2)', textDecoration: 'none', fontSize: '13px' }}><BI icon="box-arrow-up-right" /></a>}
      </div>
      {/* Plusieurs dossiers kDrive pour la même personne : on les montre au lieu
          de les compter comme autant de profils. */}
      {(f._dupes || []).length > 0 && (
        <div style={{ margin: '0 16px 10px', padding: '8px 10px', borderRadius: '9px', background: 'var(--erp-warning-soft)', border: '1px solid var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '11.5px', lineHeight: 1.5 }}>
          <BI icon="files" style={{ marginRight: '6px' }} />{f._dupes.length + 1} dossiers kDrive pour ce candidat — même personne écrite différemment. Gardez celui qui contient les pièces et supprimez les autres.
          {f._dupes.map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
              <span style={{ flex: 1, minWidth: 0, fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(d.path || '').split('/').pop()}{(d.pieces || []).filter(p => p.status === 'present').length ? ` — ${(d.pieces || []).filter(p => p.status === 'present').length} pièce(s)` : ' — vide'}
              </span>
              {(d.url || d.kdrive_url) && <a href={d.url || d.kdrive_url} target="_blank" rel="noreferrer" title="Ouvrir dans kDrive" style={{ flex: 'none', color: 'var(--erp-warning)' }}><BI icon="box-arrow-up-right" /></a>}
              <button onClick={() => deleteCandidateEverywhere(d)} className="erp-btn erp-btn--danger erp-btn--sm" style={{ flex: 'none' }}>
                <BI icon="trash3" /> Supprimer
              </button>
            </div>
          ))}
        </div>
      )}
      {shownPieces.length > 0 && (
        <div style={{ padding: '8px 16px' }}>
          {shownPieces.map((p, i) => {
            const cfg = PIECE[p.status] || PIECE.a_fournir;
            const bk = pieceKey(f, p); const busy = !!pieceBusy[bk];
            // Une ligne CV dont le CV de référence n'est pas encore désigné.
            // Un CV déjà validé pour le besoin n'a RIEN à choisir ni à
            // téléverser — même s'il n'est pas encore copié sur kDrive :
            // afficher ces boutons contredirait le badge « CV validé ».
            const cvAChoisir = pieceKind(p.label) === 'cv' && folderIds(f).candidate_id != null
              && p.cv_designe !== true && !cvChoisi[cvChoisiKey(f)]?.designe;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: '120px', fontSize: '13px', color: 'var(--text)' }}>{nomPiece(p)}</span>
                {p._libre && <Badge bg="var(--chip-bg)" fg="var(--text-2)" title="Document hors checklist (fichier libre du dossier kDrive)"><BI icon="paperclip" /> Libre</Badge>}
                {/* Relance déjà envoyée au candidat pour cette pièce. */}
                {p.status !== 'present' && p.requested_at && (() => {
                  const d = new Date(p.requested_at);
                  if (Number.isNaN(d.getTime())) return null;
                  return <Badge bg="var(--chip-bg)" fg="var(--text-2)" title={`Pièce demandée au candidat le ${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}><BI icon="send" /> demandé le {d.toLocaleDateString('fr-FR')}</Badge>;
                })()}
                {/* Fichier libre qui EST en fait une pièce attendue mais dont le
                    nom n'a pas été reconnu (ex. « CI_Dupont.pdf » pour « CNI »). */}
                {p._libre && !busy && folderIds(f).candidate_id != null && (() => {
                  const missing = (f.pieces || []).filter(x => x.status !== 'present');
                  if (!missing.length) return null;
                  return (
                    <ErpiaSelect value="" onChange={e => { const v = e.target.value; e.target.value = ''; if (v) assignPiece(f, p, v); }}
                      title="Ce fichier est en fait une pièce attendue ? Rattachez-le à la checklist (ex. un fichier « CI… » à classer comme CNI). Le fichier est renommé en conséquence sur kDrive."
                      className="erp-btn--sm" style={{ flex: 'none', maxWidth: '150px' }}>
                      <option value="">Classer comme…</option>
                      {missing.map(m => <option key={m.label} value={m.label}>{nomPiece(m)}</option>)}
                    </ErpiaSelect>
                  );
                })()}
                <Badge bg={cfg.bg} fg={cfg.fg}><BI icon={cfg.icon} /> {cfg.label}</Badge>
                {/* La date de la dernière version du CV (30.10 : « ajoute la date de
                    mise à jour du CV sur la ligne ») — servie par le scan. */}
                {pieceKind(p.label) === 'cv' && (p.cv_erp_date || p.cv_kdrive_date) && (() => {
                  const d = new Date(p.cv_erp_date || p.cv_kdrive_date);
                  if (isNaN(d)) return null;
                  return (
                    <span className="ats-mono" title="Dernière mise à jour du CV" style={{ fontSize: '11px', color: 'var(--erp-text-tertiary)', whiteSpace: 'nowrap' }}>
                      <BI icon="clock-history" /> {d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })} {d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  );
                })()}
                {/* Le serveur ne pose ce champ que sur un CV choisi par la
                    recruteuse — pas sur un CV deviné d'après son nom de fichier. */}
                {/* Le serveur confirme la désignation dans sa réponse : le badge
                    ne dépend plus du prochain scan. Le choix mémorisé localement
                    nomme en plus le document retenu. */}
                {cvObsolete(p) && (
                  <Badge bg="var(--erp-danger-soft)" fg="var(--erp-danger)"
                    title={[p.cv_erp_date ? `Dernière version ERP : ${p.cv_erp_date}` : null, p.cv_kdrive_date ? `Fichier kDrive : ${p.cv_kdrive_date}` : null].filter(Boolean).join(' · ') || 'La dernière version du CV n\'est pas dans le dossier kDrive.'}>
                    <BI icon="exclamation-octagon-fill" /> CV pas à jour sur kDrive
                  </Badge>
                )}
                {(p.cv_designe === true || (pieceKind(p.label) === 'cv' && cvChoisi[cvChoisiKey(f)]?.designe)) && (
                  <Badge bg="var(--erp-success-soft)" fg="var(--erp-success)">
                    <BI icon="patch-check" /> CV validé pour ce besoin
                    {cvChoisi[cvChoisiKey(f)]?.nom ? ` : ${cvChoisi[cvChoisiKey(f)].nom}` : ''}
                  </Badge>
                )}
                {/* Repli automatique du serveur, PAS une décision : le CV format
                    entreprise de la fiche est retenu faute de désignation. Badge
                    neutre, pour ne pas ressembler à un choix validé — et le
                    bouton « Choisir le CV » reste proposé. */}
                {(() => {
                  const perdue = designationPerdue(f, p);
                  if (!perdue) return null;
                  return (
                    <Badge bg="var(--erp-danger-soft)" fg="var(--erp-danger)"
                      title={`« ${perdue.nom || 'CV désigné'} » avait été confirmé${perdue.date ? ` le ${new Date(perdue.date).toLocaleDateString('fr-FR')}` : ''} comme CV de ce besoin, mais l'analyse ne le renvoie plus. Re-choisissez le CV, et signalez ce dossier si cela se reproduit.`}>
                      <BI icon="exclamation-octagon-fill" /> Désignation perdue — re-choisir le CV
                    </Badge>
                  );
                })()}
                {p.cv_designe !== true && p.cv_defaut === 'format_entreprise'
                  && !(pieceKind(p.label) === 'cv' && cvChoisi[cvChoisiKey(f)]?.designe)
                  && !designationPerdue(f, p) && (
                  <Badge bg="var(--chip-bg, #f3f4f6)" fg="var(--text-2)">
                    <BI icon="file-earmark-text" /> CV entreprise (par défaut)
                  </Badge>
                )}
                <div style={{ display: 'inline-flex', gap: '4px', flex: 'none' }}>
                  {p.carte_profil ? (() => {
                    // Ligne « Carte profil » : générée et déposée par le serveur
                    // (même fenêtre que le bouton de la ligne du candidat).
                    const enCours = !!carteBusy[f.path];
                    const cid = folderIds(f).candidate_id;
                    const voir = p.doc_id != null && cid != null ? avecCle(`${BASE}/candidates/${cid}/documents/${p.doc_id}/preview`) : null;
                    if (enCours) return <span className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="arrow-repeat" style={{ animation: 'ao-spin .7s linear infinite' }} /> En cours…</span>;
                    return p.status === 'present' ? <>
                      {voir && <a href={voir} target="_blank" rel="noopener noreferrer" title="Voir la carte profil" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="eye" /></a>}
                      {voir && <a href={`${voir}${voir.includes('?') ? '&' : '?'}download=1`} download title="Télécharger la carte profil" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="download" /></a>}
                      <button onClick={() => setCarteAo({ f, photo: false, regenerer: false })} title="Régénérer la carte profil de ce lot et remplacer celle du dossier" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="arrow-clockwise" /></button>
                    </> : (
                      <button onClick={() => setCarteAo({ f, photo: false, regenerer: false })} title="Générer la carte profil de ce candidat pour ce lot et la déposer dans son dossier" className="erp-btn erp-btn--primary erp-btn--sm"><BI icon="person-vcard" /> Générer</button>
                    );
                  })() : busy ? (
                    <span className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="arrow-repeat" style={{ animation: 'ao-spin .7s linear infinite' }} /> En cours…</span>
                  ) : (
                    p.status === 'present' ? <>
                      {/* Le geste de correction, en premier et en plein : recopier
                          la dernière version sur kDrive remplace l'ancien fichier. */}
                      {cvObsolete(p) && (
                        <button onClick={() => uploadPieceCopy(f, p)} title="Recopier la dernière version du CV dans le dossier kDrive — l'ancien fichier est remplacé" className="erp-btn erp-btn--primary erp-btn--sm">
                          <BI icon="cloud-upload" /> Mettre à jour sur kDrive
                        </button>
                      )}
                      <button onClick={() => openViewer(f, p)} title="Visualiser" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="eye" /></button>
                      {/* Même système que la vue besoin : UN bouton Télécharger,
                          survol → choix du format (Word natif, couleurs intactes /
                          PDF converti). Sans CV identifié : téléchargement simple. */}
                      {(() => {
                        const cvId = docIdDeCv(f, p);
                        const cid = folderIds(f).candidate_id;
                        if (cvId == null || cid == null) {
                          return <button onClick={() => downloadPiece(f, p)} title="Télécharger le fichier tel quel (Word natif, couleurs intactes)" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="download" /></button>;
                        }
                        // Route canonique candidate_documents : SANS format,
                        // elle sert les octets natifs (le .docx tel quel,
                        // couleurs du thème intactes) ; format=pdf convertit.
                        // La route piece/file du dossier, elle, convertit — le
                        // « Word » y téléchargeait un PDF.
                        const dlBase = avecCle(`${BASE}/candidates/${cid}/documents/${cvId}/preview?download=1`);
                        return (
                          <span className="ats-dl-split">
                            <a href={dlBase} download title="Télécharger — survolez pour choisir le format" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="download" /></a>
                            <span className="ats-dl-menu">
                              <a href={dlBase} download><BI icon="file-earmark-word" /> Word (.docx)</a>
                              <a href={`${dlBase}&format=pdf`} download><BI icon="file-earmark-pdf" /> PDF</a>
                            </span>
                          </span>
                        );
                      })()}
                      {isGeneratable(p)
                        ? <button onClick={() => generatePiece(f, p)} title="Régénérer" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="arrow-clockwise" /></button>
                        : <button onClick={() => askPieceFile(f, p)} title="Remplacer par un nouveau fichier" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="cloud-arrow-up" /></button>}
                      {cvAChoisir && (<>
                        <button onClick={() => openDocPicker(f, p, 'cv')} title="Ce CV a été reconnu d'après son nom de fichier. Désignez celui qui fait référence pour ce besoin." className="erp-btn erp-btn--secondary erp-btn--sm">
                          <BI icon="patch-question" /> Choisir le CV
                        </button>
                        <button onClick={() => askCvFile(f, p)} title="Envoyer un CV depuis votre poste : il rejoint les documents du candidat et devient le CV de ce besoin" className="erp-btn erp-btn--secondary erp-btn--sm">
                          <BI icon="cloud-arrow-up" /> Téléverser un CV
                        </button>
                      </>)}
                      <button onClick={() => deletePiece(f, p)} title="Supprimer" className="erp-btn erp-btn--danger erp-btn--sm"><BI icon="trash3" /></button>
                    </> : <>
                      {/* Proposé quel que soit l'état de la ligne : « à générer » et
                          « à copier » sont aussi des cas où le CV de référence
                          n'est pas encore choisi. */}
                      {cvAChoisir && (<>
                        <button onClick={() => openDocPicker(f, p, 'cv')} title="Désigner le CV qui fait référence pour ce besoin" className="erp-btn erp-btn--secondary erp-btn--sm">
                          <BI icon="patch-question" /> Choisir le CV
                        </button>
                        <button onClick={() => askCvFile(f, p)} title="Envoyer un CV depuis votre poste : il rejoint les documents du candidat et devient le CV de ce besoin" className="erp-btn erp-btn--primary erp-btn--sm">
                          <BI icon="cloud-arrow-up" /> Téléverser un CV
                        </button>
                      </>)}
                      {pieceHref(p) && <button onClick={() => openViewer(f, p)} title="Voir le document tel qu'il est dans l'ERP — il n'est pas encore dans le dossier kDrive" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="eye" /></button>}
                      {/* Le document EXISTE et n'attend que la copie : « Copier »
                          est l'action principale, même sur une pièce générable —
                          « Générer » reste offert pour re-produire depuis le
                          modèle au lieu de copier l'existant. */}
                      {p.status === 'a_uploader' ? (<>
                        <button onClick={() => uploadPieceCopy(f, p)} title="Le document existe dans l'ERP (l'œil l'affiche) mais pas dans le dossier kDrive — le copier maintenant" className="erp-btn erp-btn--primary erp-btn--sm"><BI icon="cloud-upload" /> Copier sur kDrive</button>
                        {isGeneratable(p) && (
                          <button onClick={() => generatePiece(f, p)} title="Re-produire le document depuis le modèle plutôt que copier l'existant" className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="arrow-clockwise" /> Générer</button>
                        )}
                        <button onClick={() => deletePiece(f, p)} title="Détacher le document de cette pièce — utile si la copie échoue en boucle ; la pièce repassera « à fournir »" className="erp-btn erp-btn--danger erp-btn--sm"><BI icon="trash3" /></button>
                      </>) : isGeneratable(p) ? (
                        <button onClick={() => generatePiece(f, p)} title="Générer" className="erp-btn erp-btn--primary erp-btn--sm"><BI icon="file-earmark-text" /> Générer</button>
                      ) : (<>
                        {folderIds(f).candidate_id != null && (
                          <button onClick={() => openDocPicker(f, p)} title="Copier un document déjà présent sur la fiche candidat vers le dossier kDrive" className="erp-btn erp-btn--secondary erp-btn--sm">
                            <BI icon="person-badge" /> Depuis la fiche
                          </button>
                        )}
                        <button onClick={() => askPieceFile(f, p)} title="Choisir un fichier sur votre poste" className="erp-btn erp-btn--primary erp-btn--sm"><BI icon="paperclip" /> Fournir</button>
                      </>)}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Dossier>
  );

  return (
    <div style={{ margin: '0 auto', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <style>{`@keyframes ao-spin{to{transform:rotate(360deg)}}@keyframes ao-recalc-bar{0%{left:-40%}100%{left:100%}}@keyframes ao-recalc-pulse{0%,100%{opacity:.55}50%{opacity:.4}}
        .ao-del-btn:hover{background:var(--danger-bg)!important;color:var(--danger-fg)!important;border-color:var(--danger-bd)!important;}`}</style>
      {confirmElement}

      {/* Nom du fichier du CV à générer — facultatif : inchangé = le nom
          actuel (mémorisé pour le couple) est conservé, on n'envoie rien. */}
      {natDemande && (
        <div onClick={() => !natDemande.busy && setNatDemande(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(420px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px' }}>
            <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Nationalité{natDemande.nom ? ` de ${natDemande.nom}` : ''}</div>
            <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Lue sur la pièce que vous venez de déposer. Elle est enregistrée sur la fiche et reportée dans le récapitulatif client.
            </div>
            <input value={natDemande.valeur} autoFocus list="ao-nationalites"
              onChange={e => setNatDemande(d => ({ ...d, valeur: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') enregistrerNationalite(); if (e.key === 'Escape') setNatDemande(null); }}
              placeholder="Suisse, Française, Italienne…" className="erp-field" style={{ width: '100%', marginTop: '14px' }} />
            <datalist id="ao-nationalites">
              {['Suisse', 'Française', 'Italienne', 'Portugaise', 'Espagnole', 'Allemande', 'Belge', 'Marocaine', 'Tunisienne', 'Algérienne'].map(n => <option key={n} value={n} />)}
            </datalist>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setNatDemande(null)} disabled={natDemande.busy} className="erp-btn erp-btn--secondary">Plus tard</button>
              <button onClick={enregistrerNationalite} disabled={natDemande.busy || !String(natDemande.valeur || '').trim()} className="erp-btn erp-btn--primary">
                <BI icon={natDemande.busy ? 'arrow-repeat' : 'check2'} /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      {genCvNom && (
        <div onClick={() => setGenCvNom(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(460px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px' }}>
            <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Générer « {nomPiece(genCvNom.p)} »</div>
            <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Vous pouvez choisir le nom du fichier généré — ou laisser tel quel.
            </div>
            <div style={{ marginTop: '14px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '5px' }}>Nom du fichier (facultatif)</div>
            <input value={genCvNom.nom} autoFocus onChange={e => setGenCvNom(g => ({ ...g, nom: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') { const { f, p, nom, nomInitial } = genCvNom; setGenCvNom(null); const n = nom.trim(); generatePieceDirect(f, p, n && n !== nomInitial ? n : null); } if (e.key === 'Escape') setGenCvNom(null); }}
              placeholder="Nom par défaut du serveur"
              className="erp-field" style={{ width: '100%' }} />
            <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
              Sans extension — un préfixe « CV_ » est ajouté automatiquement s'il manque. Laissez le nom affiché pour le conserver.
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setGenCvNom(null)} className="erp-btn erp-btn--secondary">Annuler</button>
              <button onClick={() => { const { f, p, nom, nomInitial } = genCvNom; setGenCvNom(null); const n = nom.trim(); generatePieceDirect(f, p, n && n !== nomInitial ? n : null); }}
                className="erp-btn erp-btn--primary">
                <BI icon="file-earmark-text" /> Générer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carte profil à déposer dans le dossier du candidat. */}
      {carteAo && (
        <div onClick={() => setCarteAo(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div role="dialog" aria-modal="true" aria-label="Carte profil" onClick={e => e.stopPropagation()} style={{ width: 'min(460px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px' }}>
            <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Carte profil — {nomDuDossier(carteAo.f)}</div>
            <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              La carte de ce lot est produite en PDF et déposée dans le dossier du candidat ; une carte plus ancienne est remplacée. L'argumentaire est rédigé par Leman s'il manque ou si le score a changé. Le CV doit être validé pour ce besoin.
            </div>
            <label style={{ marginTop: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12.5px', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={carteAo.photo} onChange={e => setCarteAo(c => ({ ...c, photo: e.target.checked }))} style={{ marginTop: '2px' }} />
              <span>Avec la photo</span>
            </label>
            <label style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12.5px', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={carteAo.regenerer} onChange={e => setCarteAo(c => ({ ...c, regenerer: e.target.checked }))} style={{ marginTop: '2px' }} />
              <span>Réécrire l'argumentaire même s'il est à jour</span>
            </label>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setCarteAo(null)} className="erp-btn erp-btn--secondary">Annuler</button>
              <button autoFocus onClick={() => { const c = carteAo; setCarteAo(null); deposerCarte(c); }} className="erp-btn erp-btn--primary">
                <BI icon="person-vcard" /> Générer et déposer
              </button>
            </div>
          </div>
        </div>
      )}

      {pickDoc && (
        <div onClick={() => setPickDoc(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(520px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px 0' }}>
              <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>
                {pickDoc.mode === 'cv' ? 'Choisir le CV pour ce besoin' : 'Copier depuis la fiche candidat'}
              </div>
              <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                {pickDoc.mode === 'cv'
                  ? 'Le CV choisi devient celui qui fait référence pour ce besoin — dans le dossier comme sur la fiche du candidat.'
                  : <>Le document choisi est copié dans le dossier kDrive et vaut « {pickDoc.p?.label} ». La fiche candidat n'est pas modifiée.</>}
              </div>
            </div>
            <div style={{ padding: '14px 20px 4px', maxHeight: '46vh', overflowY: 'auto' }}>
              {pickDoc.docs == null ? (
                <div style={{ fontSize: '12.5px', color: 'var(--text-2)' }}><BI icon="arrow-repeat" style={{ marginRight: '6px', animation: 'ao-spin .9s linear infinite' }} />Lecture des documents de la fiche…</div>
              ) : (() => {
                const cv = pickDoc.mode === 'cv';
                const liste = cv && !cvTousDocs ? pickDoc.docs.filter(estCvDoc) : pickDoc.docs;
                // Rien au format entreprise parmi les CV : la génération est la
                // suite naturelle, autant la proposer ici.
                const sansEntreprise = cv && !pickDoc.docs.filter(estCvDoc).some(estCvEntreprise);
                return (<>
                  {cv && sansEntreprise && isGeneratable(pickDoc.p) && (
                    <button onClick={() => { const { f, p } = pickDoc; setPickDoc(null); generatePiece(f, p); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '11px', marginBottom: '9px', border: '1px solid var(--input-border)', borderRadius: '10px', background: 'var(--accent-soft, var(--ghost-bg))', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <BI icon="file-earmark-text" style={{ flex: 'none', color: 'var(--accent)' }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Générer un CV format entreprise</span>
                        <span style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: 'var(--text-2)' }}>Aucun CV à ce format sur la fiche. Le CV généré est désigné pour ce besoin.</span>
                      </span>
                    </button>
                  )}
                  {liste.length === 0 ? (
                    <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {cv && !cvTousDocs && pickDoc.docs.length > 0 ? (<>
                        Aucun document de cette fiche n'est identifié comme CV.{' '}
                        <button onClick={() => setCvTousDocs(true)} style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                          Afficher tous les documents
                        </button>
                      </>) : 'Aucun document disponible sur la fiche de ce candidat. Utilisez « Fournir » pour choisir un fichier sur votre poste.'}
                    </div>
                  ) : liste.map((d, i) => (
                <button key={d.id ?? i} onClick={() => (cv ? designerCv(d) : copyDocToKdrive(d))}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '10px 11px', marginBottom: '7px', border: '1px solid var(--input-border)', borderRadius: '10px', background: 'var(--ghost-bg)', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <BI icon="file-earmark-text" style={{ flex: 'none', color: 'var(--text-3)' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label || d.filename || `Document ${i + 1}`}</span>
                    {(d.filename || d.created_at) && (
                      <span style={{ display: 'block', marginTop: '2px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.filename || ''}{d.created_at ? `${d.filename ? ' · ' : ''}${new Date(d.created_at).toLocaleDateString('fr-FR')}` : ''}
                      </span>
                    )}
                  </span>
                  {cv && d.id != null && folderIds(pickDoc.f).candidate_id != null && (
                    <span role="button" tabIndex={0} title="Aperçu du document"
                      onClick={e => { e.stopPropagation(); setCanonView(d._cdId == null && d._pdId != null
                        ? { previewBase: `${BASE}/position-documents/${d._pdId}/preview`, label: d.label || d.filename || 'Document', estCv: true }
                        : { candidateId: folderIds(pickDoc.f).candidate_id, docId: d._cdId ?? d.id, label: d.label || d.filename || 'Document', estCv: true }); }}
                      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.click(); }}
                      style={{ flex: 'none', color: 'var(--text-3)', padding: '0 4px' }}><BI icon="eye" /></span>
                  )}
                  <span style={{ flex: 'none', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)' }}>{cv ? 'Désigner' : 'Copier'}</span>
                </button>
                  ))}
                </>);
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', padding: '14px 20px 18px' }}>
              <button onClick={() => setPickDoc(null)} style={btnGhost}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {copyCand && (() => {
        const opts = lotOptions().filter(o => o.path !== copyCand.fromPath);
        const label = (copyCand.f.path || '').split('/').pop();
        const pieces = (copyCand.f.pieces || []).filter(p => p.status === 'present').length;
        return (
          <div onClick={() => !copying && setCopyCand(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 'min(520px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ padding: '18px 20px 0' }}>
                <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Présenter sur un autre lot</div>
                <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                  « {label} » sera positionné sur un second lot de cet AO{pieces > 0 ? `, en reprenant ses ${pieces} document(s) déjà fournis` : ''}. Le dossier d'origine n'est pas modifié.
                </div>
              </div>
              <div style={{ padding: '14px 20px 4px' }}>
                <ErpiaSelect value={copyCand.toBesoinId} onChange={e => setCopyCand(t => ({ ...t, toBesoinId: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', height: '42px', border: '1px solid var(--input-border)', borderRadius: '9px', background: 'var(--input-bg)', padding: '0 10px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text)' }}>
                  {opts.map(o => <option key={o.besoin_id} value={o.besoin_id}>{o.label}</option>)}
                </ErpiaSelect>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '11px', fontSize: '12.5px', color: 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={copyCand.withDocs} onChange={e => setCopyCand(t => ({ ...t, withDocs: e.target.checked }))} style={{ marginTop: '2px' }} />
                  <span>Copier aussi les documents déjà fournis (CNI, permis, fichiers libres…) dans le dossier du nouveau lot. Le CV entreprise et la matrice, liés au poste, resteront à regénérer pour le lot cible.</span>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', padding: '14px 20px 18px' }}>
                <button onClick={() => setCopyCand(null)} disabled={copying} style={btnGhost}>Annuler</button>
                <button onClick={confirmCopyCandidate} disabled={copying || !copyCand.toBesoinId}
                  style={{ ...btnPrimary, opacity: (copying || !copyCand.toBesoinId) ? 0.6 : 1 }}>
                  <BI icon={copying ? 'arrow-repeat' : 'files'} style={copying ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {copying ? 'Copie…' : 'Présenter'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {renameLot && (
        <div onClick={() => !renamingLot && setRenameLot(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(460px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px 0' }}>
              <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>{renameLot.kdriveOnly ? 'Renommer le dossier kDrive' : 'Renommer le lot'}</div>
              <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)' }}>{renameLot.kdriveOnly ? 'Ce dossier n\'est rattaché à aucun besoin ERP : il sera renommé directement sur kDrive, puis le dossier AO sera rescanné.' : 'Le titre du besoin ET le dossier kDrive associé seront renommés.'}</div>
            </div>
            <div style={{ padding: '14px 20px 4px' }}>
              <input autoFocus value={renameLot.value} onChange={e => setRenameLot(t => ({ ...t, value: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') confirmRenameLot(); if (e.key === 'Escape' && !renamingLot) setRenameLot(null); }}
                placeholder="Nouveau titre du lot"
                style={{ width: '100%', boxSizing: 'border-box', height: '42px', border: '1px solid var(--input-border)', borderRadius: '9px', background: 'var(--input-bg)', padding: '0 12px', fontFamily: 'inherit', fontSize: '13.5px', color: 'var(--text)', outline: 'none' }} />
            </div>
            {/* Doublon : le serveur a trouvé plusieurs dossiers kDrive pour ce lot
                et attend qu'on désigne celui à renommer. */}
            {renameLot.matches?.length > 1 && (
              <div style={{ padding: '10px 20px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--erp-warning)', marginBottom: '7px' }}>
                  <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />{renameLot.matches.length} dossiers kDrive correspondent à ce lot. Choisissez celui à renommer :
                </div>
                {renameLot.matches.map((m, i) => {
                  const id = m.folder_id ?? m.id ?? m.path;
                  const picked = String(renameLot.pickFolderId ?? '') === String(id);
                  return (
                    <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', marginBottom: '6px', cursor: 'pointer', border: `1px solid ${picked ? 'var(--text-strong)' : 'var(--hair)'}`, borderRadius: '9px', background: picked ? 'var(--accent-soft-bg)' : 'transparent' }}>
                      <input type="radio" name="ao-rename-pick" checked={picked} onChange={() => setRenameLot(t => ({ ...t, pickFolderId: id }))} style={{ marginTop: '2px' }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '12px', fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)', wordBreak: 'break-word' }}>{m.name || (m.path || '').split('/').pop()}</span>
                        <span style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: 'var(--text-2)' }}>
                          {m.files_count != null ? `${m.files_count} fichier${m.files_count > 1 ? 's' : ''}` : (m.path || '')}
                          {m.url && <> · <a href={m.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-2)' }}>ouvrir dans kDrive</a></>}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', padding: '14px 20px 18px' }}>
              <button onClick={() => setRenameLot(null)} disabled={renamingLot} style={btnGhost}>Annuler</button>
              <button onClick={confirmRenameLot} disabled={renamingLot || !(renameLot.value || '').trim() || (renameLot.matches?.length > 1 && !renameLot.pickFolderId)}
                style={{ ...btnPrimary, opacity: (renamingLot || !(renameLot.value || '').trim() || (renameLot.matches?.length > 1 && !renameLot.pickFolderId)) ? 0.6 : 1 }}>
                <BI icon={renamingLot ? 'arrow-repeat' : 'pencil'} style={renamingLot ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {renamingLot ? 'Renommage…' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────── LISTE ───────── */}
      {view === 'list' && (
        <>
          <Header>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {aoList.length > 0 && (
                <div style={{ display: 'inline-flex', border: '1px solid var(--input-border)', borderRadius: '8px', overflow: 'hidden' }}>
                  {[['list', 'list-ul', 'Liste'], ['cards', 'grid', 'Cartes']].map(([id, ic, lbl]) => (
                    <button key={id} onClick={() => setListLayout(id)} title={lbl}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, background: listLayout === id ? 'var(--text-strong)' : 'var(--ghost-bg)', color: listLayout === id ? 'var(--surface)' : 'var(--text-2)' }}>
                      <BI icon={ic} /> {lbl}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={openNew} style={btnPrimary}><BI icon="plus-lg" /> Nouvel AO</button>
            </div>
          </Header>
          {loadingList ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-faint)' }}><BI icon="arrow-repeat" /> Chargement…</div>
          ) : aoList.length === 0 ? (
            <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
              <BI icon="folder" style={{ fontSize: '32px', color: 'var(--text-faint)' }} />
              <p style={{ margin: '12px 0 4px', fontSize: '14.5px', fontWeight: 600, color: 'var(--text)' }}>Aucun AO</p>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-2)' }}>Crée un AO pour analyser les dossiers candidats.</p>
            </div>
          ) : listLayout === 'cards' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
              {aoList.map(ao => {
                const st = AO_STATUT[ao.statut] || AO_STATUT.draft;
                return (
                  <div key={ao.id} onClick={() => goDetail(ao)} style={{ ...card, padding: '16px 18px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '12px', transition: 'box-shadow .15s, border-color .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 22px var(--hover-shadow, rgba(0,0,0,0.08))'; e.currentTarget.style.borderColor = 'var(--row-hover-bd, var(--input-border))'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', color: 'var(--text-3)', fontSize: '17px' }}><BI icon="folder2" /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ao.titre}</div>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ao.client || '—'}</div>
                      </div>
                      <Badge bg={st.bg} fg={st.fg}>{st.label}</Badge>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: 'var(--text-2)', borderTop: '1px solid var(--hair)', paddingTop: '11px' }}>
                      <span title="Besoins" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><BI icon="briefcase" /> <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text)' }}>{(ao.besoin_ids || []).length}</b></span>
                      <span title="Mis à jour" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--text-faint)' }}><BI icon="clock-history" /> {ao.updated_at ? new Date(ao.updated_at).toLocaleDateString('fr-FR') : '—'}</span>
                      <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                        {ao.kdrive_url && <a href={ao.kdrive_url} target="_blank" rel="noreferrer" title="Ouvrir dans kDrive" style={{ color: 'var(--text-2)', textDecoration: 'none', padding: '5px', borderRadius: '6px', fontSize: '13px' }}><BI icon="box-arrow-up-right" /></a>}
                        {isAdmin && (
                          <button onClick={() => deleteAo(ao)} title="Supprimer cet AO (double confirmation)"
                            style={{ border: 'none', background: 'none', color: 'var(--danger-fg)', cursor: 'pointer', padding: '5px', borderRadius: '6px', fontSize: '13px' }}>
                            <BI icon="trash3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ ...card, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)' }}>
                    {['AO', 'Client', 'Statut', 'Besoins', 'Mis à jour', 'kDrive', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: 'var(--text-3)', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aoList.map(ao => {
                    const st = AO_STATUT[ao.statut] || AO_STATUT.draft;
                    return (
                      <tr key={ao.id} onClick={() => goDetail(ao)} style={{ borderTop: '1px solid var(--hair)', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover-bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-strong)' }}>{ao.titre}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>{ao.client || '—'}</td>
                        <td style={{ padding: '12px 16px' }}><Badge bg={st.bg} fg={st.fg}>{st.label}</Badge></td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-2)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px' }}>{(ao.besoin_ids || []).length}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-faint)', fontSize: '12px' }}>{ao.updated_at ? new Date(ao.updated_at).toLocaleDateString('fr-FR') : '—'}</td>
                        <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
                          {ao.kdrive_url ? <a href={ao.kdrive_url} target="_blank" rel="noreferrer" style={{ color: 'var(--text)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 600, fontSize: '12.5px' }}><BI icon="box-arrow-up-right" /> Ouvrir</a> : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          {isAdmin && (
                            <button onClick={() => deleteAo(ao)} title="Supprimer cet AO (double confirmation)"
                              style={{ border: 'none', background: 'none', color: 'var(--danger-fg)', cursor: 'pointer', padding: '5px', borderRadius: '6px', fontSize: '13px' }}>
                              <BI icon="trash3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ───────── NOUVEAU / SCAN ───────── */}
      {view === 'new' && (
        <>
          <Header>
            <button onClick={() => setView('list')} style={btnGhost}><BI icon="chevron-left" /> Retour</button>
          </Header>
          <div style={{ ...card, padding: '18px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={label}>Titre de l'AO *</label>
                <input value={aoTitre} onChange={e => setAoTitre(e.target.value)} placeholder="ex: OCSIN - 2026-01" style={input} />
              </div>
              <div>
                <label style={label}>Client (optionnel)</label>
                <input value={client} onChange={e => setClient(e.target.value)} placeholder="ex: OCSIN" style={input} />
              </div>
            </div>

            <label style={label}>Besoins à inclure</label>
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <BI icon="search" style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', fontSize: '13px' }} />
              <input value={rechercheBesoin} onChange={e => setRechercheBesoin(e.target.value)}
                placeholder="Rechercher un besoin (titre, client, référence)…"
                style={{ ...input, paddingLeft: '32px' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '240px', overflowY: 'auto', marginBottom: '16px' }}>
              {(() => {
                // Filtre local, insensible à la casse et aux accents. Un besoin
                // déjà coché reste TOUJOURS affiché : le filtre aide à trouver,
                // il ne cache jamais ce qu'on a sélectionné.
                const norm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const q = norm(rechercheBesoin.trim());
                const visibles = !q ? besoins : besoins.filter(b => selected.has(b.id)
                  || norm(`${b.titre} ${b.client} ${b.reference || b.ref || ''}`).includes(q));
                if (!visibles.length) return (
                  <div style={{ padding: '14px', fontSize: '12.5px', color: 'var(--text-3)', textAlign: 'center' }}>
                    Aucun besoin ne correspond à « {rechercheBesoin.trim()} ».
                  </div>
                );
                return visibles.map(b => {
                const n = b._count?.candidats ?? b.nb_candidats ?? 0;
                const checked = selected.has(b.id);
                return (
                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 11px', borderRadius: '8px', cursor: 'pointer', background: checked ? 'var(--accent-soft-bg)' : 'transparent', border: '1px solid', borderColor: checked ? 'var(--row-hover-bd)' : 'var(--hair)' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(b.id)} style={{ width: '16px', height: '16px', accentColor: 'var(--accent)' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.titre}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{b.client}</div>
                    </div>
                    <span style={{ fontSize: '11.5px', color: 'var(--text-2)', flex: 'none', fontFamily: "'IBM Plex Mono',monospace" }}>{n}</span>
                  </label>
                );
              });
              })()}
            </div>

            <label style={label}>Template (optionnel — vide = défaut serveur)</label>
            <textarea value={template} onChange={e => setTemplate(e.target.value)} rows={4} placeholder="Structure du dossier… (laisser vide pour le template par défaut)"
              style={{ ...input, resize: 'vertical', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px', marginBottom: '16px' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '14px', borderTop: '1px solid var(--hair)' }}>
              <span style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>{selected.size} besoin(s) sélectionné(s)</span>
              <button onClick={() => scan()} disabled={scanning || !selected.size || !aoTitre.trim()}
                style={{ ...btnPrimary, opacity: (scanning || !selected.size || !aoTitre.trim()) ? 0.5 : 1, cursor: (scanning || !selected.size || !aoTitre.trim()) ? 'not-allowed' : 'pointer' }}>
                <BI icon={scanning ? 'arrow-repeat' : 'search'} /> {scanning ? 'Analyse…' : 'Analyser'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ───────── RAPPORT ───────── */}
      {view === 'report' && (
        <>
          <input ref={pieceFileInputRef} type="file" multiple style={{ display: 'none' }}
            onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; onPieceFiles(fs); }} />
          <input ref={cvFileInputRef} type="file" accept=".pdf,.doc,.docx,.odt,.rtf" style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0]; e.target.value = '';
              const t = cvTargetRef.current; cvTargetRef.current = null;
              if (file && t) televerserCv(t.f, t.p, file);
            }} />
          {showRecap && (
            <AoRecapSociete aoId={aoId} showNotification={showNotification} onClose={() => setShowRecap(false)}
              candidateIds={[...new Set((report?.folders || []).filter(f => f.bind === 'candidate')
                .map(f => folderIds(f).candidate_id).filter(v => v != null))]} />
          )}
          {showInfosContrat && (
            <AoInfosContrat aoId={aoId} personnes={personnesAContacter} showNotification={showNotification}
              onClose={() => setShowInfosContrat(false)}
              onContrats={isAdmin ? () => { setShowInfosContrat(false); setShowContracts(true); } : null} />
          )}
          {showContracts && isAdmin && <AoContractsModal aoId={aoId} showNotification={showNotification} onClose={() => setShowContracts(false)} onGenerated={rescan}
            onRecap={() => { setShowContracts(false); setShowRecap(true); }} />}
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <button onClick={() => { loadList(); if (onCloseAo) onCloseAo(); else setView('list'); }} title="Retour aux dossiers AO"
              style={{ ...btnGhost, width: '36px', height: '36px', padding: 0, justifyContent: 'center' }}><BI icon="arrow-left" /></button>
            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-3)' }}>Dossiers AO <span style={{ color: 'var(--text-faint)' }}>/</span> <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{aoMeta?.titre || `AO ${aoId}`}</span></span>
            <div style={{ flex: 1 }} />
            {/* Action primaire : l'assistant guidé. Le compteur suit la file en direct. */}
            {report && (() => {
              const { pend } = aoCursor();
              return (
                <button onClick={() => { setAssistOpen(o => !o); setReportTab('pieces'); }}
                  title="Parcourir les pièces manquantes une par une, l'administratif d'abord"
                  style={{ ...btnPrimary, opacity: pend.length ? 1 : 0.75 }}>
                  <BI icon={assistOpen ? 'chevron-up' : 'stars'} /> {pend.length ? `Compléter le dossier — ${pend.length} pièce${pend.length > 1 ? 's' : ''}` : 'Dossier complet'}
                </button>
              );
            })()}
            {/* Revue des CV : le CV qui partira, le texte à copier pour le
                challenger, valider ou remplacer — profil par profil. */}
            {report && (
              <button onClick={() => { setAssistOpen(false); setReportTab('pieces'); setRevueCv('revue'); }}
                title="Relire les CV un par un : copier le poste et le CV pour votre assistant, puis valider ou remplacer"
                style={btnGhost}>
                <BI icon="file-earmark-check" /> Revue des CV
              </button>
            )}
            {report && <CommercialCvAo aoId={aoId} showNotification={showNotification} />}
            {revueCv && (
              <RevueCvAo aoId={aoId} showNotification={showNotification} machine={revueCv === 'machine'}
                onClose={() => setRevueCv(false)}
                onChange={() => rescan()}
                // « Générer » de la bulle = le bouton « Générer » de la ligne
                // CV entreprise du dossier : même route, mêmes contrôles.
                onGenerer={async (besoinId, candidateId) => {
                  const f = (report?.folders || []).find(x => x.bind === 'candidate'
                    && Number(folderIds(x).besoin_id) === Number(besoinId) && Number(folderIds(x).candidate_id) === Number(candidateId));
                  const p = (f?.pieces || []).find(x => pieceKind(x.label) === 'cv');
                  if (!f || !p) { showNotification?.('Ce profil n’a pas encore de dossier dans l’AO : rescannez le dossier, puis réessayez.', 'error'); return; }
                  await generatePieceDirect(f, p);
                }}
                onFocus={(besoinId) => {
                  const lot = (report?.folders || []).find(f => Number(f.besoin_id) === Number(besoinId) && f.candidate_id == null && f.bind !== 'candidate');
                  setPieceQuery('');
                  if (lot?.path) setExpandedLot(lot.path);
                }} />
            )}
            {/* La MACHINE CV remplace « Générer N pièces » (30.10 : « dangereux,
                il génère tout et n'importe quoi sans qu'on voie ce qui se
                passe derrière ») : CV par CV, sous les yeux — généré pour le
                besoin, écarts lus, ce qu'il faut demander au candidat, puis
                Suivant. Les autres pièces se génèrent depuis leur ligne. */}
            {report && (
              <button onClick={() => { setAssistOpen(false); setReportTab('pieces'); setRevueCv('machine'); }}
                title="CV par CV, sur vos lots : le CV format entreprise généré pour le besoin, ses écarts au lot, ce qu'il faut demander au candidat — puis Suivant"
                style={btnGhost}>
                <BI icon="file-earmark-play" /> Machine CV
              </button>
            )}
            {aoBesoinIds.length > 0 && (
              <button onClick={rescan} disabled={scanning}
                title={`Relire kDrive et rafraîchir le rapport maintenant. À l'ouverture, l'analyse se relance déjà toute seule quand le rapport a plus de 10 minutes.${scannedAt ? '' : ' Âge du rapport inconnu (rapport antérieur au suivi des dates).'}`}
                style={{ ...(report ? btnGhost : btnPrimary), opacity: scanning ? 0.6 : 1, cursor: scanning ? 'wait' : 'pointer' }}>
                <BI icon={scanning ? 'arrow-repeat' : 'arrow-clockwise'} /> {scanning ? 'Analyse…' : (report ? 'Rescanner' : 'Lancer l\'analyse')}
              </button>
            )}
            {scannedAt && !scanning && (() => {
              const min = Math.max(0, Math.round((Date.now() - new Date(scannedAt).getTime()) / 60000));
              const txt = min < 1 ? "à l'instant" : min < 60 ? `il y a ${min} min` : min < 1440 ? `il y a ${Math.round(min / 60)} h` : `le ${new Date(scannedAt).toLocaleDateString('fr-FR')}`;
              return <span title="Date de la dernière lecture du dossier kDrive" style={{ fontSize: '11.5px', color: 'var(--text-faint)', alignSelf: 'center' }}>analysé {txt}</span>;
            })()}
            {aoId != null && view === 'report' && (
              <AoControleQualite aoId={aoId} nommage={aoNommage} lotsScan={lotsControle()} jobs={jobs}
                showNotification={showNotification} confirm={confirm}
                onJob={(j) => { const job = { ...(j.job || {}), id: j.job_id }; setJobs(prev => [...prev.filter(x => jobId(x) !== jobId(job)), job]); }}
                gestes={gestesControle} />
            )}
            <button onClick={() => setShowInfosContrat(true)} title="Parcourir tous les lots et tous les candidats pour réunir les informations nécessaires aux contrats"
              style={btnGhost}><BI icon="ui-checks" /> Compléter les infos contrat</button>
            {report && aoId != null && (
              <AoCartesPhotos aoId={aoId} showNotification={showNotification} onJob={ajouterJob} jobsFinis={jobsFinis}
                onFini={() => scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true })} />
            )}
            {isAdmin && aoMeta && (
              <button onClick={() => deleteAo({ id: aoId, titre: aoMeta?.titre || 'cet AO', besoin_ids: aoBesoinIds })}
                title="Supprimer cet AO — double confirmation, réservé à l'administration"
                style={{ ...btnGhost, color: 'var(--danger-fg)', borderColor: 'var(--danger-bd)' }}>
                <BI icon="trash3" /> Supprimer l'AO
              </button>
            )}
            {/* Génération des contrats : réservée à l'administration. Le serveur
                refuse déjà l'appel aux autres rôles ; laisser le bouton visible
                promettrait une action qui échouerait au clic. */}
            {isAdmin && (
              <button onClick={() => setShowContracts(true)} title="Générer les contrats des candidats placés (template DOCX + signature électronique)"
                style={btnGhost}><BI icon="file-earmark-text" /> Générer les contrats</button>
            )}
            {kdriveUrl && <a href={kdriveUrl} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: 'none' }}><BI icon="box-arrow-up-right" /> Dossier kDrive</a>}
          </div>

          {/* La grille par défaut du dossier, lue dans son CCCC : elle
              s'applique aux postes sans grille propre. Rejouer les scores
              recalcule le matching sur les évaluations déjà faites. */}
          {aoId && (
            <GrilleAo aoId={aoId} showNotification={showNotification}
              onRejoue={() => { setMatchingStatus('computing'); loadMatching(aoId); setTimeout(() => loadMatching(aoId), 5000); setTimeout(() => loadMatching(aoId), 15000); }} />
          )}

          {aoMeta && (() => {
            const lots = aoBesoinIds.length || (report?.folders || []).filter(f => f.bind !== 'candidate').length;
            const profils = (report?.folders || []).filter(f => f.bind === 'candidate').length;
            const numStyle = (c) => ({ fontFamily: "'Archivo',sans-serif", fontSize: '23px', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: c || 'var(--text-strong)' });
            const kpiLbl = { marginTop: '4px', fontSize: '9.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' };
            const sum = report?.summary;
            const kpis = [
              { k: 'Lots', v: lots, c: null }, { k: 'Profils', v: profils, c: null },
              ...(sum ? [
                { sep: true },
                { k: 'À créer', v: sum.folders_to_create ?? 0, c: null },
                { k: 'À uploader', v: sum.pieces_to_upload ?? 0, c: null },
                { k: 'À générer', v: sum.pieces_to_generate ?? 0, c: null },
                { k: 'Manquantes', v: sum.pieces_missing ?? 0, c: (sum.pieces_missing ?? 0) > 0 ? 'var(--erp-warning)' : null },
                { k: 'Suspects', v: sum.suspicious ?? 0, c: (sum.suspicious ?? 0) > 0 ? 'var(--danger-fg)' : null },
              ] : []),
              // Validation des lots : l'information la plus attendue en fin de
              // dossier, elle n'a pas à se mériter un clic.
              ...(livr?.lots_total > 0 ? [
                { sep: true },
                { k: 'Lots validés', v: `${livr.lots_valides}/${livr.lots_total}`, c: livr.lots_valides === livr.lots_total ? '#15803d' : null },
              ] : []),
            ];
            const rapportAt = aoSync?.lastSyncedAt ? new Date(aoSync.lastSyncedAt) : null;
            return (
              <Dossier level="ao" style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '16px', padding: '18px 22px', flexWrap: 'wrap' }}>
                <Monogram text={aoMeta.client || aoMeta.titre} size={48} />
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-strong)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{aoMeta.titre || 'AO'}</span>
                    {(() => {
                      const st = AO_STATUT[aoSync?.statut] || AO_STATUT.draft;
                      const tip = rapportAt && !Number.isNaN(rapportAt.getTime())
                        ? `Dernière synchro kDrive : ${rapportAt.toLocaleDateString('fr-FR')} ${rapportAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                        : 'Aucune synchro kDrive enregistrée';
                      return <span title={tip} style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: st.bg, color: st.fg, cursor: 'default' }}>{st.label}</span>;
                    })()}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
                    {aoMeta.client || '—'}
                    {rapportAt && !Number.isNaN(rapportAt.getTime()) && <span style={{ color: 'var(--text-faint)' }}> · Rapport du {rapportAt.toLocaleDateString('fr-FR')} à {rapportAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: '22px', flex: 'none', flexWrap: 'wrap' }}>
                  {kpis.map((s, i) => s.sep
                    ? <div key={`sep${i}`} style={{ width: '1px', background: 'var(--input-border)' }} />
                    : (
                      <div key={s.k} style={{ textAlign: 'right' }}>
                        <div style={numStyle(s.c)}>{s.v}</div>
                        <div style={kpiLbl}>{s.k}</div>
                      </div>
                    ))}
                </div>
              </Dossier>
            );
          })()}

          {/* Traitements en tâche de fond : on ne bloque pas l'écran, on informe. */}
          {jobs.map((jb) => {
            const st = jb.statut || jb.status;
            const total = jb.total ?? null;
            const done = jb.done ?? 0;
            const pct = jb.progress != null ? Math.max(0, Math.min(100, Number(jb.progress))) : (total ? Math.round((done / total) * 100) : null);
            const ko = jb.ko_count ?? 0;
            return (
              <div key={jobId(jb)} style={{ marginBottom: '14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 15px', flexWrap: 'wrap' }}>
                  <BI icon="arrow-repeat" style={{ color: 'var(--brand)', animation: 'ao-spin .9s linear infinite', flex: 'none' }} />
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)', flex: 'none' }}>
                    {(() => {
                      if (estJobControle(jb)) return st === 'queued' ? 'Contrôle qualité en attente' : 'Contrôle qualité en cours';
                      const typ = jb.type || jb.kind;
                      if (typ === 'ao_cartes_profil' || typ === 'ao_cartes_profil_relecture') {
                        const quoi = typ === 'ao_cartes_profil' ? 'Cartes profil' : 'Relecture des cartes';
                        return `${quoi} ${st === 'queued' ? 'en attente' : 'en cours'}${total ? ` (${total} carte${total > 1 ? 's' : ''})` : ''}`;
                      }
                      if (jb._titre) return jb._titre;
                      return `${st === 'queued' ? 'Génération en attente' : 'Génération en cours'}${total ? ` (${total} pièce${total > 1 ? 's' : ''})` : ''}`;
                    })()}
                  </span>
                  <span style={{ flex: 1, minWidth: '140px', fontSize: '12px', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {jb.label || 'Préparation…'}
                  </span>
                  <span style={{ flex: 'none', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-2)' }}>
                    {total ? `${done}/${total}` : `${done} traitée${done > 1 ? 's' : ''}`}{ko ? ` · ${ko} en échec` : ''}
                  </span>
                  <button onClick={() => cancelJob(jobId(jb))} title="Arrêter le traitement — la pièce en cours sera terminée avant l'arrêt"
                    className="erp-btn erp-btn--danger erp-btn--sm" style={{ flex: 'none' }}>
                    <BI icon="x-circle" /> Arrêter
                  </button>
                </div>
                <div style={{ height: '4px', background: 'var(--hair)' }}>
                  <div style={{ height: '100%', width: pct != null ? `${pct}%` : '30%', background: 'var(--brand)', transition: 'width .4s', ...(pct == null ? { animation: 'ao-recalc-pulse 1.2s ease-in-out infinite' } : {}) }} />
                </div>
              </div>
            );
          })}

          {/* Assistant guidé — entre l'en-tête et les onglets, donc visible partout */}
          {assistOpen && report && (() => {
            const cursor = aoCursor();
            const busy = cursor.cur?.p ? !!pieceBusy[pieceKey(cursor.cur.f, cursor.cur.p)] : false;
            return (
              <AoAssistant cursor={cursor} busy={busy} aoId={aoId} onToast={showNotification}
                onIdx={assistMove} onUpload={assistUpload} onGenerate={assistGenerate} onAsk={assistAsk}
                onClose={() => setAssistOpen(false)} />
            );
          })()}

          {/* Onglets Pièces / Matching */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--hair)' }}>
            {[['pieces', 'Pièces', 'folder2'], ['matching', 'Matching', 'diagram-3'],
              ['livraison', 'Validation & livraison', 'patch-check'],
              // Onglet société : servi au seul administrateur — un recruteur ne
              // reçoit ni l'onglet ni les données.
              ...(socOn ? [['societe', 'Administratif société', 'building-check']] : [])].map(([id, lbl, ic]) => (
              <button key={id} onClick={() => { setReportTab(id); if (id === 'matching' && matching == null) loadMatching(aoId); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, color: reportTab === id ? 'var(--text-strong)' : 'var(--text-3)', borderBottom: reportTab === id ? '2px solid var(--brand)' : '2px solid transparent', marginBottom: '-1px' }}>
                <BI icon={ic} /> {lbl}
                {id === 'livraison' && livr?.lots_total > 0 && (
                  <span title={`${livr.lots_valides} lot(s) validé(s) sur ${livr.lots_total}`}
                    style={{ fontSize: '10.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: livr.lots_valides === livr.lots_total ? 'var(--ok-bg, #f0fdf4)' : 'var(--ghost-bg)', color: livr.lots_valides === livr.lots_total ? '#15803d' : 'var(--text-2)' }}>
                    {livr.lots_valides}/{livr.lots_total}
                  </span>
                )}
                {id === 'societe' && societeAlerts(socDocs) > 0 && (
                  <span title={`${societeAlerts(socDocs)} pièce(s) obligatoire(s) non conforme(s)`}
                    style={{ fontSize: '10.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>{societeAlerts(socDocs)}</span>
                )}
              </button>
            ))}
          </div>

          {reportTab === 'livraison' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button onClick={() => setShowRecap(true)} title="Compléter le classeur Excel du client avec les candidats du dossier"
                  style={btnGhost}><BI icon="file-earmark-spreadsheet" /> Remplir le récapitulatif client</button>
              </div>
              <AoLivraison aoId={aoId} lotsScan={orbeLots()} isAdmin={isAdmin} showNotification={showNotification}
                onJob={ajouterJob} />
            </>
          )}

          {reportTab === 'societe' && socOn && (
            <AoSociete aoId={aoId} aoMeta={aoMeta} report={report} showNotification={showNotification}
              docs={socDocs} onPersist={persistSocDocs} onDocsFromServer={setSocDocs} saving={socSaving} storeUnavailable={socStoreKo} />
          )}

          {reportTab === 'matching' && (
            <MatchingPanel data={matching} loading={matchingLoading} unavailable={matchingUnavailable}
              onOpenCv={(candidateId, docId, label, besoinId, nom) => setCanonView({ candidateId, docId, label, besoinId, nom, estCv: true })}
              onUnlink={retirerDuBesoin}
              status={matchingStatus} progress={matchingProgress} duplicates={matchingDupes} ponderations={matchingPonderations}
              recomputing={recomputing} onRecompute={(force) => setMatchLancement({ force: !!force })} onRefetch={() => loadMatching(aoId)}
              recomputingBesoin={recomputingBesoin} onRecomputeBesoin={(bid) => setMatchLancement({ besoinId: bid })}
              expanded={expandedCand} onToggle={toggleCand} onOpenCandidat={onOpenCandidat} onOpenBesoin={onOpenBesoin} onReport={matchingReport} showNotification={showNotification} />
          )}
          {/* Pré-lancement du matching : réglages par défaut ou ajustés pour ce
              lancement — le recalcul ne part qu'après ce choix. */}
          {matchLancement && (
            <MatchingLancementModal
              titre={matchLancement.besoinId != null ? 'Matching de ce besoin' : matchLancement.force ? 'Re-notation intégrale' : 'Recalcul du matching'}
              sousTitre={matchLancement.besoinId != null ? 'Seul ce besoin sera recalculé.' : matchLancement.force ? 'Toutes les notes sont recalculées de zéro.' : 'Seul ce qui a changé est recalculé.'}
              confirmLabel={matchLancement.force ? 'Re-noter tout' : 'Lancer le recalcul'}
              besoinId={matchLancement.besoinId ?? null}
              onClose={() => setMatchLancement(null)}
              onLancer={(p) => matchLancement.besoinId != null ? recomputeBesoin(matchLancement.besoinId, p) : recomputeMatching(matchLancement.force, p)} />
          )}

          {reportTab === 'pieces' && report && (() => {
            const lots = orbeLots();
            return lots.length ? (
              <AoOrbe lots={lots} metric={orbeMetric} onMetric={setOrbeMetric}
                selected={orbeLot} onSelect={setOrbeLot} onCta={orbeCta} matchingLoading={matchingLoading} />
            ) : null;
          })()}

          {rattacherOuvert && (
            <AoRattacherBesoin aoId={aoId} besoinIdsDuDossier={aoBesoinIds} showNotification={showNotification}
              onClose={() => setRattacherOuvert(false)}
              onFait={(j) => { setRattacherOuvert(false); if (Array.isArray(j.besoin_ids)) setAoBesoinIds(j.besoin_ids); refreshDetail(); loadList(); }} />
          )}

          {reportTab === 'pieces' && report && (
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <BI icon="search" style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', fontSize: '13px' }} />
              <input value={pieceQuery} onChange={e => setPieceQuery(e.target.value)} placeholder="Rechercher un lot, un candidat, un document…"
                style={{ ...input, paddingLeft: '36px', paddingRight: pieceQuery ? '36px' : '12px' }} />
              {pieceQuery && (
                <button onClick={() => setPieceQuery('')} title="Effacer" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', padding: '5px', fontSize: '13px' }}><BI icon="x-lg" /></button>
              )}
            </div>
          )}

          {reportTab === 'pieces' && (treeLoading || tree || treeUnavailable) && (
            <div style={{ ...card, marginBottom: '16px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '12px 16px', borderBottom: '1px solid var(--hair)' }}>
                <BI icon="hdd-network" style={{ color: 'var(--text-3)', flex: 'none' }} />
                <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>Contenu kDrive · temps réel</span>
                {treeLoading && <span style={{ fontSize: '12px', color: 'var(--text-faint)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><BI icon="arrow-repeat" /> Lecture…</span>}
                <button onClick={() => loadTree(aoId)} disabled={treeLoading} title="Rafraîchir"
                  style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-2)', cursor: treeLoading ? 'wait' : 'pointer', padding: '5px 7px', borderRadius: '6px', fontSize: '13px' }}>
                  <BI icon="arrow-clockwise" />
                </button>
              </div>

              {treeUnavailable ? (
                <div style={{ padding: '16px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Lecture temps réel indisponible (endpoint <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px' }}>GET /ao/:id/tree</code> non actif côté serveur).
                  Utilise « Rafraîchir » pour relancer l'analyse et les liens kDrive ci-dessous.
                </div>
              ) : (tree && tree.length === 0 && !treeLoading) ? (
                <div style={{ padding: '16px', fontSize: '12.5px', color: 'var(--text-2)' }}>Aucun dossier kDrive lié pour le moment.</div>
              ) : (tree || []).map((f, i) => {
                const key = f.path || f.id || i;
                const open = openFolders.has(key);
                const files = (f.files || []).filter(file => isAdmin || !estNomContrat(file?.name));
                return (
                  <div key={key} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                    <div onClick={() => toggleFolder(key)} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 16px', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover-bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <BI icon={open ? 'chevron-down' : 'chevron-right'} style={{ color: 'var(--text-faint)', fontSize: '11px', flex: 'none' }} />
                      <BI icon={f.bind === 'candidate' ? 'person' : 'folder'} style={{ color: 'var(--text-3)', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)', wordBreak: 'break-word' }}>{f.path || f.name}</span>
                      <span style={{ flex: 'none', fontSize: '11.5px', color: 'var(--text-2)', fontFamily: "'IBM Plex Mono',monospace" }}>{files.length} fich.</span>
                      {(f.url || f.kdrive_url) && <a href={f.url || f.kdrive_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="Ouvrir dans kDrive" style={{ flex: 'none', color: 'var(--text-2)', textDecoration: 'none', fontSize: '13px' }}><BI icon="box-arrow-up-right" /></a>}
                    </div>
                    {open && (
                      <div style={{ padding: '2px 16px 10px 41px' }}>
                        {files.length === 0 ? (
                          <div style={{ fontSize: '12px', color: 'var(--text-faint)', padding: '6px 0' }}>Dossier vide</div>
                        ) : files.map((file, k) => (
                          <a key={k} href={file.url || file.kdrive_url || undefined} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 0', borderTop: k === 0 ? 'none' : '1px solid var(--hair)', textDecoration: 'none', color: 'var(--text)' }}>
                            <BI icon="file-earmark-text" style={{ color: 'var(--text-3)', flex: 'none', fontSize: '13px' }} />
                            <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</span>
                            {file.size != null && <span style={{ flex: 'none', fontSize: '11px', color: 'var(--text-faint)', fontFamily: "'IBM Plex Mono',monospace" }}>{fmtSize(file.size)}</span>}
                            {file.modified_at && <span style={{ flex: 'none', fontSize: '11px', color: 'var(--text-faint)' }}>{new Date(file.modified_at).toLocaleDateString('fr-FR')}</span>}
                            {(file.url || file.kdrive_url) && <BI icon="box-arrow-up-right" style={{ flex: 'none', color: 'var(--text-faint)', fontSize: '11px' }} />}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {reportTab === 'pieces' && (!report ? (
            <div style={{ ...card, padding: '40px 24px', textAlign: 'center' }}>
              <BI icon="clipboard-data" style={{ fontSize: '28px', color: 'var(--text-faint)' }} />
              <p style={{ margin: '12px 0 4px', fontWeight: 600, color: 'var(--text)' }}>Aucun rapport pour cet AO</p>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                {aoBesoinIds.length > 0
                  ? 'Lance l\'analyse pour comparer les dossiers candidats avec kDrive en temps réel.'
                  : 'Aucun besoin rattaché — recrée l\'AO via « Nouvel AO ».'}
              </p>
              {aoBesoinIds.length > 0 && (
                <button onClick={rescan} disabled={scanning} style={{ ...btnPrimary, margin: '0 auto', opacity: scanning ? 0.6 : 1, cursor: scanning ? 'wait' : 'pointer' }}>
                  <BI icon={scanning ? 'arrow-repeat' : 'search'} /> {scanning ? 'Analyse…' : 'Lancer l\'analyse'}
                </button>
              )}
            </div>
          ) : (<>
            {report.kdrive?.ambiguous && (
              <div style={{ ...card, padding: '14px 16px', marginBottom: '16px', borderColor: 'rgba(217,119,6,0.4)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-warning)', marginBottom: '8px' }}><BI icon="exclamation-triangle" /> Plusieurs dossiers kDrive portent ce titre — choisis le bon :</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {(report.kdrive.candidates || []).map((c, i) => (
                    <button key={c.id || i} onClick={() => { setKdriveFolderId(String(c.id)); scan(c.id); }} style={btnGhost}>{c.name || c.path || c.id}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input value={kdriveFolderId} onChange={e => setKdriveFolderId(e.target.value)} placeholder="ou colle un id / URL de dossier kDrive" style={{ ...input, flex: 1 }} />
                  <button onClick={() => scan()} style={btnGhost}>Re-scanner</button>
                </div>
              </div>
            )}

            {(() => {
              const q = pieceQuery.trim().toLowerCase();
              const baseFolders = report.folders || [];
              const depth = (p) => (p || '').split('/').filter(Boolean).length;
              const rootDepth = baseFolders.length ? Math.min(...baseFolders.map(f => depth(f.path))) : 1;
              // Un dossier candidat est exactement au niveau lot + 1. Ce qui est
              // plus profond, ce sont les sous-dossiers rangés par l'utilisateur
              // DANS le dossier d'un candidat (« Certificats de travail »…) :
              // ce ne sont ni des lots, ni des candidats.
              const extraFolders = (report.extra || []).map(e => {
                const path = (typeof e === 'string' ? e : e.path) || '';
                const d = depth(path);
                if (d > rootDepth + 2) return null;
                return { path, bind: d === rootDepth + 2 ? 'candidate' : undefined, exists: true, _extra: true };
              }).filter(Boolean);
              const all = [...baseFolders, ...extraFolders];
              const candByParent = new Map();
              all.filter(f => f.bind === 'candidate' && depth(f.path) <= rootDepth + 2).forEach(cf => {
                const parent = (cf.path || '').split('/').slice(0, -1).join('/');
                if (!candByParent.has(parent)) candByParent.set(parent, []);
                candByParent.get(parent).push(cf);
              });
              const lotMap = new Map();
              all.filter(f => f.bind !== 'candidate' && depth(f.path) === rootDepth + 1).forEach(lf => {
                lotMap.set(lf.path, { path: lf.path, label: (lf.path || '').split('/').pop(), folder: lf, cands: candByParent.get(lf.path) || [], nom_interne: nomInterneDuLot(lf) });
              });
              // On ne crée un lot implicite que pour un vrai parent de lot : sans
              // ce garde-fou, le dossier d'un candidat contenant des
              // sous-dossiers devenait un lot avec « N profils ».
              candByParent.forEach((cands, parent) => {
                if (!lotMap.has(parent) && depth(parent) === rootDepth + 1) {
                  lotMap.set(parent, { path: parent, label: (parent || '').split('/').pop(), folder: null, cands, nom_interne: null });
                }
              });
              // Fusion des doublons : sur kDrive le dossier peut s'appeler
              // « Lot 14 : X … Réf. R » alors que le besoin ERP s'appelle « X … Réf. R ».
              // Même référence (ou même nom une fois le préfixe « Lot n : » et les
              // accents neutralisés) = même lot → une seule tuile, candidats fusionnés.
              const refOf = (s) => ((s || '').match(/\d{2}-\d{2}-\d{4}-\d{3}/) || [])[0] || null;
              const normLot = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/^lot\s*\d+\s*[:.\-\u2013]?\s*/, '').replace(/[^a-z0-9]+/g, ' ').trim();
              const isExtraLot = (l) => !l.folder || !!l.folder._extra;
              const mergedLots = new Map();
              lotMap.forEach(lot => {
                const k = refOf(lot.label) || normLot(lot.label) || lot.path;
                const prev = mergedLots.get(k);
                if (!prev) { mergedLots.set(k, { ...lot, dupes: [lot] }); return; }
                const main = isExtraLot(prev) && !isExtraLot(lot) ? lot : prev;
                const other = main === prev ? lot : prev;
                const folder = main.folder ? { ...main.folder } : (other.folder ? { ...other.folder } : null);
                if (folder) {
                  // l'autre entrée prouve que le dossier kDrive existe ; on récupère son URL si besoin
                  if (folder.exists === false && other.folder?.exists !== false) folder.exists = true;
                  if (!folder.url && !folder.kdrive_url && (other.folder?.url || other.folder?.kdrive_url)) folder.url = other.folder.url || other.folder.kdrive_url;
                }
                const seen = new Set(main.cands.map(c => normLot((c.path || '').split('/').pop())));
                const cands = [...main.cands, ...other.cands.filter(c => !seen.has(normLot((c.path || '').split('/').pop())))];
                // On garde la trace des dossiers fusionnés : un vrai doublon sur
                // kDrive bloque le renommage côté serveur, il faut le montrer.
                const dupes = [...(prev.dupes || [prev]), ...(lot.dupes || [lot])]
                  .filter((d, i, a) => a.findIndex(x => x.path === d.path) === i);
                mergedLots.set(k, { ...main, folder, cands, dupes, nom_interne: main.nom_interne || other.nom_interne || null });
              });
              // Un même candidat peut avoir plusieurs dossiers kDrive selon la
              // façon dont son nom a été écrit (« FEMENIAS ludovic »,
              // « FEMENIAS_Ludovic », « Ludovic Femenias ») : on regroupe sur
              // le nom normalisé, mots triés, pour ne plus compter 3 profils.
              const persKey = (path) => (path || '').split('/').pop()
                .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .split(/[^a-z0-9]+/).filter(Boolean).sort().join(' ');
              const groupCands = (cands) => {
                const byPers = new Map();
                cands.forEach(cf => {
                  const k = persKey(cf.path) || cf.path;
                  const prev = byPers.get(k);
                  if (!prev) { byPers.set(k, { ...cf, _dupes: [] }); return; }
                  // Le dossier rattaché à une fiche ERP mène ; les autres sont
                  // signalés comme doublons, jamais supprimés en douce.
                  const prevBound = folderIds(prev).candidate_id != null;
                  const curBound = folderIds(cf).candidate_id != null;
                  if (!prevBound && curBound) {
                    byPers.set(k, { ...cf, _dupes: [...(prev._dupes || []), prev] });
                  } else {
                    prev._dupes = [...(prev._dupes || []), cf];
                  }
                });
                return [...byPers.values()];
              };
              let lots = [...mergedLots.values()].map(lot => {
                const visCands = groupCands(lot.cands).map(cf => [cf, folderMatches(cf)]).filter(([, p]) => p !== null);
                const hit = !q || lot.label.toLowerCase().includes(q) || (lot.path || '').toLowerCase().includes(q) || (lot.nom_interne || '').toLowerCase().includes(q);
                const extra = !!lot.folder?._extra || (lot.cands.length > 0 && lot.cands.every(c => c._extra));
                return { ...lot, visCands, extra, show: hit || visCands.length > 0 };
              }).filter(l => l.show);
              if (lots.length === 0) return (
                <div style={{ ...card, padding: '32px 24px', textAlign: 'center' }}>
                  <BI icon={q ? 'search' : 'folder2'} style={{ fontSize: '24px', color: 'var(--text-faint)' }} />
                  <p style={{ margin: '10px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>{q ? `Aucun lot ou candidat ne correspond à « ${pieceQuery} ».` : 'Aucun lot dans ce dossier.'}</p>
                  {!q && (
                    <button type="button" className="erp-btn erp-btn--secondary" style={{ marginTop: '12px' }} onClick={() => setRattacherOuvert(true)}>
                      <BI icon="plus-lg" /> Ajouter un besoin existant
                    </button>
                  )}
                </div>
              );
              const seg = (active) => ({ padding: '7px 14px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: active ? 600 : 500, background: active ? 'var(--accent-soft-bg)' : 'transparent', color: active ? 'var(--text-strong)' : 'var(--text-2)' });
              const lotBadge = (lot) => lot.folder?.exists === false
                ? <Badge bg="var(--chip-bg)" fg="var(--text-2)"><BI icon="plus-lg" /> À créer</Badge>
                : lot.folder?.exists === true ? <Badge bg="var(--erp-success-soft)" fg="var(--erp-success)"><BI icon="check2" /> Existe</Badge> : null;
              const lotHeader = (lot, isOpen, clickable) => (
                <div onClick={clickable ? () => {
                    setExpandedLot(isOpen ? null : lot.path);
                    // Le serveur a rafraîchi le rapport en tâche de fond après le
                    // dernier ajout/copie : à l'ouverture d'un lot, on ressert la
                    // version à jour — une seule relecture, pas de rescan.
                    if (!isOpen && rapportPerime.current) { rapportPerime.current = false; refreshDetail(); }
                  } : undefined} style={{ padding: '13px 15px', cursor: clickable ? 'pointer' : 'default', borderBottom: (!clickable || isOpen) ? '1px solid var(--hair)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <BI icon="folder" style={{ color: 'var(--text-3)', flex: 'none', marginTop: '2px' }} />
                    {(() => {
                      const bid = lotBesoinId(lot);
                      const enEdition = !!refLot && bid != null && String(refLot.besoinId) === String(bid);
                      return (<>
                        <span style={{ flex: 1, minWidth: 0 }} onClick={enEdition ? (e => e.stopPropagation()) : undefined}>
                          {/* Le NOM INTERNE du lot (la référence interne du besoin) en tête ;
                              le nom du dossier kDrive dessous — c'est lui que « Renommer » change.
                              Sans nom interne, la ligne le dit, et le crayon le donne d'ici. */}
                          {enEdition ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input autoFocus className="erp-field" maxLength={120} value={refLot.value} placeholder="Nom interne du lot (ex. archi01)"
                                onChange={e => setRefLot(r => ({ ...r, value: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); enregistrerNomInterne(bid, refLot.value); } if (e.key === 'Escape') setRefLot(null); }}
                                style={{ flex: 1, minWidth: 0, fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px' }} />
                              <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" title="Enregistrer" onClick={() => enregistrerNomInterne(bid, refLot.value)}><BI icon="check2" /></button>
                              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" title="Annuler" onClick={() => setRefLot(null)}><BI icon="x-lg" /></button>
                            </span>
                          ) : (<>
                            <span title={lot.nom_interne ? `${lot.nom_interne} — dossier kDrive : ${lot.label}` : lot.label} style={{ fontSize: '13px', fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: 'var(--text-strong)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.35 }}>{lot.nom_interne || lot.label}</span>
                            {lot.nom_interne
                              ? <span style={{ display: 'block', marginTop: '2px', fontSize: '11px', fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lot.label}</span>
                              : bid != null && <span style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: 'var(--text-faint)' }}>Nom interne : —</span>}
                          </>)}
                        </span>
                        {bid != null && !enEdition && (
                          <button type="button" onClick={e => { e.stopPropagation(); setRefLot({ besoinId: bid, value: lot.nom_interne || '' }); }}
                            title={lot.nom_interne ? 'Changer le nom interne du lot (la référence interne du besoin)' : 'Donner un nom interne au lot (la référence interne du besoin) — pour l’équipe, jamais publié'}
                            className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="pencil" /></button>
                        )}
                      </>);
                    })()}
                    {/* Lien direct vers le dossier kDrive du lot — quand une URL est
                        connue (résout « je n'arrive pas à voir le dossier ») */}
                    {(lot.folder?.url || lot.folder?.kdrive_url) && (
                      <a href={lot.folder.url || lot.folder.kdrive_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        title="Ouvrir le dossier du lot dans kDrive" style={{ flex: 'none', color: 'var(--text-2)', textDecoration: 'none', fontSize: '14px', marginTop: '1px' }}><BI icon="box-arrow-up-right" /></a>
                    )}
                    {clickable && <BI icon={isOpen ? 'chevron-up' : 'chevron-down'} style={{ color: 'var(--text-faint)', flex: 'none', marginTop: '2px' }} />}
                  </div>

                  {/* Validation du lot, à l'endroit où on la cherche : sur le lot.
                      Le nom du validateur vaut engagement, on l'affiche en clair. */}
                  {lotBesoinId(lot) != null && (() => {
                    const bid = lotBesoinId(lot);
                    const v = validationDuLot(bid);
                    const occupe = livrLot === String(bid);
                    return (
                      <div onClick={e => e.stopPropagation()} style={{ margin: '9px 0 0 26px', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                        {v ? (<>
                          <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#15803d', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <BI icon="patch-check-fill" /> Validé par {v.par || 'un collaborateur'}{v.le ? ` le ${new Date(v.le).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` : ''}
                          </span>
                          <button onClick={() => validerLotDeGrille(bid, true)} disabled={occupe}
                            title="Seul le validateur ou un administrateur peut retirer cette validation"
                            style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', textDecoration: 'underline' }}>Retirer</button>
                        </>) : (
                          <button onClick={() => validerLotDeGrille(bid, false)} disabled={occupe}
                            title="Valider ce lot en votre nom : documents complets et adéquation relue"
                            className="erp-btn erp-btn--secondary erp-btn--sm" style={{ opacity: occupe ? 0.6 : 1 }}>
                            <BI icon={occupe ? 'arrow-repeat' : 'check-lg'} style={occupe ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> Valider ce lot
                          </button>
                        )}
                        <button onClick={() => setReportTab('livraison')} title="Emplacements kDrive et copie vers le dossier final"
                          style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: '11.5px', fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}>Dossier final…</button>
                        {aoId != null && (
                          <AoCartesPhotos compact aoId={aoId} besoinId={bid} lotLabel={lot.nom_interne || lot.label}
                            showNotification={showNotification} onJob={ajouterJob} jobsFinis={jobsFinis}
                            onFini={() => scanExisting(aoId, aoBesoinIds, aoMeta?.titre, aoMeta?.client, { silent: true })} />
                        )}
                      </div>
                    );
                  })()}
                  {/* Incohérence courante : le back rapporte « À créer » alors que le
                      dossier contient déjà des candidats détectés sur kDrive (nom du
                      dossier non reconnu, souvent titre trop long). On prévient pour
                      éviter un doublon. */}
                  {lot.folder?.exists === false && !lot.extra && (() => {
                    // Seuls les candidats DÉTECTÉS sur kDrive comptent : ceux que
                    // l'ERP attend (positionnés, dossier à créer) ne prouvent rien.
                    const detectes = lot.visCands.filter(([cf]) => cf?.exists === true || cf?._extra);
                    return detectes.length > 0 && (
                      <div style={{ margin: '9px 0 0 26px', padding: '7px 10px', borderRadius: '8px', background: 'var(--erp-warning-soft)', border: '1px solid var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '11.5px', lineHeight: 1.45 }}>
                        <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />Ce dossier contient déjà {detectes.length} candidat{detectes.length > 1 ? 's' : ''} sur kDrive — il existe donc, mais son nom n'a pas été reconnu. Utilisez « Rescanner » (en haut) plutôt que « Créer » pour éviter un doublon.
                      </div>
                    );
                  })()}
                  {/* Doublon RÉEL sur kDrive : deux dossiers distincts (folder_id
                      différents) désignent le même lot. Deux entrées peuvent
                      pointer vers le même dossier physique (titre du besoin d'un
                      côté, nom réel « Lot n … » de l'autre) — ce n'est alors pas
                      un doublon et on n'alarme pas. */}
                  {(lot.dupes?.length > 1) && (() => {
                    const ids = lot.dupes.map(d => d.folder?.folder_id ?? d.folder?.id).filter(v => v != null);
                    return new Set(ids.map(String)).size > 1;
                  })() && (
                    <div style={{ margin: '9px 0 0 26px', padding: '8px 10px', borderRadius: '8px', background: 'var(--erp-warning-soft)', border: '1px solid var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '11.5px', lineHeight: 1.5 }}>
                      <BI icon="files" style={{ marginRight: '6px' }} />{lot.dupes.length} dossiers kDrive correspondent à ce lot — c'est un doublon. Tant qu'il existe, le renommage automatique du dossier est refusé. Ouvrez-les, gardez celui qui contient les candidats, supprimez l'autre dans kDrive, puis « Rescanner ».
                      <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {lot.dupes.map((d, i) => {
                          const href = d.folder?.url || d.folder?.kdrive_url;
                          const name = (d.path || '').split('/').pop();
                          const n = (d.cands || []).length;
                          const txt = `${name} — ${n} candidat${n > 1 ? 's' : ''}`;
                          return href
                            ? <a key={i} href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--erp-warning)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', wordBreak: 'break-word' }}><BI icon="box-arrow-up-right" style={{ marginRight: '5px' }} />{txt}</a>
                            : <span key={i} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', opacity: 0.85, wordBreak: 'break-word' }}>• {txt}</span>;
                        })}
                      </div>
                    </div>
                  )}
                  {/* Même dossier physique vu sous deux noms (titre du besoin vs
                      nom réel sur kDrive) : simple information, pas une alerte. */}
                  {(lot.dupes?.length > 1) && !((() => {
                    const ids = lot.dupes.map(d => d.folder?.folder_id ?? d.folder?.id).filter(v => v != null);
                    return new Set(ids.map(String)).size > 1;
                  })()) && (
                    <div style={{ margin: '9px 0 0 26px', padding: '7px 10px', borderRadius: '8px', background: 'var(--chip-bg)', color: 'var(--text-2)', fontSize: '11.5px', lineHeight: 1.45 }}>
                      <BI icon="info-circle" style={{ marginRight: '6px' }} />Sur kDrive, ce lot apparaît sous {lot.dupes.length} noms : {lot.dupes.map(d => `« ${(d.path || '').split('/').pop()} »`).join(', ')}. S'il s'agit du même dossier, « Renommer » alignera les noms ; si le serveur refuse (« plusieurs dossiers correspondent »), vérifiez dans kDrive qu'il n'y a pas réellement deux dossiers.
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px', marginTop: '9px', paddingLeft: '26px' }}>
                    {lot.extra
                      ? <Badge bg="var(--erp-warning-soft)" fg="var(--erp-warning)" title="Dossier présent sur kDrive mais non rattaché à un besoin de l'ERP (nom non reconnu : doublon, faute de frappe, ou candidats non identifiés). Utilisez « Créer le besoin » pour le rattacher."><BI icon="question-circle" /> Non rattaché</Badge>
                      : lotBadge(lot)}
                    {(lot.extra || lot.folder?.exists === false) && (
                      <button onClick={e => { e.stopPropagation(); createLot(lot); }} disabled={!!creatingLot[lot.path]}
                        title={lot.extra
                          ? 'Le dossier existe déjà sur kDrive — crée (ou rattache) uniquement le besoin dans l\'ERP'
                          : 'Le besoin existe déjà dans l\'ERP — crée (ou rattache) son dossier kDrive (le serveur ne recrée pas un dossier déjà présent)'}
                        className="erp-btn erp-btn--primary erp-btn--sm" style={{ flex: 'none', opacity: creatingLot[lot.path] ? 0.6 : 1 }}>
                        <BI icon={creatingLot[lot.path] ? 'arrow-repeat' : 'folder-plus'} style={creatingLot[lot.path] ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {creatingLot[lot.path] ? 'Création…' : (lot.extra ? 'Créer le besoin' : 'Créer le dossier kDrive')}
                      </button>
                    )}
                    {folderIds(lot.folder || {}).besoin_id == null && lot.extra && (
                      <button onClick={e => { e.stopPropagation(); setRenameLot({ lot, value: lot.label, kdriveOnly: true }); }}
                        title="Renommer ce dossier directement sur kDrive (il n'est rattaché à aucun besoin ERP — renommez-le pour qu'il soit reconnu, puis rescannez)"
                        className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
                        <BI icon="pencil" /> Renommer
                      </button>
                    )}
                    {onOpenBesoin && folderIds(lot.folder || {}).besoin_id != null && (
                      <button onClick={e => { e.stopPropagation(); onOpenBesoin(folderIds(lot.folder).besoin_id); }} title="Ouvrir la fiche du besoin"
                        className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
                        <BI icon="briefcase" /> Ouvrir le besoin
                      </button>
                    )}
                    {folderIds(lot.folder || {}).besoin_id != null && (
                      <button onClick={e => { e.stopPropagation(); setRenameLot({ lot, value: (lot.label || '').replace(/^lot\s*\d+\s*:\s*/i, '') }); }}
                        title="Renommer le lot : le titre du besoin ET son dossier kDrive (re-soumettre le même titre resynchronise le dossier kDrive)"
                        className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
                        <BI icon="pencil" /> Renommer
                      </button>
                    )}
                    {folderIds(lot.folder || {}).besoin_id != null && (
                      <button onClick={e => { e.stopPropagation(); detachLot(lot); }} disabled={!!detachingLot[lot.path]}
                        title="Détacher de l'AO : retire le rattachement sans supprimer le besoin ni son dossier kDrive"
                        className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none', opacity: detachingLot[lot.path] ? 0.6 : 1 }}>
                        <BI icon={detachingLot[lot.path] ? 'arrow-repeat' : 'box-arrow-up-right'} style={detachingLot[lot.path] ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {detachingLot[lot.path] ? 'Détachement…' : 'Détacher'}
                      </button>
                    )}
                    {folderIds(lot.folder || {}).besoin_id != null && (
                      <button onClick={e => { e.stopPropagation(); deleteLot(lot); }} disabled={!!deletingLot[lot.path]}
                        title="Supprimer le lot : le besoin ERP ET son dossier kDrive associé"
                        className="erp-btn erp-btn--danger erp-btn--sm" style={{ flex: 'none', opacity: deletingLot[lot.path] ? 0.6 : 1 }}>
                        <BI icon={deletingLot[lot.path] ? 'arrow-repeat' : 'trash3'} style={deletingLot[lot.path] ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {deletingLot[lot.path] ? 'Suppression…' : 'Supprimer'}
                      </button>
                    )}
                    <span style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>{lot.visCands.length} profil{lot.visCands.length > 1 ? 's' : ''}</span>
                    {/* Combien de profils sont prêts (checklist complète). */}
                    {(() => {
                      const cands = lot.visCands.map(([cf]) => cf).filter(cf => folderProgress(cf).total > 0);
                      if (!cands.length) return null;
                      const ready = cands.filter(cf => folderProgress(cf).complete).length;
                      const all = ready === cands.length;
                      return (
                        <span title={all ? 'Tous les profils ont leurs pièces — lot prêt à être envoyé.' : `${cands.length - ready} profil(s) avec des pièces manquantes`}
                          style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: all ? 'var(--erp-success-soft)' : 'var(--chip-bg)', color: all ? 'var(--erp-success)' : 'var(--text-2)' }}>
                          <BI icon={all ? 'patch-check-fill' : 'hourglass-split'} style={{ marginRight: '5px' }} />{ready}/{cands.length} prêt{ready > 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              );
              const lotBody = (lot) => (
                <div style={{ padding: '2px 14px 12px' }}>
                  {lot.visCands.length === 0
                    ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', padding: '8px 2px' }}>Aucun candidat dans ce lot.</div>
                    : lot.visCands.map(([f, sp], i) => renderFolder(f, sp, `${lot.path}-${i}`))}
                </div>
              );
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    {/* Ajouter un besoin existant (27.10) : à côté des lots, là
                        où sont les besoins du dossier — au-dessus du drive, on
                        ne le voyait pas. */}
                    <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setRattacherOuvert(true)}>
                      <BI icon="plus-lg" /> Ajouter un besoin existant
                    </button>
                    <div style={{ display: 'inline-flex', border: '1px solid var(--input-border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--ghost-bg)' }}>
                      {[['dossier', 'Dossier', 'folder2'], ['liste', 'Liste', 'list-ul']].map(([id, lbl, ic]) => (
                        <button key={id} onClick={() => setPiecesMode(id)} style={{ ...seg(piecesMode === id), display: 'inline-flex', alignItems: 'center', gap: '5px' }}><BI icon={ic} /> {lbl}</button>
                      ))}
                    </div>
                  </div>

                  {piecesMode === 'dossier' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(272px, 1fr))', gap: '16px' }}>
                      {lots.map((lot) => {
                        const isOpen = expandedLot === lot.path;
                        return (
                          <Dossier key={lot.path} level="lot" style={{ gridColumn: isOpen ? '1 / -1' : 'auto' }}>
                            {lotHeader(lot, isOpen, true)}
                            {isOpen && lotBody(lot)}
                          </Dossier>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {lots.map((lot) => (
                        <Dossier key={lot.path} level="lot">
                          {lotHeader(lot, true, false)}
                          {lotBody(lot)}
                        </Dossier>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </>))}
        </>
      )}

      {canonView && (
        <DocumentViewer candidateId={canonView.candidateId} docId={canonView.docId} label={canonView.label}
          cv={estCvDocument(canonView)}
          previewBase={canonView.previewBase} besoinId={canonView.besoinId} candidatNom={canonView.nom}
          showNotification={showNotification} zIndex={pickDoc ? 700 : 500} onClose={() => setCanonView(null)} />
      )}

      {viewer && (
        <div onClick={closeViewer} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '960px', height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
              <BI icon="file-earmark-text" style={{ color: 'var(--text-3)', flex: 'none' }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{viewer.label}</span>
              {viewer.href && <a href={viewer.href} target="_blank" rel="noreferrer" style={{ ...btnGhost, padding: '7px 11px', textDecoration: 'none' }} title="Ouvrir dans kDrive"><BI icon="box-arrow-up-right" /></a>}
              {viewer.dl && <a href={viewer.dl} download={viewer.label} style={{ ...btnGhost, padding: '7px 11px', textDecoration: 'none' }} title="Télécharger"><BI icon="download" /></a>}
              <button onClick={closeViewer} style={{ ...btnGhost, padding: '7px 10px' }} title="Fermer"><BI icon="x-lg" /></button>
            </div>
            <div style={{ flex: 1, minHeight: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {viewer.status === 'loading' && (
                <div style={{ textAlign: 'center', color: 'var(--text-faint)' }}><BI icon="arrow-repeat" style={{ fontSize: '24px' }} /><div style={{ marginTop: '8px', fontSize: '13px' }}>Chargement du document…</div></div>
              )}
              {viewer.status === 'error' && (
                <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '24px' }}>
                  <BI icon="exclamation-triangle" style={{ fontSize: '24px', color: 'var(--erp-warning)' }} />
                  <div style={{ marginTop: '8px', fontSize: '13.5px', fontWeight: 600 }}>Impossible d'afficher le document</div>
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>{viewer.error}</div>
                  {viewer.href && <a href={viewer.href} target="_blank" rel="noreferrer" style={{ ...btnPrimary, marginTop: '14px', textDecoration: 'none' }}><BI icon="box-arrow-up-right" /> Ouvrir dans kDrive</a>}
                </div>
              )}
              {viewer.status === 'ready' && viewer.kind === 'image' && (
                <img src={viewer.url} alt={viewer.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
              {viewer.status === 'ready' && viewer.kind === 'pdf' && (
                <iframe /* pas de sandbox : cf. DocumentViewer */src={viewer.url} title={viewer.label} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
              )}
              {viewer.status === 'ready' && viewer.kind === 'docx' && (
                <div style={{ width: '100%', height: '100%', overflow: 'auto', background: '#3a3d44', padding: '28px 16px' }}>
                  <div className="docx-preview" style={{ maxWidth: '820px', margin: '0 auto', background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.4)', padding: '56px 64px', borderRadius: '2px', fontFamily: 'Calibri, Arial, sans-serif', fontSize: '11pt', lineHeight: 1.5, color: '#000' }}
                    dangerouslySetInnerHTML={{ __html: htmlSur(viewer.html) }} />
                </div>
              )}
              {viewer.status === 'ready' && viewer.kind === 'other' && (
                <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '24px', maxWidth: '420px' }}>
                  <BI icon="file-earmark-richtext" style={{ fontSize: '30px', color: 'var(--text-3)' }} />
                  <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>Aperçu non disponible pour ce format</div>
                  <div style={{ marginTop: '5px', fontSize: '12.5px' }}>Ce format (PowerPoint, Excel…) ne s'affiche pas dans le navigateur. Ouvre-le dans kDrive ou télécharge-le.</div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px' }}>
                    {viewer.href && <a href={viewer.href} target="_blank" rel="noreferrer" style={{ ...btnPrimary, textDecoration: 'none' }}><BI icon="box-arrow-up-right" /> Ouvrir dans kDrive</a>}
                    {viewer.dl && <a href={viewer.dl} download={viewer.label} style={{ ...btnGhost, textDecoration: 'none' }}><BI icon="download" /> Télécharger</a>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Documents d'un positionnement ─────────────────────
const slugName = (s) => (s || 'Candidat').trim().replace(/\s+/g, '_');
function PositionDocsRow({ besoinId, candidateId, candidateName }) {
  const [docs, setDocs] = React.useState(null);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };
  React.useEffect(() => {
    if (besoinId == null || candidateId == null) { setDocs([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/besoins/${besoinId}/candidates/${candidateId}/documents`, { headers: authH });
        if (!r.ok) { if (!cancelled) setDocs([]); return; }
        const j = await r.json();
        const arr = j.data || j.documents || (Array.isArray(j) ? j : []);
        if (!cancelled) setDocs(Array.isArray(arr) ? arr.filter(d => (d.status || 'active') === 'active') : []);
      } catch { if (!cancelled) setDocs([]); }
    })();
    return () => { cancelled = true; };
  }, [besoinId, candidateId]);

  const real = docs || [];
  const hasLabel = (re) => real.some(d => re.test(d.label || ''));
  const derived = [];
  if (!hasLabel(/format entreprise|cv.*entreprise/i)) derived.push({ label: 'CV format entreprise', kind: 'generated', _derived: true, filename: `CV_${slugName(candidateName)}_Entreprise.pdf` });
  if (!hasLabel(/matrice/i)) derived.push({ label: 'Matrice de compétences', kind: 'generated', _derived: true, filename: `Matrice_${slugName(candidateName)}.xlsx` });
  const all = [...real, ...derived];
  if (all.length === 0) return null;

  const kindTag = (k) => k === 'generated' ? 'Généré' : k === 'from_profile' ? 'Repris de la fiche' : 'Importé';
  const dlHref = (d) => d.file_id != null || d.download_url ? (d.download_url || avecCle(`${BASE}/position-documents/${d.id}/download`)) : null;

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '9px' }}>Documents pour ce besoin</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {all.map((d, i) => {
          const href = dlHref(d);
          const chip = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 500, padding: '4px 9px', borderRadius: '6px', background: 'var(--erp-success-soft)', color: 'var(--erp-success)', border: '1px solid rgba(45,200,110,0.25)' }}>
              <BI icon={/matrice/i.test(d.label) ? 'file-earmark-spreadsheet' : 'file-earmark-text'} />
              {d.filename || d.label}
              <span style={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#0f9d58', opacity: 0.8 }}>{kindTag(d.kind)}</span>
            </span>
          );
          return href
            ? <a key={i} href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>{chip}</a>
            : <span key={i} title={d._derived ? 'Sera généré automatiquement à la création du positionnement' : undefined} style={{ opacity: d._derived ? 0.7 : 1 }}>{chip}</span>;
        })}
      </div>
    </div>
  );
}

// ── Résolution des écarts ──
function GapActions({ besoinId, candidateId, candidateEmail, gaps, onRefetch, showNotification }) {
  const [token, setToken] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [manual, setManual] = React.useState(false);
  const [answers, setAnswers] = React.useState({});
  const [resolving, setResolving] = React.useState(false);
  const [emailPrompt, setEmailPrompt] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [serverLink, setServerLink] = React.useState(null);
  const [qStatus, setQStatus] = React.useState(null);
  const [showQR, setShowQR] = React.useState(false);
  const authH = { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' };
  const base = `${BASE}/candidates/${candidateId}/besoins/${besoinId}`;

  const loadQStatus = React.useCallback(async () => {
    try {
      const r = await fetch(`${base}/questionnaire`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (r.ok) { const j = await r.json(); const d = j.data || j; if (d && d.status) setQStatus(d); }
    } catch { /* pas encore de questionnaire */ }
  }, [base]);
  React.useEffect(() => { if (besoinId != null && candidateId != null) loadQStatus(); }, [besoinId, candidateId, loadQStatus]);

  if (besoinId == null || candidateId == null) return null;

  const scheduleRescore = () => { onRefetch?.(); setTimeout(() => { onRefetch?.(); loadQStatus(); }, 10000); };
  const link = serverLink || (token ? `${window.location.origin}/questionnaire/${token}` : null);

  const sendQuestionnaire = async (emailOverride) => {
    const email = (emailOverride || candidateEmail || '').trim();
    setSending(true);
    try {
      const r = await fetch(`${base}/questionnaire/send`, { method: 'POST', headers: authH, body: JSON.stringify(email ? { email } : {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok && !j.need_email) throw new Error(j.error || `HTTP ${r.status}`);
      const tk = j.token || j.data?.token; if (tk) setToken(tk);
      if (j.lien || j.link || j.url) setServerLink(j.lien || j.link || j.url);
      if (j.need_email) { setEmailPrompt(true); showNotification?.('Aucun email sur la fiche — précise l\'adresse.', 'error'); }
      else if (j.envoye) { showNotification?.(`Questionnaire envoyé à ${j.envoye_a || email}${j.questions ? ` (${j.questions} questions)` : ''}`, 'success'); setEmailPrompt(false); loadQStatus(); }
      else { showNotification?.(j.avertissement || 'Lien créé — à transmettre au candidat (copie ci-dessous).', 'success'); }
      scheduleRescore();
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setSending(false); }
  };

  const saveEmailAndSend = async () => {
    const email = emailInput.trim();
    if (!/.+@.+\..+/.test(email)) { showNotification?.('Email invalide', 'error'); return; }
    try { await fetch(`${BASE}/candidates/${candidateId}`, { method: 'PATCH', headers: authH, body: JSON.stringify({ email }) }); } catch { /* non bloquant */ }
    sendQuestionnaire(email);
  };

  const copyLink = () => { if (link) { try { navigator.clipboard.writeText(link); showNotification?.('Lien copié', 'success'); } catch { /* noop */ } } };

  const resolveManual = async () => {
    const reponses = gaps.map((g, i) => ({ point: g.text, reponse: (answers[i] || '').trim() })).filter(x => x.reponse);
    if (!reponses.length) { showNotification?.('Renseigne au moins une réponse', 'error'); return; }
    setResolving(true);
    try {
      const r = await fetch(`${base}/gaps/resolve`, { method: 'POST', headers: authH, body: JSON.stringify({ reponses }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(`${j.injectes ?? reponses.length} réponse(s) enregistrée(s) — rescore en cours…`, 'success');
      setManual(false); setAnswers({}); scheduleRescore();
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setResolving(false); }
  };

  const btn = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 11px', borderRadius: '7px', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)' };

  const qd = (x) => { if (!x) return ''; const t = new Date(x); return isNaN(t) ? '' : t.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' }); };
  const answered = qStatus?.status === 'answered';

  return (
    <div style={{ marginTop: '12px' }}>
      {qStatus && (
        <div style={{ marginBottom: '9px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: answered ? 'var(--erp-success-soft)' : 'var(--chip-bg)', color: answered ? 'var(--erp-success)' : 'var(--text-2)' }}>
            <BI icon={answered ? 'check-circle-fill' : 'send-check'} />
            {answered
              ? `Répondu le ${qd(qStatus.answered_at)}${qStatus.questions ? ` (${qStatus.answers?.length ?? '?'}/${qStatus.questions})` : ''}`
              : `Envoyé${qStatus.sent_to ? ` à ${qStatus.sent_to}` : ''}${qStatus.sent_at ? ` le ${qd(qStatus.sent_at)}` : ''}`}
          </span>
          {answered && Array.isArray(qStatus.answers) && qStatus.answers.length > 0 && (
            <button onClick={() => setShowQR(v => !v)} style={{ ...btn, padding: '3px 9px', fontSize: '11.5px' }}><BI icon={showQR ? 'chevron-up' : 'chevron-down'} /> {showQR ? 'Masquer' : 'Voir les réponses'}</button>
          )}
        </div>
      )}

      {showQR && answered && (
        <div style={{ marginBottom: '10px', padding: '12px', borderRadius: '10px', border: '1px solid var(--hair)', background: 'var(--ghost-bg)' }}>
          {(qStatus.answers || []).map((a, i) => (
            <div key={i} style={{ marginBottom: i === (qStatus.answers.length - 1) ? 0 : '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}>{a.question || a.point || `Q${i + 1}`}</div>
              <div style={{ fontSize: '13px', color: 'var(--text)', marginTop: '2px' }}>{a.answer || a.reponse || '—'}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => sendQuestionnaire()} disabled={sending} style={{ ...btn, opacity: sending ? 0.6 : 1 }}><BI icon={sending ? 'arrow-repeat' : 'envelope'} /> {sending ? 'Envoi…' : (qStatus ? 'Renvoyer le questionnaire' : (candidateEmail ? 'Envoyer le questionnaire par email' : 'Envoyer le questionnaire'))}</button>
        <button onClick={() => setManual(m => !m)} style={btn}><BI icon="pencil-square" /> Répondre manuellement</button>
      </div>

      {emailPrompt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '9px', padding: '9px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-2)', flex: 'none' }}>Email du candidat :</span>
          <input value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveEmailAndSend(); }} type="email" autoFocus placeholder="prenom.nom@email.com"
            style={{ flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: '6px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', outline: 'none' }} />
          <button onClick={saveEmailAndSend} disabled={sending} style={{ ...btn, flex: 'none', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }}><BI icon="send" /> Envoyer</button>
        </div>
      )}

      {link && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '9px', padding: '8px 11px', borderRadius: '8px', background: 'var(--code-bg, var(--hair))' }}>
          <BI icon="link-45deg" style={{ color: 'var(--text-3)', flex: 'none' }} />
          <span style={{ flex: 1, minWidth: 0, fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{link}</span>
          <button onClick={copyLink} style={{ ...btn, flex: 'none', padding: '4px 9px' }}><BI icon="clipboard" /> Copier</button>
        </div>
      )}

      {manual && (
        <div style={{ marginTop: '10px', padding: '12px', borderRadius: '10px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)' }}>
          {gaps.map((g, i) => (
            <div key={i} style={{ marginBottom: '9px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '4px' }}>{g.text}</div>
              <input value={answers[i] || ''} onChange={e => setAnswers(a => ({ ...a, [i]: e.target.value }))} placeholder="Réponse / preuve…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', outline: 'none' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button onClick={resolveManual} disabled={resolving} style={{ ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', opacity: resolving ? 0.6 : 1 }}><BI icon="check-lg" /> {resolving ? 'Envoi…' : 'Valider les réponses'}</button>
            <button onClick={() => setManual(false)} style={btn}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Couleur-signal d'un score de matching ──
const matchColor = (s) => {
  const v = Number(s) || 0;
  if (v >= 80) return { fg: 'var(--erp-success)', bg: 'var(--erp-success-soft)' };
  if (v >= 55) return { fg: 'var(--erp-warning)', bg: 'var(--erp-warning-soft)' };
  return { fg: '#7b828f', bg: '#eceef1' };
};
const matchTag = (v) => { const n = Number(v) || 0; if (n >= 80) return { label: 'Fort', ...matchColor(n) }; if (n >= 55) return { label: 'Partiel', ...matchColor(n) }; return { label: 'Faible', ...matchColor(n) }; };
const GAP_FG = 'var(--erp-warning)';
const SUBS = [
  { k: 'competences', l: 'Compétences' },
  { k: 'experience', l: 'Expérience' },
  { k: 'seniorite', l: 'Séniorité' },
];
const NON_SCORED = /localisation|disponibilit/i;

// Le score d'un dossier AO est l'ADÉQUATION au poste, ramenée à 100 (expert
// AO, 30.10) — pas une note finale, qui ajouterait le prix de l'offre.
const TITRE_ADEQUATION = (v) => `Adéquation au poste : ${v}/100 (le prix de l'offre n'entre pas dans ce score).`;
function MatchRing({ score }) {
  const v = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  const c = matchColor(v);
  return (
    <div title={TITRE_ADEQUATION(v)} aria-label={`Adéquation ${v} sur 100`} style={{ flex: 'none', width: '52px', height: '52px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${c.fg} 0 ${v}%, var(--hair) ${v}% 100%)` }}>
      <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: "'IBM Plex Mono',monospace", color: c.fg }}>{v}</span>
      </div>
    </div>
  );
}

function MatchingPanel({ data, loading, unavailable, status, progress, duplicates, ponderations, recomputing, onRecompute, onRefetch, recomputingBesoin, onRecomputeBesoin, expanded, onToggle, onOpenCandidat, onOpenBesoin, onOpenCv, onUnlink, onReport, showNotification }) {
  const ssTitle = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '9px' };
  const [showDetail, setShowDetail] = useState(true);
  const computing = status && status !== 'ready';
  const besoins = data || [];
  const rankMap = React.useMemo(() => {
    const pairs = besoins.flatMap(b => (b.candidats || b.candidates || []).map(c => ({ poste: b.titre || b.besoin_titre, cid: c.candidate_id ?? c.nom ?? c.name, score: Number(c.score) || 0 })));
    pairs.sort((x, y) => y.score - x.score);
    const m = new Map(); pairs.forEach((p, i) => m.set(`${p.poste}|${p.cid}`, i + 1));
    return m;
  }, [besoins]);
  const rankOf = (poste, cid) => rankMap.get(`${poste}|${cid ?? ''}`);

  if (loading && besoins.length === 0) return (
    <div style={{ ...card, padding: '40px', textAlign: 'center', color: 'var(--text-faint)' }}>
      <BI icon="arrow-repeat" /> Calcul du matching…
      {progress?.total ? <div style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-2)' }}>{progress.done}/{progress.total} besoins analysés</div> : null}
    </div>
  );
  if (unavailable) return (
    <div style={{ ...card, padding: '32px 24px', textAlign: 'center' }}>
      <BI icon="diagram-3" style={{ fontSize: '26px', color: 'var(--text-faint)' }} />
      <p style={{ margin: '12px 0 4px', fontWeight: 600, color: 'var(--text)' }}>Matching indisponible</p>
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-2)' }}>L'endpoint <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px' }}>GET /ao/:id/matching</code> n'est pas encore actif côté serveur.</p>
      <button onClick={() => onRecompute(false)} disabled={recomputing || computing} style={{ ...btnGhost, padding: '8px 14px', opacity: (recomputing || computing) ? 0.6 : 1 }}>
        <BI icon="arrow-clockwise" /> {recomputing ? 'Lancement…' : 'Lancer le calcul'}
      </button>
    </div>
  );
  if (besoins.length === 0 && !computing) return (
    <div style={{ ...card, padding: '32px 24px', textAlign: 'center' }}>
      <BI icon="people" style={{ fontSize: '26px', color: 'var(--text-faint)' }} />
      <p style={{ margin: '12px 0 4px', fontWeight: 600, color: 'var(--text)' }}>Aucun candidat scoré</p>
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-2)', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>Le cache est peut-être vide (calcul d'avant le fix). Lance un recalcul — il force le calcul et ignore le cache.</p>
      <button onClick={() => onRecompute(false)} disabled={recomputing} style={{ ...btnPrimary, padding: '9px 16px', opacity: recomputing ? 0.6 : 1 }}>
        <BI icon={recomputing ? 'arrow-repeat' : 'arrow-clockwise'} /> {recomputing ? 'Lancement…' : 'Recalculer le matching'}
      </button>
    </div>
  );

  return (
    <>
      {computing && (() => { const done = progress?.done ?? besoins.length; const total = progress?.total || done; const pct = total ? Math.round((done / total) * 100) : 0; return (
        <div style={{ ...card, padding: '12px 16px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
            <BI icon="arrow-repeat" style={{ color: 'var(--brand)' }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>Analyse en cours…</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-2)', fontFamily: "'IBM Plex Mono',monospace" }}>{done}/{total} besoins</span>
          </div>
          <div style={{ height: '6px', borderRadius: '4px', background: 'var(--hair)', overflow: 'hidden' }}><div style={{ height: '100%', width: `${pct}%`, background: 'var(--brand)', borderRadius: '4px', transition: 'width .4s' }} /></div>
        </div>
      ); })()}

      {/* Ce que mesure le score (expert AO, 30.10) : l'adéquation, sur 100 —
          la note finale de l'acheteur ajoute le prix, qu'on ne peut pas calculer. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 2px 12px', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>
        <BI icon="info-circle" />
        <span>Score = <strong>adéquation au poste, sur 100</strong> — le prix de l’offre n’entre pas dans ce score.</span>
      </div>

      {/* Doublons de fiches besoin : information d'administration, pas une erreur —
          les scores affichés sont corrects, c'est la base qui reste à nettoyer. */}
      {(duplicates || []).length > 0 && (() => {
        const byRef = new Map();
        duplicates.forEach(d => {
          const k = d.reference || d.titre || String(d.besoin_id);
          if (!byRef.has(k)) byRef.set(k, { ref: d.reference, titre: d.titre, n: 1 });
          else byRef.get(k).n += 1;
        });
        return (
          <div style={{ ...card, padding: '11px 14px', marginBottom: '14px', borderColor: 'rgba(217,119,6,0.35)', background: 'rgba(217,119,6,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', fontWeight: 600, color: 'var(--erp-warning)' }}>
              <BI icon="exclamation-triangle-fill" /> Fiches besoin en double
            </div>
            <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Les candidats sont bien regroupés et le classement est juste — il reste à fusionner ces fiches dans l'ERP.
            </div>
            <div style={{ marginTop: '7px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {[...byRef.values()].map((d, i) => (
                <div key={i} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.n + 1} fiches besoin pour {d.ref ? `la référence ${d.ref}` : `« ${d.titre || 'poste sans titre'} »`} — à fusionner
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Avec quoi ce rapport a été calculé — le calcul démarre souvent tout
          seul après l'analyse : le dire, et offrir le geste d'à côté. */}
      <ReglagesMatchingBanner ponderations={ponderations ? (ponderations.poids || {}) : null}
        auteur={ponderations?.auteur} date={ponderations?.date} onAjuster={() => onRecompute(false)} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', margin: '0 0 14px', fontSize: '11.5px', color: 'var(--text-2)' }}>
        <span><span style={{ width: '9px', height: '9px', borderRadius: '3px', background: 'var(--erp-success)', display: 'inline-block', marginRight: '5px' }} />Fort ≥ 80</span>
        <span><span style={{ width: '9px', height: '9px', borderRadius: '3px', background: 'var(--erp-warning)', display: 'inline-block', marginRight: '5px' }} />Moyen 55–79</span>
        <span><span style={{ width: '9px', height: '9px', borderRadius: '3px', background: '#7b828f', display: 'inline-block', marginRight: '5px' }} />Faible &lt; 55</span>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', border: '1px solid var(--input-border)', borderRadius: '8px', overflow: 'hidden' }}>
            {[[true, 'Avec détail', 'folder2'], [false, 'Sans détail', 'list-ul']].map(([val, lbl, ic]) => (
              <button key={String(val)} onClick={() => setShowDetail(val)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 11px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, background: showDetail === val ? 'var(--accent-soft-bg)' : 'var(--ghost-bg)', color: showDetail === val ? 'var(--text-strong)' : 'var(--text-2)' }}><BI icon={ic} /> {lbl}</button>
            ))}
          </div>
          <button onClick={() => onRecompute(false)} disabled={recomputing || computing} style={{ ...btnGhost, padding: '7px 12px', opacity: (recomputing || computing) ? 0.6 : 1 }}
            title="Ne reprend que ce qui a changé depuis le dernier calcul — rapide et sans surcoût">
            <BI icon={(recomputing || computing) ? 'arrow-repeat' : 'arrow-clockwise'} /> {computing ? 'Calcul…' : recomputing ? 'Lancement…' : 'Recalculer'}
          </button>
        </div>
      </div>

      {(!showDetail
        ? [{ _flat: true, titre: 'Classement global', candidats: besoins.flatMap(b => (b.candidats || b.candidates || []).map(c => ({ ...c, _poste: b.titre || b.besoin_titre, _besoin_id: b.besoin_id ?? b.id }))) }]
        : besoins
      ).map((b, bi) => {
        const cands = [...(b.candidats || b.candidates || [])].sort((x, y) => (Number(y.score) || 0) - (Number(x.score) || 0));
        const lotBusy = !b._flat && recomputingBesoin === (b.besoin_id ?? b.id);
        return (
          <Dossier key={b.besoin_id || bi} level="lot" style={{ marginBottom: '16px', position: 'relative', overflow: 'hidden' }}>
            {lotBusy && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--hair)', overflow: 'hidden', zIndex: 3 }}>
                <div style={{ position: 'absolute', top: 0, height: '100%', width: '40%', background: 'var(--brand)', borderRadius: '2px', animation: 'ao-recalc-bar 1.1s ease-in-out infinite' }} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: '1px solid var(--hair)' }}>
              <BI icon="briefcase" style={{ color: 'var(--text-3)', fontSize: '17px', flex: 'none' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>{b.titre || b.besoin_titre || 'Poste'}</span>
                  {/* Groupe primaire d'une référence en double : les candidats des
                      deux fiches sont déjà réunis ici par le serveur. */}
                  {(b.duplicates || []).length > 0 && (
                    <span title="Plusieurs fiches besoin portent cette référence dans l'ERP — leurs candidats sont regroupés ici. Pensez à fusionner les fiches."
                      style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}>
                      {b.duplicates.length + 1} fiches besoin regroupées
                    </span>
                  )}
                </div>
                {(b.exigences?.length || b.requirements?.length) ? <div style={{ fontSize: '12px', color: 'var(--text-2)' }}>Exigences : {(b.exigences || b.requirements).join(' · ')}</div> : null}
              </div>
              {!b._flat && onRecomputeBesoin && (b.besoin_id ?? b.id) != null && (() => {
                const bid = b.besoin_id ?? b.id; const busy = recomputingBesoin === bid;
                return <button onClick={() => onRecomputeBesoin(bid)} disabled={busy || recomputing} title="Recalculer uniquement les scores de ce besoin" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="arrow-clockwise" style={busy ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> {busy ? 'Recalcul…' : 'Recalculer'}</button>;
              })()}
              {!b._flat && onOpenBesoin && (b.besoin_id ?? b.id) != null && (
                <button onClick={() => onOpenBesoin(b.besoin_id ?? b.id)} title="Ouvrir la fiche du besoin" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}><BI icon="briefcase" /> Ouvrir le besoin</button>
              )}
              <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '20px', background: 'var(--chip-bg)', color: 'var(--text-2)', flex: 'none' }}>{cands.length} profil{cands.length > 1 ? 's' : ''}</span>
            </div>

            {!b._flat && Array.isArray(b.sanity_warnings) && b.sanity_warnings.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '10px 18px', borderBottom: '1px solid var(--hair)', background: 'rgba(217,119,6,0.06)' }}>
                {b.sanity_warnings.map((w, wi) => (
                  <div key={wi} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '12px', color: '#b45309' }}>
                    <BI icon="exclamation-triangle" style={{ flex: 'none', marginTop: '2px' }} /> <span>{typeof w === 'string' ? w : (w.message || w.text || w.label || '')}</span>
                  </div>
                ))}
              </div>
            )}

            {lotBusy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', fontSize: '12px', fontWeight: 600, color: 'var(--brand)', borderBottom: '1px solid var(--hair)', background: 'rgba(226,35,26,0.05)' }}>
                <BI icon="arrow-repeat" style={{ animation: 'ao-spin .8s linear infinite' }} /> Recalcul des scores en cours…
              </div>
            )}
            <div style={lotBusy ? { animation: 'ao-recalc-pulse 1.4s ease-in-out infinite', pointerEvents: 'none', filter: 'grayscale(.15)', transition: 'opacity .3s' } : { transition: 'opacity .3s' }}>
            {cands.length === 0 ? (
              <div style={{ padding: '16px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun candidat positionné.</div>
            ) : cands.map((c, ci) => {
              const key = `${b.besoin_id || bi}|${c._poste || ''}|${c.candidate_id || ci}`;
              const open = expanded.has(key);
              const subs = c.sous_scores || c.subscores || {};
              const critArr = Array.isArray(c.criteria) ? c.criteria.filter(cr => !NON_SCORED.test(cr.label || '')) : [];
              const chipItems = SUBS.some(s => subs[s.k] != null)
                ? SUBS.filter(s => subs[s.k] != null).map(s => ({ label: s.l, pct: subs[s.k] }))
                : critArr.map(cr => ({ label: cr.label, pct: cr.pct ?? cr.score }));
              const risk = typeof c.risque_administratif === 'string' ? c.risque_administratif : (c.risque_administratif?.niveau || c.risque_administratif?.label || null);
              const showRisk = risk && !/faible|aucun|none|ok/i.test(risk);
              const flags = Array.isArray(c.flags) ? c.flags : [];
              const sectionsNP = Array.isArray(c.sections_non_parsees) ? c.sections_non_parsees : [];
              const cvPartial = c.cv_partiellement_parse === true || sectionsNP.length > 0;
              const needCheck = c.verification_requise === true;
              const roleNature = typeof c.role_nature === 'string' ? c.role_nature : (c.role_nature?.label || null);
              // Le CV sur lequel le score a été calculé. Plusieurs formes
              // acceptées ; en l'absence du champ, pas de puce — on n'affiche
              // pas une provenance que le serveur n'a pas donnée.
              const cvU = c.cv_utilise || c.cv_used || null;
              const cvULabel = (typeof cvU === 'string' ? cvU : (cvU?.label || cvU?.filename)) || c.cv_label || c.cv_filename || null;
              const cvUDocId = (cvU && typeof cvU === 'object' ? (cvU.document_id ?? cvU.doc_id) : null) ?? c.cv_document_id ?? c.cv_doc_id ?? null;
              const cvUDesigne = c.cv_designe === true || (cvU && typeof cvU === 'object' && cvU.designe === true);
              const hasChips = showRisk || flags.length > 0 || cvPartial || needCheck || roleNature || cvULabel;
              const rank = rankOf(b._flat ? c._poste : (b.titre || b.besoin_titre), c.candidate_id ?? c.nom ?? c.name);
              return (
                <div key={key} style={{ borderTop: '1px solid var(--hair)' }}>
                  <div onClick={() => onToggle(key)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 18px', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover-bg, #fafbfc)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {rank != null && <span style={{ flex: 'none', width: '28px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-faint)' }}>#{rank}</span>}
                    <div style={{ width: '40px', height: '40px', borderRadius: '9px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13.5px', fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", background: '#eef1f6', color: '#3a4252' }}>{(c.nom || c.name || '··').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>{c.nom || c.name || `Candidat ${c.candidate_id || ''}`} <BI icon={open ? 'chevron-down' : 'chevron-right'} style={{ fontSize: '10px', color: 'var(--text-faint)' }} /></div>
                      <div style={{ fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c._poste ? <span style={{ fontWeight: 600, color: 'var(--text-3)' }}>{c._poste} · </span> : null}{c.titre || c.role || ''}{c.localisation ? ` · ${c.localisation}` : ''}{c.disponibilite ? ` · ${c.disponibilite}` : ''}</div>
                      {c.resume_1_ligne && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.resume_1_ligne}</div>}
                      {hasChips && (
                        <div style={{ display: 'flex', gap: '5px', marginTop: '5px', flexWrap: 'wrap' }}>
                          {cvULabel && (
                            <span
                              title={cvUDesigne ? 'Score calculé sur le CV désigné pour ce besoin' : 'Score calculé sur ce CV'}
                              onClick={cvUDocId != null && c.candidate_id != null && onOpenCv
                                ? (e) => { e.stopPropagation(); onOpenCv(c.candidate_id, cvUDocId, cvULabel, c._besoin_id ?? c.besoin_id, c.nom || c.name); }
                                : undefined}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: cvUDesigne ? 'rgba(22,163,74,0.12)' : 'var(--chip-bg)', color: cvUDesigne ? 'var(--erp-success)' : 'var(--text-2)', cursor: (cvUDocId != null && onOpenCv) ? 'pointer' : 'default' }}>
                              <BI icon={cvUDesigne ? 'patch-check' : 'file-earmark-text'} /> CV analysé : {cvULabel}
                            </span>
                          )}
                          {roleNature && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>{roleNature}</span>}
                          {showRisk && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}><BI icon="exclamation-triangle" /> {risk}</span>}
                          {needCheck && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}><BI icon="patch-question" /> À vérifier</span>}
                          {cvPartial && <span title={sectionsNP.length ? `Sections non lues : ${sectionsNP.join(', ')}` : undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}><BI icon="file-earmark-break" /> CV partiellement lu</span>}
                          {flags.slice(0, 3).map((f, k) => <span key={k} style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>{typeof f === 'string' ? f : (f.label || f.text || '')}</span>)}
                        </div>
                      )}
                      {showDetail && chipItems.length > 0 && (
                        <div style={{ display: 'flex', gap: '5px', marginTop: '6px', flexWrap: 'wrap' }}>
                          {chipItems.map((it, k) => { const cc = matchColor(it.pct); return (
                            <span key={k} style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: cc.bg, color: cc.fg }}>{it.label} {Math.round(Number(it.pct) || 0)}</span>
                          ); })}
                        </div>
                      )}
                    </div>
                    {onOpenCandidat && c.candidate_id != null && (
                      <button onClick={e => { e.stopPropagation(); onOpenCandidat(c.candidate_id); }} title="Ouvrir la fiche candidat"
                        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                        <BI icon="person-badge" /> Fiche
                      </button>
                    )}
                    {onUnlink && c.candidate_id != null && (b._flat ? c._besoin_id : (b.besoin_id ?? b.id)) != null && (
                      <button onClick={e => { e.stopPropagation(); onUnlink(c.candidate_id, b._flat ? c._besoin_id : (b.besoin_id ?? b.id), c.nom || c.name, b._flat ? c._poste : (b.titre || b.besoin_titre)); }}
                        title="Retirer ce candidat du besoin — sa fiche et son dossier kDrive restent"
                        className="ao-del-btn"
                        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', cursor: 'pointer', fontSize: '13px' }}>
                        <BI icon="person-dash" />
                      </button>
                    )}
                    {c.poids_personnalises === true && (
                      <span title="Score calculé avec des poids ajustés pour ce lancement — pas les réglages par défaut."
                        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '5px', background: 'var(--erp-warning-soft)', color: '#b45309' }}>
                        <BI icon="sliders" /> ajusté
                      </span>
                    )}
                    <StatutAo statut={c.statut_ao} libelle={c.statut_ao_libelle} enLEtat={c.score_en_l_etat} potentiel={c.score_potentiel} aConfirmer={c.a_confirmer} confirmations={c.confirmations_ao} besoinId={b._flat ? c._besoin_id : (b.besoin_id ?? b.id)} candidateId={c.candidate_id} showNotification={showNotification} />
                    <MatchRing score={c.score} />
                  </div>

                  {open && (() => {
                    const sm = c.skills_match || c.competences_ok || [];
                    const mm = c.skills_missing || c.competences_manquantes || [];
                    const criteria = (c.criteria && c.criteria.length)
                      ? c.criteria.map(cr => ({ label: cr.label, pct: cr.pct ?? cr.score, detail: cr.detail, tag: cr.tag })).filter(cr => !NON_SCORED.test(cr.label || ''))
                      : SUBS.filter(s => subs[s.k] != null).map(s => ({ label: s.l, pct: subs[s.k] }));
                    const coverage = c.skill_coverage || c.skillCoverage || ((sm.length || mm.length) ? `${sm.length}/${sm.length + mm.length} requises` : null);
                    const gaps = (c.gaps && c.gaps.length)
                      ? c.gaps.map(g => (typeof g === 'string' ? { text: g } : g))
                      : mm.map(s => ({ text: `${s} — compétence non couverte`, delta: 'à sonder' }));
                    const vc = c.verdict ? matchColor(c.verdict === 'fort' ? 90 : c.verdict === 'moyen' ? 65 : 40) : null;
                    return (
                    <div style={{ borderTop: '1px solid var(--hair)', background: 'var(--ghost-bg, #fafbfc)', padding: '18px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        {c.verdict && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 700, padding: '5px 11px', borderRadius: '7px', background: vc.bg, color: vc.fg }}><BI icon="patch-check-fill" /> {c.verdict === 'fort' ? 'Fort match — recommandé' : c.verdict === 'moyen' ? 'Match moyen' : 'Match faible'}</span>}
                        {coverage && <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>{coverage}</span>}
                        {c.statut_ao && <span style={{ flexBasis: '100%' }}><StatutAo large statut={c.statut_ao} libelle={c.statut_ao_libelle} enLEtat={c.score_en_l_etat} potentiel={c.score_potentiel} aConfirmer={c.a_confirmer} confirmations={c.confirmations_ao} besoinId={b._flat ? c._besoin_id : (b.besoin_id ?? b.id)} candidateId={c.candidate_id} showNotification={showNotification} /></span>}
                        {onReport && <button onClick={() => onReport(b, c)} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 13px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}><BI icon="filetype-pdf" /> Rapport PDF</button>}
                      </div>

                      <div style={ssTitle}>Adéquation par critère</div>
                      {criteria.map((cr, i) => { const v = Math.max(0, Math.min(100, Math.round(Number(cr.pct) || 0))); const tag = matchTag(v); return (
                        <div key={i} style={{ marginBottom: cr.detail ? '10px' : '9px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ width: '128px', fontSize: '13px', fontWeight: 600, color: 'var(--text)', flex: 'none' }}>{cr.label}</span>
                            <div style={{ flex: 1, height: '7px', borderRadius: '4px', background: 'var(--code-bg, var(--hair))', overflow: 'hidden' }}><div style={{ height: '100%', width: `${v}%`, borderRadius: '4px', background: tag.fg }} /></div>
                            <span style={{ flex: 'none', width: '58px', textAlign: 'right', fontSize: '11px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: tag.bg, color: tag.fg }}>{cr.tag || tag.label}</span>
                          </div>
                          {cr.detail && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', paddingLeft: '138px', marginTop: '3px' }}>{cr.detail}</div>}
                        </div>
                      ); })}

                      {(sm.length || mm.length) ? (
                        <div style={{ marginTop: '16px' }}>
                          <div style={ssTitle}>Compétences requises{coverage ? ` · ${coverage}` : ''}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {sm.map((s, i) => <span key={`h${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', fontWeight: 500, padding: '3px 9px', borderRadius: '6px', background: 'var(--erp-success-soft)', color: 'var(--erp-success)' }}><BI icon="check-lg" /> {s}</span>)}
                            {mm.map((s, i) => <span key={`m${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', fontWeight: 500, padding: '3px 9px', borderRadius: '6px', background: '#eceef1', color: '#7b828f' }}><BI icon="dash-lg" /> {s}</span>)}
                          </div>
                        </div>
                      ) : null}

                      {gaps.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ ...ssTitle, display: 'inline-flex', alignItems: 'center', gap: '7px' }}><BI icon="chat-square-quote" /> Écarts à challenger</div>
                          {gaps.map((g, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '5px 0' }}>
                              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: GAP_FG, marginTop: '6px', flex: 'none' }} />
                              <span style={{ flex: 1, minWidth: 0, fontSize: '13px', color: 'var(--text)' }}>{g.text}</span>
                              {g.delta && <span style={{ flex: 'none', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: GAP_FG }}>{g.delta}</span>}
                            </div>
                          ))}
                          {!b._flat && <GapActions besoinId={b.besoin_id ?? b.id} candidateId={c.candidate_id} candidateEmail={c.email || c.mail} gaps={gaps} onRefetch={onRefetch} showNotification={showNotification} />}
                        </div>
                      )}

                      {!b._flat && <PositionDocsRow besoinId={b.besoin_id ?? b.id} candidateId={c.candidate_id} candidateName={c.nom || c.name} />}
                    </div>
                    );
                  })()}
                </div>
              );
            })}
            </div>
          </Dossier>
        );
      })}
    </>
  );
}

// ── Génération des contrats de l'AO ──────────────────────────────────────────
// Variables du template : le back remplit firstName/lastName/TODAY tout seul ;
// les autres (détectées via GET /templates/:id, formats {{cle}} et $cle$)
// sont saisies dans un mini-formulaire et envoyées dans POST { vars }.
// Repli quand le serveur ne publie pas la liste des variables qu'il remplit.
// Cette liste ne doit PAS grandir ici : dès que le serveur en remplit d'autres
// depuis la fiche du candidat, c'est lui qui doit le dire, sinon l'écran
// continue de réclamer à la main ce qui est déjà connu.
const CONTRACT_AUTO_VARS = ['firstName', 'lastName', 'TODAY'];

/**
 * Variables propres à la personne, par opposition aux conditions du contrat.
 *
 * La distinction n'est pas cosmétique : les variables sont saisies UNE fois et
 * écrites sur tous les contrats du lot. Une condition commune (date de début,
 * salaire) le supporte ; une civilité ou une date de naissance non — elle
 * produirait un contrat faux pour tout le monde sauf une personne.
 */
// Une pièce qui dit la nationalité : pièce d'identité, passeport, permis de
// travail ou de séjour (libellé de la pièce ou nom du fichier).
const PIECE_IDENTITE = /\b(cni|carte d.?identit|pi[eè]ce d.?identit|passeport|passport|permis|titre de s[ée]jour|id card)/i;

const CONTRACT_VARS_PERSONNE = [
  'civility', 'address', 'dateOfBirth', 'birthDate', 'nationality', 'email', 'phone', 'avsNumber', 'workPermit',
  // Le salaire est négocié par personne et par lot : une valeur unique saisie
  // ici s'écrirait à l'identique sur tous les contrats, ce qui est faux et
  // invisible sur un document signé.
  'annualSalary', 'salary', 'salaire', 'salaireAnnuel', 'monthlySalary',
];
const estVarPersonne = (v) => CONTRACT_VARS_PERSONNE.some(p => p.toLowerCase() === String(v).toLowerCase());
const CONTRACT_VAR_FIELDS = {
  civility: { label: 'Civilité', placeholder: 'Monsieur / Madame' },
  address: { label: 'Adresse' },
  dateOfBirth: { label: 'Date de naissance', type: 'date' },
  nationality: { label: 'Nationalité' },
  startDate: { label: 'Date de début', type: 'date' },
  annualSalary: { label: 'Salaire annuel (CHF)', placeholder: "96'000" },
  employeeType: { label: 'Type d\'emploi', placeholder: 'CDI, CDD, mission…' },
  dailyExpenses: { label: 'Frais journaliers (CHF)', placeholder: '30' },
};
const normVarName = (v) => String(v || '').replace(/^\{\{|\}\}$/g, '').replace(/^\$|\$$/g, '').trim();

/**
 * Libellé lisible d'une variable de template, quelle que soit sa casse.
 *
 * Les templates écrivent « annualsalary » là où la table dit « annualSalary » :
 * une consultation sensible à la casse renvoyait le nom technique brut à
 * l'écran. On compare donc en minuscules — la substitution côté serveur l'est
 * déjà, l'affichage n'a pas de raison d'être plus strict.
 */
const champVar = (v) => {
  const k = String(v || '').toLowerCase();
  const trouve = Object.keys(CONTRACT_VAR_FIELDS).find(c => c.toLowerCase() === k);
  return trouve ? CONTRACT_VAR_FIELDS[trouve] : null;
};
const libelleVar = (v) => champVar(v)?.label || v;

const CIBLE_AIDE = {
  candidat: 'Cliquez sur le document pour placer la signature du candidat',
  date_candidat: 'Cliquez pour placer la date du candidat',
  entreprise: 'Cliquez pour placer la contresignature de l\'entreprise',
  date_entreprise: 'Cliquez pour placer la date de l\'entreprise',
};

/**
 * Repère de placement, ancré par son coin haut-gauche — la même référence que
 * le serveur. Le repère inactif est estompé, pour qu'on voie toutes les
 * positions sans se tromper de cible.
 */
function Repere({ x, y, actif, fond, texte, libelle, fin, w, h }) {
  // Quand le serveur nous donne l'encombrement réel du sceau, on dessine la
  // zone plutôt qu'une étiquette : un sceau posé trop bas se voit déborder
  // avant d'être généré, au lieu d'être recadré en silence.
  const zone = w != null && h != null;
  return (
    <div style={{
      position: 'absolute', left: `${x}%`, top: `${y}%`,
      ...(zone ? { width: `${w}%`, height: `${h}%`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${fond}`, borderRadius: '5px', background: 'transparent' } : {}),
      pointerEvents: 'none', opacity: actif ? 1 : 0.5, boxSizing: 'border-box',
    }}>
      <span style={{
        padding: fin ? '2px 6px' : '3px 8px', borderRadius: '6px',
        background: fond, color: texte, fontSize: fin ? '9px' : '10px', fontWeight: 700,
        whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}>{libelle}</span>
    </div>
  );
}

const nombreOuNull = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

/**
 * Un champ du catalogue publié par le serveur (bloc `champs` de
 * GET /ao/:id/contracts/preview) : sa valeur par défaut et ses bornes.
 *
 * Le catalogue est la seule source — aucun nom n'est inventé. En revanche
 * `signature.sceau_w` désigne le chemin dans la REQUÊTE, et le catalogue peut
 * légitimement l'indexer sous ce chemin, sous sa forme imbriquée, ou sous le
 * seul nom du champ. Les trois désignent la même clé ; ne lire que la première
 * faisait disparaître le réglage de l'écran alors qu'il était publié.
 */
function lireChamp(champs, chemin) {
  if (!champs) return null;
  const [tete, queue] = chemin.split('.');
  const c = champs[chemin] ?? (queue ? champs[tete]?.[queue] ?? champs[queue] : champs[tete]);
  if (c == null) return null;
  if (typeof c !== 'object') return { defaut: nombreOuNull(c), min: null, max: null };
  const b = c.bornes || c;
  return {
    defaut: nombreOuNull(c.defaut ?? c.default),
    min: nombreOuNull(b.min ?? b.minimum),
    max: nombreOuNull(b.max ?? b.maximum),
  };
}

// D'où vient la valeur d'une variable, dit en français. « ABSENTE » n'est pas
// une origine mais un manque : c'est le seul cas qui appelle une action, et il
// est présenté comme tel.
const ORIGINE_VAR = {
  positionnement: 'du lot',
  fiche_candidat: 'de la fiche',
  vars: 'saisie pour tout le lot',
  defaut_organisation: 'du réglage de l\'organisation',
  ABSENTE: null,
};
const estAbsente = (v) => String(v || '').toUpperCase() === 'ABSENTE';

const NOM_BLOC = {
  sceau_candidat: 'la signature du candidat',
  date_candidat: 'la date du candidat',
  sceau_entreprise: 'la contresignature de l\'entreprise',
  date_entreprise: 'la date de l\'entreprise',
};

/**
 * Recadrages à montrer. Le serveur en signale jusqu'à quatre, mais tous ne
 * concernent pas l'utilisatrice : quand l'origine est une position par défaut,
 * c'est le serveur qui s'ajuste tout seul, et l'avertir reviendrait à lui
 * reprocher un choix qu'elle n'a pas fait. On ne garde que le cas où SA
 * position a été modifiée.
 */
const recadragesAMontrer = (v) =>
  (Array.isArray(v) ? v : v ? [v] : []).filter(r => (r?.origine || 'position demandée') === 'position demandée');

/** Bornes de placement d'un bloc, telles que le serveur les publie. */
function lireBornes(bornes, bloc) {
  const b = bornes?.[bloc];
  if (!b) return null;
  return {
    xMin: nombreOuNull(b.x_pct_min) ?? 0, xMax: nombreOuNull(b.x_pct_max) ?? 100,
    yMin: nombreOuNull(b.y_pct_min) ?? 0, yMax: nombreOuNull(b.y_pct_max) ?? 100,
  };
}

/**
 * Bornes calculées sur la page affichée, en repli tant qu'aucun aperçu n'a
 * publié les siennes.
 *
 * Les bornes du preview supposent un A4 : sur un template d'un autre format,
 * elles laisseraient placer un sceau hors page. Le calcul part des POINTS, qui
 * sont exacts, et n'utilise que des constantes publiées — aucune marge en dur.
 * Une seule donnée manquante et on rend null : les bornes du serveur reprennent
 * la main, plutôt qu'un mélange des deux.
 */
function bornesSurPageReelle(src) {
  const pw = nombreOuNull(src?.page_w);
  const ph = nombreOuNull(src?.page_h);
  const m = src?.marges;
  if (!pw || !ph || !m) return null;
  const pct = (pt, total) => Math.round((pt / total) * 10000) / 100;
  const bloc = (largeur_pt, hauteur_pt, basMarge) => {
    if (largeur_pt == null || hauteur_pt == null || basMarge == null) return null;
    return {
      xMin: pct(nombreOuNull(m.gauche_pt) ?? 0, pw),
      xMax: pct(pw - (nombreOuNull(m.droite_pt) ?? 0) - largeur_pt, pw),
      yMin: pct(nombreOuNull(m.haut_pt) ?? 0, ph),
      yMax: pct(ph - basMarge - hauteur_pt, ph),
    };
  };
  return {
    sceau: bloc(nombreOuNull(src.sceau_w), nombreOuNull(src.sceau_h), nombreOuNull(m.bas_sceau_pt)),
    date: bloc(nombreOuNull(src.date?.largeur_pt), nombreOuNull(src.date?.hauteur_pt), nombreOuNull(m.bas_date_pt)),
  };
}

/**
 * Réglages de la date d'un bloc : imprimée ou non, placée librement ou posée
 * sous le sceau, et sa taille. « Sous le sceau » est le défaut du serveur, donc
 * l'état par défaut ici aussi — on ne force personne à placer une date.
 */
function ReglagesDate({ b, set, cible, setCible, cleCible, champ, styleBtn, styleInp, styleMono }) {
  if (b.date_visible === false) {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '6px' }}>
        <input type="checkbox" checked={false} onChange={() => set(p => ({ ...p, date_visible: true }))} />
        <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>Imprimer la date sur le contrat</span>
      </label>
    );
  }
  const place = b.date_x_pct != null && b.date_y_pct != null;
  return (
    <div style={{ marginTop: '7px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input type="checkbox" checked onChange={() => set(p => ({ ...p, date_visible: false }))} />
        <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>Imprimer la date sur le contrat</span>
      </label>
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setCible(cleCible)}
          title="Puis cliquez sur le document, au coin haut-gauche de la date"
          style={{ ...styleBtn, padding: '5px 10px', fontSize: '11.5px', ...(cible === cleCible ? { background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none' } : {}) }}>
          <BI icon="crosshair" style={{ marginRight: '5px' }} />{place ? 'Déplacer la date' : 'Placer la date'}
        </button>
        {place && (
          <button onClick={() => { set(p => ({ ...p, date_x_pct: null, date_y_pct: null })); if (cible === cleCible) setCible('candidat'); }}
            style={{ ...styleBtn, padding: '5px 10px', fontSize: '11.5px' }}>Sous le sceau</button>
        )}
        <input type="number" min={champ?.min ?? undefined} max={champ?.max ?? undefined} value={b.date_taille}
          onChange={e => set(p => ({ ...p, date_taille: e.target.value }))} placeholder={champ?.defaut != null ? String(champ.defaut) : 'taille'}
          title={champ?.min != null ? `Taille de la date en points (${champ.min} à ${champ.max}) — vide = ${champ.defaut ?? 'taille par défaut'}` : 'Taille de la date en points — vide = taille par défaut'}
          style={{ ...styleInp, ...styleMono, width: '78px', padding: '5px 8px', fontSize: '11.5px' }} />
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>
        {place
          ? <>Date placée à <span style={styleMono}>x {b.date_x_pct}% · y {b.date_y_pct}%</span>.</>
          : 'Sans placement, la date se pose sous le sceau.'}
      </div>
    </div>
  );
}

/**
 * Champs de position de la date, pour un bloc (candidat ou entreprise).
 *
 * Trois cas, et un seul est explicite dans la requête :
 *  - date masquée      → date_visible: false, et rien d'autre ;
 *  - date placée       → date_page / date_x_pct / date_y_pct (coin haut-gauche) ;
 *  - date non placée   → on n'envoie AUCUNE position, le serveur la pose sous
 *                        le sceau. Envoyer des coordonnées vides reviendrait à
 *                        placer la date en haut de page.
 */
function blocDate(b, page) {
  if (b.date_visible === false) return { date_visible: false };
  const taille = String(b.date_taille || '').trim();
  return {
    ...(b.date_x_pct != null && b.date_y_pct != null
      ? { date_page: page, date_x_pct: Number(b.date_x_pct), date_y_pct: Number(b.date_y_pct) }
      : {}),
    ...(taille ? { date_taille: Number(taille) } : {}),
  };
}

export function AoContractsModal({ aoId, showNotification, onClose, onGenerated, onRecap }) {
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };
  const jsonH = { ...authH, 'Content-Type': 'application/json' };
  const [step, setStep] = useState('select');
  const [cands, setCands] = useState(null);
  // Adresse de repli pour les candidats SANS email (04.11 : « mets admin@… ;
  // on les transférera à la main ») : retenue pour l'organisation par le
  // serveur, pré-remplie depuis l'aperçu.
  const [emailRepli, setEmailRepli] = useState('');
  const emailValide = (e) => /^[^\s@<>()",;]+@[^\s@<>()",;]+\.[a-z]{2,}$/i.test(String(e || '').trim());
  const [checked, setChecked] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  // (x_pct, y_pct) = COIN HAUT-GAUCHE de la zone, jamais son centre : c'est ce
  // que le serveur dessine, et le repère de l'aperçu est ancré pareil.
  // date_x_pct/date_y_pct à null = la date se pose sous le sceau, côté serveur.
  const [sig, setSig] = useState({ page: 1, x_pct: 60, y_pct: 88, date_visible: true, date_x_pct: null, date_y_pct: null, date_taille: '' });
  const [dateCand, setDateCand] = useState('');       // vide = jour de la génération
  // Contresignature de l'entreprise : bloc optionnel, même sémantique de
  // position que le sceau candidat. Par défaut à gauche, en face du candidat.
  const [ent, setEnt] = useState({ actif: false, x_pct: 12, y_pct: 88, signataire: '', date: '', tampon: false, date_visible: true, date_x_pct: null, date_y_pct: null, date_taille: '' });
  const [cible, setCible] = useState('candidat');     // ce qu'un clic sur l'aperçu déplace
  const [tamponConfig, setTamponConfig] = useState(null); // { present } | null
  // Catalogue des champs, bornes de placement et géométrie de la date, publiés
  // par le serveur : ils bornent le placement AVANT l'envoi, ce qui fait
  // disparaître le recadrage au lieu de l'expliquer après coup.
  const [champs, setChamps] = useState(null);         // catalogue { clé: { defaut, min, max } }
  const [bornes, setBornes] = useState(null);         // { sceau: {...}, date: {...} }
  const [dateGeo, setDateGeo] = useState(null);       // { largeur_pct, hauteur_pct, … }
  const [sceauPt, setSceauPt] = useState(null);       // { largeur_pt, hauteur_pt } du preview
  const [marges, setMarges] = useState(null);         // marges de bornage, en points
  const [pageDim, setPageDim] = useState(null);       // page réelle du template, en points
  // Contrat d'appel de l'aperçu, publié par le serveur. Son absence dit que le
  // back joint est antérieur à l'aperçu — un déploiement, pas un droit.
  const [apercuInfo, setApercuInfo] = useState(null);
  const [varsAuto, setVarsAuto] = useState(null);     // variables remplies par le serveur
  // Taille du sceau telle que le serveur l'a annoncée à l'ouverture : on ne
  // renvoie une taille que si elle s'en écarte.
  const tailleRef = useRef({ w: null, h: null });
  // Aperçu réel du contrat (même moteur que la génération), sans rien écrire.
  const [apercu, setApercu] = useState(null);         // { url, geo } | 'absent' | null
  const [apercuBusy, setApercuBusy] = useState(false);
  const [reperes, setReperes] = useState(false);      // cadres des zones dessinés sur l'aperçu
  const [apercuErreur, setApercuErreur] = useState(null); // réglage refusé par le serveur
  const [busy, setBusy] = useState(false);
  // Avancement de la génération (04.11 : « savoir sur quel contrat on est,
  // 10 %… »). Un contrat par appel : la route est la même, l'écran sait donc
  // lequel est en train d'être généré. null hors génération.
  const [progres, setProgres] = useState(null);       // { fait, total, nom }
  const [result, setResult] = useState(null);
  const [tplVars, setTplVars] = useState([]); // variables détectées du template choisi
  const [vars, setVars] = useState({});       // valeurs saisies (mini-formulaire)
  // Aperçu du VRAI document sous le placeur : GET /templates/:id/preview.pdf
  // rendu via pdf.js (chargé à la volée). Route absente → page factice + note.
  const [pdf, setPdf] = useState(null);       // { doc, numPages } | 'missing' | null (chargement)
  const pdfCanvasRef = useRef(null);
  const PREVIEW_W = 300;

  useEffect(() => {
    if (step !== 'signature' || !templateId || pdf) return;
    let cancelled = false;
    (async () => {
      try {
        // Le PDF est fetché AVEC le Bearer (jamais d'URL directe en <iframe> :
        // le navigateur n'y joint pas le token → 401)
        const r = await fetch(`${BASE}/templates/${templateId}/preview.pdf`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        if (r.status === 404 || r.status === 501) { if (!cancelled) setPdf('missing'); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const buf = await r.arrayBuffer();
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
        // Worker servi INTACT depuis public/ (copié de node_modules par
        // scripts/copy-pdf-worker.js en pre{start,build} — version toujours
        // synchrone avec pdfjs-dist). Ni new URL() ni import : babel-loader
        // transformait le fichier (require() absolus) → « fake worker failed ».
        if (!pdfjs.GlobalWorkerOptions.workerSrc) {
          pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
        }
        const doc = await pdfjs.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPdf({ doc, numPages: doc.numPages });
        setSig(s => ({ ...s, page: doc.numPages })); // défaut : dernière page (la signature y vit)
      } catch (e) { if (!cancelled) { setPdf('missing'); showNotification?.('Aperçu du contrat : ' + (e?.message || 'échec du rendu PDF'), 'error'); } }
    })();
    return () => { cancelled = true; };
  }, [step, templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rend la page choisie sur le canvas (netteté : devicePixelRatio)
  useEffect(() => {
    if (!pdf || pdf === 'missing') return;
    let cancelled = false;
    (async () => {
      const pageNum = Math.min(Math.max(1, Number(sig.page) || 1), pdf.numPages);
      const page = await pdf.doc.getPage(pageNum);
      const canvas = pdfCanvasRef.current;
      if (!canvas || cancelled) return;
      const dpr = window.devicePixelRatio || 1;
      const vp1 = page.getViewport({ scale: 1 });
      // Taille réelle de la page du template, en points. Mesurée sur le document
      // lui-même : ni gabarit A4 supposé, ni reconstitution à partir des bornes.
      setPageDim(d => (d && d.w === vp1.width && d.h === vp1.height ? d : { w: vp1.width, h: vp1.height }));
      const scale = PREVIEW_W / vp1.width;
      const vp = page.getViewport({ scale: scale * dpr });
      canvas.width = vp.width; canvas.height = vp.height;
      canvas.style.width = `${PREVIEW_W}px`;
      canvas.style.height = `${Math.round(vp.height / dpr)}px`;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    })();
    return () => { cancelled = true; };
  }, [pdf, sig.page]);

  // Variables du template sélectionné (GET /templates/:id — {{cle}} et $cle$
  // confondus). Les auto (firstName/lastName/TODAY) sont exclues du formulaire.
  useEffect(() => {
    if (!templateId || !aoId) return;
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/ao/${aoId}/contracts/preview?template_id=${encodeURIComponent(templateId)}`, { headers: authH });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        const d = j.data || j;
        if (!annule && Array.isArray(d.variables_auto)) setVarsAuto(d.variables_auto);
      } catch (e) { /* le catalogue général reste en place */ }
    })();
    return () => { annule = true; };
  }, [templateId, aoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!templateId) { setTplVars([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/templates/${templateId}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        const j = await r.json().catch(() => ({}));
        const t = j.data || j;
        const names = (Array.isArray(t.variables) ? t.variables : []).map(normVarName).filter(Boolean);
        if (!cancelled) { setTplVars([...new Set(names)]); setVars({}); }
      } catch { if (!cancelled) setTplVars([]); }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  // La liste des variables remplies par le serveur vient du serveur. Le jour où
  // il tire la civilité de la fiche du candidat, le champ disparaît de l'écran
  // sans qu'on touche au front — c'est tout l'intérêt de ne pas la tenir ici.
  // Le catalogue décrit chaque variable : ses orthographes, si elle est propre
  // à la personne, d'où elle vient. Le front ne juge plus rien de tout ça.
  const entreeAuto = (v) => (Array.isArray(varsAuto) ? varsAuto : []).find(e => {
    const noms = [e.variable, ...(Array.isArray(e.alias) ? e.alias : [])].filter(Boolean);
    return noms.some(n => normVarName(n).toLowerCase() === v.toLowerCase());
  }) || null;
  const estAuto = (v) => (Array.isArray(varsAuto) && varsAuto.length)
    ? !!entreeAuto(v)
    : CONTRACT_AUTO_VARS.some(a => a.toLowerCase() === v.toLowerCase());
  const fillableVars = tplVars.filter(v => !estAuto(v));
  const autoVars = tplVars.filter(v => estAuto(v));
  // Variables personnelles encore réclamées à la main. Le catalogue tranche
  // quand il connaît la variable ; notre liste locale ne sert plus que pour
  // celles qu'il ne couvre pas — sinon le bandeau crierait sur des champs que
  // le serveur remplit déjà tout seul.
  const varsPersonneSaisies = fillableVars.filter(v => {
    const e = entreeAuto(v);
    return e ? e.personnelle === true : estVarPersonne(v);
  });
  const sourceAuto = (v) => entreeAuto(v)?.source || null;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/ao/${aoId}/contracts/preview`, { headers: authH });   // sans template : catalogue général
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        const arr = j.data?.candidates || j.candidates || j.data || [];
        const list = Array.isArray(arr) ? arr : [];
        setCands(list);
        setChecked(Object.fromEntries(list.map(c => [c.id ?? c.candidate_id, c.eligible !== false])));
        const repliConnu = list.find(c => c.email_repli)?.email_repli || (j.data || j).email_repli || '';
        if (repliConnu) setEmailRepli(r0 => r0 || repliConnu);
        const d = j.data || j;
        // Dates pré-remplies au jour proposé par le serveur : l'utilisateur voit
        // ce qui sera écrit, au lieu de le deviner d'un champ vide.
        if (d.date_defaut) { setDateCand(d.date_defaut); setEnt(e => ({ ...e, date: d.date_defaut })); }
        if (d.tampon_entreprise) setTamponConfig(d.tampon_entreprise);
        setChamps(d.champs || null);
        setBornes(d.bornes || null);
        setDateGeo(d.date || null);
        setSceauPt(d.sceau || null);
        setMarges(d.marges || null);
        setApercuInfo(d.apercu || null);
        setVarsAuto(Array.isArray(d.variables_auto) ? d.variables_auto : null);
        // Taille du sceau : la valeur du serveur, jamais la nôtre. Le catalogue
        // la donne ; à défaut, la géométrie publiée dit la taille en cours.
        // Cette référence sert aussi à savoir si l'utilisatrice l'a changée.
        const cw = lireChamp(d.champs, 'signature.sceau_w');
        const ch = lireChamp(d.champs, 'signature.sceau_h');
        const w0 = cw?.defaut ?? nombreOuNull(d.sceau?.largeur_pt);
        const h0 = ch?.defaut ?? nombreOuNull(d.sceau?.hauteur_pt);
        tailleRef.current = { w: w0, h: h0 };
        if (w0 != null || h0 != null) setSig(s2 => ({ ...s2, sceau_w: w0 ?? s2.sceau_w, sceau_h: h0 ?? s2.sceau_h }));
        const orgNom = APIService.getOrgName?.('') || '';
        if (orgNom) setEnt(e => ({ ...e, signataire: e.signataire || orgNom }));
      } catch (e) { showNotification?.('Aperçu impossible : ' + e.message, 'error'); setCands([]); }
      try {
        const r = await fetch(`${BASE}/templates?type=contrat`, { headers: authH });
        const j = await r.json().catch(() => ({}));
        const arr = j.data || (Array.isArray(j) ? j : []);
        setTemplates(Array.isArray(arr) ? arr : []);
        if (Array.isArray(arr) && arr.length === 1) setTemplateId(String(arr[0].id));
      } catch { setTemplates([]); }
    })();
  }, [aoId]);

  const selectedIds = Object.entries(checked).filter(([, v]) => v).map(([k]) => (Number.isNaN(Number(k)) ? k : Number(k)));

  // Quatre blocs indépendants — sceau candidat, date candidat, sceau
  // entreprise, date entreprise. Chacun porte SA page, ses dimensions de page,
  // ses bornes et ses marges : sur un document dont les blocs vivent sur des
  // pages de formats différents, borner l'un avec les bornes d'un autre place
  // hors page sans que rien ne le signale.
  const geo = apercu && apercu !== 'absent' ? apercu.geo : null;
  const GEO = {
    candidat: geo?.sceau || null,
    date_candidat: geo?.date || null,
    entreprise: geo?.entreprise?.sceau || null,
    date_entreprise: geo?.entreprise?.date_position || null,
  };
  const estDate = (cle) => cle.startsWith('date_');

  // Repli tant qu'aucun aperçu n'a répondu : la page affichée ici, mesurée par
  // pdf.js, et les tailles publiées par le preview. Le sceau et la date n'ont
  // pas la même marge basse, d'où deux calculs distincts.
  const bReplis = bornesSurPageReelle(pageDim && marges && sceauPt && dateGeo ? {
    page_w: pageDim.w, page_h: pageDim.h, marges,
    sceau_w: nombreOuNull(sceauPt.largeur_pt), sceau_h: nombreOuNull(sceauPt.hauteur_pt),
    date: { largeur_pt: nombreOuNull(dateGeo.largeur_pt), hauteur_pt: nombreOuNull(dateGeo.hauteur_pt) },
  } : null);

  const bornesDe = (cle) =>
    lireBornes(GEO[cle], 'bornes')
    || (estDate(cle) ? bReplis?.date : bReplis?.sceau)
    || lireBornes(bornes, estDate(cle) ? 'date' : 'sceau');

  const bornerAvec = (b) => (x, y) => ({
    x: Math.min(b ? b.xMax : 96, Math.max(b ? b.xMin : 0, x)),
    y: Math.min(b ? b.yMax : 96, Math.max(b ? b.yMin : 0, y)),
  });
  const bornerBloc = (cle) => bornerAvec(bornesDe(cle));
  const bSceau = bornesDe('candidat');
  // « Bas droite » et « bas gauche » visent le plus bas et le plus à droite (ou
  // à gauche) que le serveur autorise, au lieu d'un couple de pourcentages
  // écrit en dur : la limite bouge avec la taille du sceau.
  const basDroite = () => { const b = bornesDe('candidat'); const p2 = bornerBloc('candidat')(b ? b.xMax : 60, b ? b.yMax : 88); return { x_pct: p2.x, y_pct: p2.y }; };
  const basGauche = () => { const b = bornesDe('candidat'); const p2 = bornerBloc('candidat')(b ? b.xMin : 12, b ? b.yMax : 88); return { x_pct: p2.x, y_pct: p2.y }; };
  const bDate = bornesDe('date_candidat');

  // Encombrement d'un bloc, en % de SA page. Publié par l'aperçu ; avant lui,
  // rapporté à la page affichée, la seule que le front ait mesurée.
  const enPct = (pt, total) => (pt == null || !total ? null : Math.round((pt / total) * 10000) / 100);
  // En rendu prestataire, ce n'est pas le sceau qui est posé mais le champ de
  // signature du prestataire, d'un autre gabarit. Dessiner le sceau montrerait
  // une emprise que le document n'aura pas.
  const enDocsign = (geo?.rendu_sceau || dateGeo?.rendu_sceau || null) === 'docsign';
  const tailleDe = (cle) => {
    const g = GEO[cle];
    if (!estDate(cle) && enDocsign) {
      const w = nombreOuNull(g?.champ_w_pct ?? geo?.champ_w_pct);
      const h = nombreOuNull(g?.champ_h_pct ?? geo?.champ_h_pct);
      if (w != null || h != null) return { largeur_pct: w, hauteur_pct: h };
    }
    if (g?.largeur_pct != null || g?.hauteur_pct != null) {
      return { largeur_pct: nombreOuNull(g.largeur_pct), hauteur_pct: nombreOuNull(g.hauteur_pct) };
    }
    const src = estDate(cle) ? dateGeo : sceauPt;
    if (!src || !pageDim) return null;
    return {
      largeur_pct: enPct(nombreOuNull(src.largeur_pt), pageDim.w),
      hauteur_pct: enPct(nombreOuNull(src.hauteur_pt), pageDim.h),
    };
  };

  // Les bornes n'arrivent qu'avec le preview, après le rendu initial : la
  // position de départ, et toute position saisie avant un changement de taille
  // de sceau, doivent être ramenées dedans. Sans ça, le premier réglage part
  // avec une valeur que le serveur recadrera en silence.
  const cleBornes = [bSceau?.xMin, bSceau?.xMax, bSceau?.yMin, bSceau?.yMax, bDate?.xMax, bDate?.yMax].join('|');
  useEffect(() => {
    const dans = (b, x, y) => !b || (x >= b.xMin && x <= b.xMax && y >= b.yMin && y <= b.yMax);
    setSig(s2 => {
      const p2 = bornerAvec(bSceau)(Number(s2.x_pct), Number(s2.y_pct));
      const d = s2.date_x_pct != null ? bornerAvec(bDate)(Number(s2.date_x_pct), Number(s2.date_y_pct)) : null;
      if (dans(bSceau, Number(s2.x_pct), Number(s2.y_pct)) && (!d || dans(bDate, Number(s2.date_x_pct), Number(s2.date_y_pct)))) return s2;
      return { ...s2, x_pct: p2.x, y_pct: p2.y, ...(d ? { date_x_pct: d.x, date_y_pct: d.y } : {}) };
    });
    setEnt(e => {
      if (!e.actif) return e;
      const bs = bornesDe('entreprise'); const bd = bornesDe('date_entreprise');
      const p2 = bornerAvec(bs)(Number(e.x_pct), Number(e.y_pct));
      const d = e.date_x_pct != null ? bornerAvec(bd)(Number(e.date_x_pct), Number(e.date_y_pct)) : null;
      if (dans(bs, Number(e.x_pct), Number(e.y_pct)) && (!d || dans(bd, Number(e.date_x_pct), Number(e.date_y_pct)))) return e;
      return { ...e, x_pct: p2.x, y_pct: p2.y, ...(d ? { date_x_pct: d.x, date_y_pct: d.y } : {}) };
    });
  }, [cleBornes]); // eslint-disable-line react-hooks/exhaustive-deps

  const champW = lireChamp(champs, 'signature.sceau_w');
  const champH = lireChamp(champs, 'signature.sceau_h');
  const champTaille = lireChamp(champs, 'signature.date_taille');

  const corpsSignature = () => ({
    page: Number(sig.page) || 1, x_pct: Number(sig.x_pct), y_pct: Number(sig.y_pct),
    // La taille n'est envoyée que si le serveur publie le champ et que la
    // valeur diffère de son défaut : on ne réaffirme pas ce qu'il sait déjà.
    ...(nombreOuNull(sig.sceau_w) != null && Number(sig.sceau_w) !== tailleRef.current.w ? { sceau_w: Number(sig.sceau_w) } : {}),
    ...(nombreOuNull(sig.sceau_h) != null && Number(sig.sceau_h) !== tailleRef.current.h ? { sceau_h: Number(sig.sceau_h) } : {}),
    ...(dateCand.trim() ? { date: dateCand.trim() } : {}),
    ...blocDate(sig, Number(sig.page) || 1),
    ...(ent.actif ? {
      entreprise: {
        page: Number(sig.page) || 1, x_pct: Number(ent.x_pct), y_pct: Number(ent.y_pct),
        ...(ent.signataire.trim() ? { signataire: ent.signataire.trim() } : {}),
        ...(ent.date.trim() ? { date: ent.date.trim() } : {}),
        tampon: !!ent.tampon,
        ...blocDate(ent, Number(sig.page) || 1),
      },
    } : {}),
  });

  /**
   * Aperçu du placement, rendu par le même moteur que la génération réelle :
   * ce qui s'affiche est ce qui sortira. Rien n'est généré, envoyé ni archivé.
   */
  const voirApercu = async (avecReperes = reperes) => {
    if (!templateId) return;
    setApercuBusy(true);
    // Le corps envoyé est mémorisé tel quel : c'est lui qu'on compare à ce que
    // le serveur dit avoir appliqué. Le relire depuis l'état après coup ne
    // prouverait rien — il aurait pu changer entre-temps.
    const envoye = corpsSignature();
    try {
      const chemin = apercuInfo?.chemin
        ? apercuInfo.chemin.replace(':id', aoId).replace('{id}', aoId)
        : `/ao/${aoId}/contracts/apercu`;
      // Les repères dessinent les cadres des zones SUR le PDF. Utile pour
      // régler un placement, trompeur par défaut : « tel qu'il sortira » doit
      // montrer le document, pas nos traits de construction.
      const url0 = `${BASE}${chemin.startsWith('/') ? '' : '/'}${chemin}${chemin.includes('?') ? '&' : '?'}reperes=${avecReperes ? 1 : 0}`;
      const r = await fetch(url0, {
        method: (apercuInfo?.methode || 'POST').toUpperCase(), headers: jsonH,
        body: JSON.stringify({
          template_id: Number(templateId) || templateId,
          ...(selectedIds.length ? { candidate_id: selectedIds[0] } : {}),
          signature: envoye,
        }),
      });
      if (r.status === 404 || r.status === 501) { setApercu('absent'); return; }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        // 403 : la fonctionnalité est coupée pour l'organisation, ou le rôle
        // ne le permet pas. Ce n'est pas une panne, et le dire évite de faire
        // chercher une erreur qui n'existe pas.
        if (r.status === 403) {
          showNotification?.(j.error === 'feature_disabled'
            ? 'L\'aperçu des contrats est désactivé pour votre organisation.'
            : 'Vous n\'avez pas les droits pour l\'aperçu des contrats.', 'error');
          return;
        }
        // 400 : le serveur refuse un réglage et dit lequel. Le message est
        // affiché à côté du champ concerné, et il y reste — une notification
        // qui s'efface pendant qu'on corrige ne sert à rien.
        if (r.status === 400 && (j.error || j.champ)) {
          setApercuErreur({
            message: j.error || 'Réglage refusé.',
            champ: j.champ || null,
            min: nombreOuNull(j.min ?? j.minimum ?? j.valeur_min),
          });
          return;
        }
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      setApercuErreur(null);
      let g = null;
      try { g = JSON.parse(r.headers.get('X-Signature-Geometrie') || 'null'); } catch (e) { g = null; }
      const url = URL.createObjectURL(await r.blob());
      setApercu(a => { if (a && a !== 'absent' && a.url) URL.revokeObjectURL(a.url); return { url, geo: g, envoye }; });
    } catch (e) { showNotification?.('Aperçu impossible : ' + e.message, 'error'); }
    finally { setApercuBusy(false); }
  };

  useEffect(() => () => { setApercu(a => { if (a && a !== 'absent' && a.url) URL.revokeObjectURL(a.url); return null; }); }, []);

  const generate = async (send) => {
    setBusy(true);
    const ids = [...selectedIds];
    const nomDe = (id) => {
      const c = (cands || []).find(x => String(x.id ?? x.candidate_id) === String(id));
      return c ? nameOf(c) : null;
    };
    const corpsCommun = {
      ...(emailValide(emailRepli) ? { email_repli: emailRepli.trim() } : {}),
      template_id: Number(templateId) || templateId,
      signature: corpsSignature(),
      // Variables du template saisies (les vides sont omises) —
      // firstName/lastName/TODAY sont remplies par le back
      ...(Object.values(vars).some(v => String(v || '').trim())
        ? { vars: Object.fromEntries(Object.entries(vars).filter(([, v]) => String(v || '').trim()).map(([k, v]) => [k, String(v).trim()])) }
        : {}),
      ...(send ? { send: true } : {}),
    };
    const results = [];
    const manquantes = [];
    const natLues = [];   // nationalités lues sur les pièces d'identité par le serveur
    let dernier = null;
    try {
      // Un contrat par appel, dans l'ordre de la liste : la même route que
      // la génération groupée, mêmes contrôles et mêmes refus.
      for (let i = 0; i < ids.length; i += 1) {
        setProgres({ fait: i, total: ids.length, nom: nomDe(ids[i]) });
        const r = await fetch(`${BASE}/ao/${aoId}/contracts`, {
          method: 'POST', headers: jsonH,
          body: JSON.stringify({ ...corpsCommun, candidate_ids: [ids[i]] }),
        });
        const j = await r.json().catch(() => ({}));
        const d = j.data || j;
        const lignes = Array.isArray(d.results) ? d.results : [];
        // Un refus qui ne porte sur aucun contrat (modèle, signature, droits)
        // vaudrait pour tous les suivants : on s'arrête et on le dit.
        if (!lignes.length && (!r.ok || j.success === false)) {
          if (i === 0 || r.status === 400 || r.status === 403) throw new Error(j.error || `HTTP ${r.status}`);
          results.push({ candidate_id: Number(ids[i]), nom: nomDe(ids[i]), error: j.error || `HTTP ${r.status}` });
          continue;
        }
        results.push(...lignes);
        if (Array.isArray(d.variables_manquantes)) manquantes.push(...d.variables_manquantes);
        if (Array.isArray(d.nationalites_lues)) natLues.push(...d.nationalites_lues);
        dernier = d;
      }
      setProgres({ fait: ids.length, total: ids.length, nom: null });
      const d = dernier || {};
      const generated = results.filter(x => x.document_id).length;
      setResult({ generated, failed: results.length - generated, results, sent: send,
        date_signature: d.date_signature || null, contresignature: d.contresignature || null,
        position_recadree: d.position_recadree || null,
        variables_manquantes: manquantes,
        nationalites_lues: natLues,
        variables_source: d.variables_source || null,
        variables_partagees: Array.isArray(d.variables_partagees) ? d.variables_partagees : [] });
      if (d.contresignature?.avertissement) showNotification?.(d.contresignature.avertissement, 'info');
      setStep('done');
    } catch (e) {
      const faits = results.filter(x => x.document_id).length;
      showNotification?.(`Erreur génération${faits ? ` (${faits} contrat${faits > 1 ? 's' : ''} déjà généré${faits > 1 ? 's' : ''})` : ''} : ` + e.message, 'error');
      if (faits) {
        setResult({ generated: faits, failed: results.length - faits, results, sent: send,
          variables_manquantes: manquantes, variables_partagees: [] });
        setStep('done');
      }
    }
    finally { setBusy(false); setProgres(null); }
  };

  // Fermeture : si des contrats ont été générés, on rafraîchit le rapport AO
  // (la pièce « Contrat » du dossier candidat apparaît dès que le back l'écrit)
  const close = () => { if (result) onGenerated?.(); onClose(); };

  const nameOf = (c) => c.name || [c.prenom, c.nom].filter(Boolean).join(' ').trim() || `Candidat ${c.id ?? c.candidate_id}`;
  const lbl = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
  const inp = { boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '9px 11px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
  const mono = { fontFamily: "'IBM Plex Mono',monospace" };

  return (
    <>
      <div onClick={busy ? undefined : close} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(600px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', flex: 1 }}>Générer les contrats</span>
          <button onClick={close} disabled={busy} style={{ ...btnGhost, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '12px' }} /></button>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>
          {step === 'select' ? 'Étape 1/2 — choisissez le template et les candidats placés.'
            : step === 'signature' ? 'Étape 2/2 — placez la zone de signature sur la page.'
              : 'Récapitulatif de la génération.'}
        </div>

        {step === 'select' && (
          <>
            <div style={{ ...lbl, marginBottom: '6px' }}>Template de contrat</div>
            {templates.length === 0 ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-3)', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)' }}>
                Aucun template de contrat — ajoutez-en un dans <b>Config → Templates</b> (fichier .docx).
              </div>
            ) : (
              <ErpiaSelect value={templateId} onChange={e => setTemplateId(e.target.value)} style={{ ...inp, width: '100%' }}>
                <option value="">Choisir…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.nom}{t.file_name ? ` — ${t.file_name}` : ''}</option>)}
              </ErpiaSelect>
            )}

            {Array.isArray(cands) && cands.some(c => !String(c.email || '').trim()) && (
              <div style={{ marginTop: '16px', padding: '10px 12px', borderRadius: '9px', border: '1px solid var(--hair)' }}>
                <div style={{ ...lbl, marginBottom: '6px' }}>Adresse pour les candidats sans email</div>
                <input type="email" value={emailRepli} placeholder="adresse de l'équipe"
                  onChange={e => setEmailRepli(e.target.value)}
                  onBlur={() => {
                    if (!emailValide(emailRepli)) return;
                    // Les placés sans email deviennent générables : on les coche.
                    setChecked(p => ({ ...p, ...Object.fromEntries((cands || [])
                      .filter(c => !String(c.email || '').trim() && c.placed !== false)
                      .map(c => [c.id ?? c.candidate_id, true])) }));
                  }}
                  style={{ ...inp, width: '100%' }} />
                <div style={{ fontSize: '11.5px', color: emailRepli && !emailValide(emailRepli) ? 'var(--danger-fg)' : 'var(--text-3)', marginTop: '5px' }}>
                  {emailRepli && !emailValide(emailRepli)
                    ? 'Adresse incomplète.'
                    : `${cands.filter(c => !String(c.email || '').trim()).length} candidat(s) sans email : leurs contrats sont adressés ici, à transférer à la main. L'adresse est retenue pour les prochaines générations.`}
                </div>
              </div>
            )}

            <div style={{ ...lbl, margin: '16px 0 6px' }}>Candidats</div>
            {cands === null ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', padding: '14px 0' }}><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ao-spin .7s linear infinite' }} /> Chargement…</div>
            ) : cands.length === 0 ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun candidat dans cet AO.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '260px', overflowY: 'auto' }}>
                {cands.map(c => {
                  const id = c.id ?? c.candidate_id;
                  const on = !!checked[id];
                  return (
                    <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 11px', borderRadius: '9px', border: '1px solid var(--hair)', cursor: 'pointer', userSelect: 'none', opacity: c.eligible === false && !on ? 0.65 : 1 }}>
                      <input type="checkbox" checked={on} onChange={e => setChecked(p => ({ ...p, [id]: e.target.checked }))}
                        style={{ accentColor: 'var(--accent)', width: '15px', height: '15px', cursor: 'pointer', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(c)}</span>
                      {c.placed && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '5px', background: 'rgba(22,163,74,0.12)', color: 'var(--erp-success)', flex: 'none' }}>placé</span>}
                      {c.eligible === false && <span title={c.reason || ''} style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '5px', background: 'var(--danger-bg)', color: 'var(--danger-fg)', flex: 'none' }}>{c.reason || 'non éligible'}</span>}
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={close} style={btnGhost}>Annuler</button>
              <button onClick={() => setStep('signature')} disabled={!templateId || selectedIds.length === 0}
                style={{ ...btnPrimary, opacity: templateId && selectedIds.length ? 1 : 0.5 }}>
                Continuer ({selectedIds.length}) <BI icon="arrow-right" />
              </button>
            </div>
          </>
        )}

        {step === 'signature' && (
          <>
            {/* Mini-formulaire des variables du template (hors auto) */}
            {fillableVars.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ ...lbl, marginBottom: '8px' }}>Variables du contrat</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '9px' }}>
                  {fillableVars.map(v => {
                    const f = champVar(v) || {};
                    const perso = estVarPersonne(v);
                    return (
                      <div key={v}>
                        <div style={{ fontSize: '11px', color: perso && selectedIds.length > 1 ? '#b45309' : 'var(--text-3)', marginBottom: '4px' }}>
                          {f.label || v}
                          {perso && selectedIds.length > 1 && <BI icon="exclamation-triangle" style={{ marginLeft: '5px' }} />}
                        </div>
                        <input value={vars[v] || ''} onChange={e => setVars(p => ({ ...p, [v]: e.target.value }))}
                          type={f.type || 'text'} placeholder={f.placeholder || ''}
                          style={{ ...inp, width: '100%', padding: '8px 10px', fontSize: '12.5px' }} />
                      </div>
                    );
                  })}
                </div>
                {/* Une valeur saisie ici part à l'identique sur tous les contrats
                    du lot. Sur une donnée propre à la personne, c'est un contrat
                    faux pour tout le monde sauf une. */}
                {varsPersonneSaisies.length > 0 && selectedIds.length > 1 && (
                  <div style={{ marginTop: '9px', display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '9px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.30)' }}>
                    <BI icon="exclamation-triangle" style={{ color: '#b45309', flex: 'none', marginTop: '1px' }} />
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {varsPersonneSaisies.map(libelleVar).join(', ')} — ces informations sont
                      propres à chaque personne, mais la valeur saisie ici sera écrite <b>à l'identique sur les {selectedIds.length} contrats</b>.
                      Saisissez-les par candidat avec <b>« Compléter les infos contrat »</b>, ou générez lot par lot.
                    </div>
                  </div>
                )}
                {varsPersonneSaisies.length > 0 && selectedIds.length === 1 && (
                  <div style={{ marginTop: '7px', fontSize: '11px', color: 'var(--text-3)' }}>
                    Ces valeurs seront écrites sur le contrat de <b style={{ color: 'var(--text-2)' }}>{nameOf(cands?.find(c => String(c.id ?? c.candidate_id) === String(selectedIds[0])) || {})}</b>.
                  </div>
                )}
                {autoVars.length > 0 && (
                  <div style={{ marginTop: '7px', fontSize: '11px', color: 'var(--text-3)' }}>
                    <BI icon="info-circle" style={{ marginRight: '5px' }} />Remplies automatiquement pour chaque candidat :
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px', marginLeft: '4px', verticalAlign: 'middle' }}>
                      {autoVars.map(v => (
                        <code key={v} title={sourceAuto(v) || 'Remplie par le serveur'}
                          style={{ ...mono, fontSize: '10.5px', background: 'var(--code-bg)', padding: '1px 5px', borderRadius: '4px' }}>
                          {libelleVar(v)}
                        </code>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div onClick={e => {
                const b = e.currentTarget.getBoundingClientRect();
                // Le point cliqué EST le coin haut-gauche de la zone. On borne
                // pour qu'elle ne déborde pas de la page.
                const brut = [
                  Math.round(((e.clientX - b.left) / b.width) * 100),
                  Math.round(((e.clientY - b.top) / b.height) * 100),
                ];
                const actif = (cible === 'entreprise' || cible === 'date_entreprise') && !ent.actif ? 'candidat' : cible;
                const { x, y } = bornerBloc(actif)(brut[0], brut[1]);
                if (actif === 'date_candidat') setSig(s2 => ({ ...s2, date_x_pct: x, date_y_pct: y }));
                else if (actif === 'date_entreprise') setEnt(p2 => ({ ...p2, date_x_pct: x, date_y_pct: y }));
                else if (actif === 'entreprise') setEnt(p2 => ({ ...p2, x_pct: x, y_pct: y }));
                else setSig(s2 => ({ ...s2, x_pct: x, y_pct: y }));
              }}
                title={CIBLE_AIDE[cible] || CIBLE_AIDE.candidat}
                style={{ position: 'relative', width: `${PREVIEW_W}px`, ...(pdf && pdf !== 'missing' ? {} : { aspectRatio: '1 / 1.414' }), border: '1px solid var(--border)', borderRadius: '8px', background: '#fff', cursor: 'crosshair', flex: 'none', overflow: 'hidden' }}>
                {pdf && pdf !== 'missing' ? (
                  /* Le VRAI document (page rendue par pdf.js) */
                  <canvas ref={pdfCanvasRef} style={{ display: 'block' }} />
                ) : (
                  <>
                    {[14, 22, 30, 38, 46, 54].map(t => <div key={t} style={{ position: 'absolute', left: '10%', right: t % 3 ? '14%' : '30%', top: `${t}%`, height: '3px', borderRadius: '2px', background: 'var(--code-bg)' }} />)}
                    <div style={{ position: 'absolute', left: '10%', right: '10%', top: '62%', fontSize: '10.5px', color: 'var(--text-faint)', textAlign: 'center', lineHeight: 1.5 }}>
                      {pdf === 'missing'
                        ? 'Aperçu du document indisponible — utilisez les presets ou les %.'
                        : 'Chargement de l\'aperçu du document…'}
                    </div>
                  </>
                )}
                {/* Repères ancrés par leur COIN HAUT-GAUCHE, comme le serveur
                    dessine : ce qu'on voit ici est ce qui sera imprimé. */}
                <Repere x={sig.x_pct} y={sig.y_pct} actif={cible === 'candidat'} w={tailleDe('candidat')?.largeur_pct} h={tailleDe('candidat')?.hauteur_pct}
                  fond="var(--accent)" texte="var(--accent-fg)" libelle="Signé par : candidat" />
                {sig.date_visible !== false && sig.date_x_pct != null && (
                  <Repere x={sig.date_x_pct} y={sig.date_y_pct} actif={cible === 'date_candidat'}
                    w={tailleDe('date_candidat')?.largeur_pct} h={tailleDe('date_candidat')?.hauteur_pct}
                    fond="var(--accent)" texte="var(--accent-fg)" libelle="Date" fin />
                )}
                {ent.actif && (
                  <Repere x={ent.x_pct} y={ent.y_pct} actif={cible === 'entreprise'} w={tailleDe('entreprise')?.largeur_pct} h={tailleDe('entreprise')?.hauteur_pct}
                    fond="#0f1b31" texte="#fff" libelle={`Signé par : entreprise${ent.tampon ? ' + tampon' : ''}`} />
                )}
                {ent.actif && ent.date_visible !== false && ent.date_x_pct != null && (
                  <Repere x={ent.date_x_pct} y={ent.date_y_pct} actif={cible === 'date_entreprise'}
                    w={tailleDe('date_entreprise')?.largeur_pct} h={tailleDe('date_entreprise')?.hauteur_pct}
                    fond="#0f1b31" texte="#fff" libelle="Date" fin />
                )}
              </div>
              <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <div style={{ ...lbl, marginBottom: '5px' }}>Page</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="number" min="1" max={pdf && pdf !== 'missing' ? pdf.numPages : undefined} value={sig.page}
                      onChange={e => setSig(s => ({ ...s, page: e.target.value }))} style={{ ...inp, ...mono, width: '90px' }} />
                    {pdf && pdf !== 'missing' && <span style={{ ...mono, fontSize: '12px', color: 'var(--text-3)' }}>/ {pdf.numPages}</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>
                    {pdf && pdf !== 'missing' ? 'L\'aperçu montre la page choisie — cliquez dessus pour placer la signature.' : 'Numéro de la page du contrat où placer la signature (souvent la dernière).'}
                  </div>
                </div>
                <div>
                  <div style={{ ...lbl, marginBottom: '5px' }}>Position</div>
                  <span style={{ ...mono, fontSize: '12px', color: 'var(--text-2)' }}>x {sig.x_pct}% · y {sig.y_pct}%</span>
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>Coin haut-gauche de la zone de signature.</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {pdf && pdf !== 'missing' && (
                    <button onClick={() => setSig(s => ({ ...s, page: pdf.numPages, ...basDroite() }))} style={{ ...btnGhost, padding: '6px 11px', fontSize: '12px' }}>Bas droite — dernière page</button>
                  )}
                  <button onClick={() => setSig(s => ({ ...s, ...basDroite() }))} style={{ ...btnGhost, padding: '6px 11px', fontSize: '12px' }}>Bas droite</button>
                  <button onClick={() => setSig(s => ({ ...s, ...basGauche() }))} style={{ ...btnGhost, padding: '6px 11px', fontSize: '12px' }}>Bas gauche</button>
                </div>
                {bSceau && (
                  <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                    Le sceau tient jusqu'à <span style={mono}>y {bSceau.yMax}%</span> — au-delà, il sortirait de la page.
                    {bDate && <> La date, elle, descend jusqu'à <span style={mono}>y {bDate.yMax}%</span>.</>}
                    {!tailleDe('candidat') && <> Le cadre exact des zones s'affiche dès que l'aperçu du document est chargé.</>}
                  </div>
                )}
                {(champW || champH || sceauPt) && (
                  <div>
                    <div style={{ ...lbl, marginBottom: '5px' }}>Taille du sceau</div>
                    <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                        largeur
                        <input type="number" min={champW?.min ?? undefined} max={champW?.max ?? undefined} value={sig.sceau_w ?? ''}
                          onChange={e => { setApercuErreur(null); setSig(s => ({ ...s, sceau_w: e.target.value })); }}
                          style={{ ...inp, ...mono, width: '80px', padding: '5px 8px', fontSize: '11.5px' }} />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                        hauteur
                        <input type="number" min={champH?.min ?? undefined} max={champH?.max ?? undefined} value={sig.sceau_h ?? ''}
                          onChange={e => { setApercuErreur(null); setSig(s => ({ ...s, sceau_h: e.target.value })); }}
                          style={{ ...inp, ...mono, width: '80px', padding: '5px 8px', fontSize: '11.5px' }} />
                      </label>
                      {(nombreOuNull(sig.sceau_w) !== tailleRef.current.w || nombreOuNull(sig.sceau_h) !== tailleRef.current.h) && (
                        <button onClick={() => setSig(s => ({ ...s, sceau_w: tailleRef.current.w ?? s.sceau_w, sceau_h: tailleRef.current.h ?? s.sceau_h }))}
                          style={{ ...btnGhost, padding: '5px 10px', fontSize: '11.5px' }}>Taille par défaut</button>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>
                      En points{champW?.min != null ? ` — largeur ${champW.min} à ${champW.max}` : ''}{champH?.min != null ? `, hauteur ${champH.min} à ${champH.max}` : ''}.
                      Vaut pour les deux sceaux.
                    </div>
                    {/* Le refus du serveur reste sous les champs tant qu'il n'est
                        pas levé : c'est là qu'on corrige. */}
                    {apercuErreur && (
                      <div style={{ marginTop: '8px', padding: '9px 11px', borderRadius: '9px', background: 'rgba(226,35,26,0.08)', border: '1px solid rgba(226,35,26,0.28)' }}>
                        <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>{apercuErreur.message}</div>
                        {apercuErreur.min != null && (
                          <button onClick={() => {
                            const champ = /hauteur|sceau_h/i.test(apercuErreur.champ || apercuErreur.message) ? 'sceau_h' : 'sceau_w';
                            setSig(s2 => ({ ...s2, [champ]: apercuErreur.min }));
                            setApercuErreur(null);
                          }} style={{ ...btnGhost, marginTop: '7px', padding: '5px 10px', fontSize: '11.5px' }}>
                            Utiliser {apercuErreur.min} pt
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => voirApercu()} disabled={!templateId || apercuBusy}
                  title="Rend le contrat tel qu'il sortira, sans rien générer ni envoyer"
                  style={{ ...btnGhost, padding: '7px 12px', fontSize: '12.5px', justifyContent: 'center' }}>
                  <BI icon={apercuBusy ? 'arrow-repeat' : 'eye'} style={apercuBusy ? { marginRight: '6px', display: 'inline-block', animation: 'ao-spin .7s linear infinite' } : { marginRight: '6px' }} />
                  {apercuBusy ? 'Rendu…' : 'Voir le contrat tel qu\'il sortira'}
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '11.5px', color: 'var(--text-3)' }}
                  title="Dessine les cadres des zones sur l'aperçu, pour vérifier un placement. Ces traits n'existent pas sur le contrat généré.">
                  <input type="checkbox" checked={reperes} disabled={apercuBusy}
                    onChange={e => {
                      const v = e.target.checked; setReperes(v);
                      // Un aperçu déjà affiché deviendrait périmé : on le refait
                      // avec le choix qui vient d'être fait.
                      if (apercu && apercu !== 'absent') voirApercu(v);
                    }} />
                  Afficher les cadres des zones (aperçu seulement)
                </label>
                <div>
                  <div style={{ ...lbl, marginBottom: '5px' }}>Date de signature du candidat</div>
                  <input type="date" value={dateCand} onChange={e => setDateCand(e.target.value)} style={{ ...inp, ...mono, width: '100%' }} />
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>Vide = le jour de la génération.</div>
                  <ReglagesDate b={sig} set={setSig} cible={cible} setCible={setCible} cleCible="date_candidat" champ={champTaille}
                    styleBtn={btnGhost} styleInp={inp} styleMono={mono} />
                </div>

                <div style={{ borderTop: '1px solid var(--hair)', paddingTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={ent.actif}
                      onChange={e => { const v = e.target.checked; setEnt(p2 => ({ ...p2, actif: v })); setCible(v ? 'entreprise' : 'candidat'); }} />
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>Contresigner au nom de l'entreprise</span>
                  </label>
                  {ent.actif && (
                    <div style={{ marginTop: '9px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {[['candidat', '✍ Candidat'], ['entreprise', '🏢 Entreprise']].map(([k, txt]) => (
                          <button key={k} onClick={() => setCible(k)}
                            title="Choisissez qui vous placez, puis cliquez sur le document"
                            style={{ ...btnGhost, padding: '5px 10px', fontSize: '11.5px', ...(cible === k ? { background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none' } : {}) }}>{txt}</button>
                        ))}
                      </div>
                      <div>
                        <div style={{ ...lbl, marginBottom: '5px' }}>Signataire</div>
                        <input value={ent.signataire} onChange={e => setEnt(p2 => ({ ...p2, signataire: e.target.value }))}
                          placeholder="Nom de l'entreprise ou du signataire" style={{ ...inp, width: '100%', fontSize: '12.5px' }} />
                      </div>
                      <div>
                        <div style={{ ...lbl, marginBottom: '5px' }}>Date de contresignature</div>
                        <input type="date" value={ent.date} onChange={e => setEnt(p2 => ({ ...p2, date: e.target.value }))} style={{ ...inp, ...mono, width: '100%' }} />
                        <ReglagesDate b={ent} set={setEnt} cible={cible} setCible={setCible} cleCible="date_entreprise" champ={champTaille}
                          styleBtn={btnGhost} styleInp={inp} styleMono={mono} />
                      </div>
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: tamponConfig?.present ? 'pointer' : 'not-allowed', opacity: tamponConfig?.present ? 1 : 0.55 }}
                          title={tamponConfig?.present ? 'Le tampon enregistré sera apposé à côté de la signature' : 'Aucun tampon enregistré — ajoutez-en un dans Configuration › Signature électronique'}>
                          <input type="checkbox" checked={ent.tampon} disabled={!tamponConfig?.present}
                            onChange={e => setEnt(p2 => ({ ...p2, tampon: e.target.checked }))} />
                          <span style={{ fontSize: '12.5px', color: 'var(--text-strong)' }}>Apposer le tampon de l'entreprise</span>
                        </label>
                        {tamponConfig && !tamponConfig.present && (
                          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>
                            Aucun tampon enregistré — Configuration › Signature électronique.
                          </div>
                        )}
                      </div>
                      <span style={{ ...mono, fontSize: '11.5px', color: 'var(--text-2)' }}>entreprise : x {ent.x_pct}% · y {ent.y_pct}%</span>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  La zone affichera le nom + prénom + ID du candidat comme signature authentifiée.
                </div>
              </div>
            </div>

            {apercu === 'absent' && (
              <div style={{ marginTop: '14px', fontSize: '11.5px', color: 'var(--text-3)', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)' }}>
                L'aperçu du contrat n'est pas encore disponible sur ce serveur — le placement reste réglable ici.
              </div>
            )}

            {apercu && apercu !== 'absent' && (
              <div style={{ marginTop: '14px' }}>
                {/* Le recadrage n'est plus silencieux : si le serveur a dû
                    remonter le sceau pour qu'il tienne, on le dit en clair. */}
                {recadragesAMontrer(apercu.geo?.recadrages).map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '9px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.30)', marginBottom: '10px' }}>
                    <BI icon="arrows-collapse" style={{ color: '#b45309', flex: 'none', marginTop: '1px' }} />
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {NOM_BLOC[r.bloc] ? <>Position de {NOM_BLOC[r.bloc]} ramenée</> : 'Position ramenée'} à{' '}
                      <b style={mono}>x {r.applique?.x_pct}% · y {r.applique?.y_pct}%</b>.
                      {r.raison ? ` ${r.raison}` : ''}
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ ...lbl }}>Aperçu du contrat</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>Rien n'a été généré ni envoyé.</span>
                  <a href={apercu.url} target="_blank" rel="noreferrer" style={{ ...btnGhost, marginLeft: 'auto', padding: '4px 9px', fontSize: '11.5px' }}>Ouvrir en grand</a>
                </div>
                <iframe /* pas de sandbox : cf. DocumentViewer */src={apercu.url} title="Aperçu du contrat"
                  style={{ width: '100%', height: '380px', border: '1px solid var(--border)', borderRadius: '9px', background: '#fff' }} />

                {/* Envoyé contre appliqué. Deux référentiels qui divergent ne se
                    voient pas sur un PDF ; côte à côte, ils sautent aux yeux. */}
                {apercu.geo && (() => {
                  const env = apercu.envoye;
                  const lignes = [
                    ['Signature du candidat', env, apercu.geo.sceau],
                    ['Date du candidat', env ? { x_pct: env.date_x_pct, y_pct: env.date_y_pct } : null, apercu.geo.date],
                    ['Contresignature', env?.entreprise, apercu.geo.entreprise?.sceau],
                    ['Date de l\'entreprise', env?.entreprise ? { x_pct: env.entreprise.date_x_pct, y_pct: env.entreprise.date_y_pct } : null, apercu.geo.entreprise?.date_position],
                  ].filter(([, e, a]) => e && a && (nombreOuNull(e.x_pct) != null || nombreOuNull(e.y_pct) != null));
                  if (!lignes.length) return null;
                  return (
                    <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-3)' }}>
                      <div style={{ ...lbl, marginBottom: '5px' }}>Envoyé · appliqué</div>
                      {lignes.map(([nom, e, a], i) => {
                        const ax = nombreOuNull(a.x_pct); const ay = nombreOuNull(a.y_pct);
                        const ecart = (v1, v2) => (v1 == null || v2 == null ? false : Math.abs(v1 - v2) > 1);
                        const devie = ecart(nombreOuNull(e.x_pct), ax) || ecart(nombreOuNull(e.y_pct), ay);
                        return (
                          <div key={i} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '2px 0', color: devie ? '#b45309' : 'var(--text-3)' }}>
                            <span style={{ minWidth: '150px' }}>{nom}</span>
                            <span style={mono}>x {e.x_pct}% · y {e.y_pct}%</span>
                            <span>→</span>
                            <span style={mono}>x {ax}% · y {ay}%</span>
                            {a.page != null && <span>page {a.page}</span>}
                            {a.ancrage && <span>({a.ancrage})</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {progres && progres.total > 0 && (() => {
              const pct = Math.round((progres.fait / progres.total) * 100);
              return (
                <div role="status" aria-live="polite" style={{ marginTop: '16px', padding: '11px 13px', borderRadius: '10px', border: '1px solid var(--hair)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--text-strong)' }}>
                    <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ao-spin .7s linear infinite' }} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {progres.nom ? `Contrat de ${progres.nom} en cours` : 'Fin de la génération'}
                    </span>
                    <span style={{ ...mono, fontSize: '11.5px', color: 'var(--text-3)' }}>{Math.min(progres.fait + 1, progres.total)}/{progres.total} · {pct} %</span>
                  </div>
                  <div style={{ marginTop: '8px', height: '6px', borderRadius: '3px', background: 'var(--hair)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', transition: 'width 120ms ease' }} />
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => setStep('select')} disabled={busy} style={btnGhost}>Retour</button>
              <button onClick={() => generate(false)} disabled={busy} style={{ ...btnGhost, opacity: busy ? 0.6 : 1 }} title="Génère les contrats en brouillon (authentifié signd)">
                <BI icon="file-earmark" /> Générer en brouillon
              </button>
              <button onClick={() => generate(true)} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
                <BI icon={busy ? 'arrow-repeat' : 'send'} /> {busy ? (progres ? `Génération ${Math.min(progres.fait + 1, progres.total)}/${progres.total}…` : 'Génération…') : `Générer et envoyer (${selectedIds.length})`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12.5px', fontWeight: 600, padding: '6px 12px', borderRadius: '8px', background: 'var(--erp-success-soft)', color: 'var(--erp-success)' }}>
                <BI icon="patch-check" /> Contrats authentifiés signd
              </span>
              <span style={{ fontSize: '12.5px', fontWeight: 600, padding: '6px 12px', borderRadius: '8px', background: 'rgba(22,163,74,0.12)', color: 'var(--erp-success)' }}>
                ✓ {result.generated} contrat{result.generated > 1 ? 's' : ''} archivé{result.generated > 1 ? 's' : ''}
              </span>
              {result.failed > 0 && (
                <span style={{ fontSize: '12.5px', fontWeight: 600, padding: '6px 12px', borderRadius: '8px', background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                  ✗ {result.failed} en échec
                </span>
              )}
            </div>

            {/* Ce qui a réellement été apposé : dates et contresignature. */}
            {(result.date_signature || result.contresignature) && (
              <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                {result.date_signature && <div>Date de signature : <strong style={{ color: 'var(--text-strong)' }}>{result.date_signature}</strong></div>}
                {result.contresignature && (
                  <div>
                    Contresigné par <strong style={{ color: 'var(--text-strong)' }}>{result.contresignature.signataire || 'l\'entreprise'}</strong>
                    {result.contresignature.date ? ` le ${result.contresignature.date}` : ''}
                    {result.contresignature.tampon ? ' · tampon apposé' : ''}
                  </div>
                )}
                {result.contresignature?.avertissement && (
                  <div style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)', color: '#b45309' }}>
                    <BI icon="exclamation-triangle" style={{ marginRight: '6px' }} />{result.contresignature.avertissement}
                  </div>
                )}
                {/* Le sceau ne tenait pas où il était demandé : le dire, sinon
                    l'utilisatrice croit à un placement qui n'a pas été respecté. */}
                {/* D'où vient chaque valeur. Les absentes d'abord : ce sont
                    des champs vides sur le contrat, donc la seule ligne qui
                    demande un geste. */}
                {result.variables_source && (() => {
                  const entrees = Object.entries(result.variables_source);
                  if (!entrees.length) return null;
                  const absentes = entrees.filter(([, o]) => estAbsente(o));
                  const remplies = entrees.filter(([, o]) => !estAbsente(o));
                  return (
                    <div style={{ marginTop: '8px' }}>
                      {absentes.length > 0 && (
                        <div style={{ padding: '10px 12px', borderRadius: '9px', background: 'rgba(226,35,26,0.08)', border: '1px solid rgba(226,35,26,0.28)', marginBottom: '6px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--brand)', marginBottom: '3px' }}>
                            <BI icon="dash-circle" style={{ marginRight: '6px' }} />
                            {absentes.length === 1 ? 'Un champ est resté vide sur le contrat' : `${absentes.length} champs sont restés vides sur le contrat`}
                          </div>
                          <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                            {absentes.map(([v]) => libelleVar(v)).join(', ')}
                          </div>
                        </div>
                      )}
                      {remplies.length > 0 && (
                        <details>
                          <summary style={{ fontSize: '11.5px', color: 'var(--text-3)', cursor: 'pointer' }}>
                            D'où viennent les valeurs ({remplies.length})
                          </summary>
                          <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {remplies.map(([v, o]) => (
                              <div key={v} style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>
                                {libelleVar(v)} — {ORIGINE_VAR[o] || o}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })()}
                {result.nationalites_lues?.length > 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '9px', border: '1px solid var(--hair)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--erp-success)', marginBottom: '4px' }}>
                      <BI icon="person-vcard" style={{ marginRight: '6px' }} />
                      {result.nationalites_lues.length} nationalité{result.nationalites_lues.length > 1 ? 's' : ''} lue{result.nationalites_lues.length > 1 ? 's' : ''} sur la pièce d'identité
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                      Écrites sur la fiche et dans le contrat : la bande lisible par machine de la pièce a été vérifiée.
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-2)' }}>
                      {result.nationalites_lues.map(n => `${[n.prenom, n.nom].filter(Boolean).join(' ')} — ${n.nationalite}`).join(' · ')}
                    </div>
                  </div>
                )}
                {result.variables_manquantes?.length > 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '9px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.30)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#b45309', marginBottom: '4px' }}>
                      <BI icon="pencil-square" style={{ marginRight: '6px' }} />
                      {result.variables_manquantes.length} contrat{result.variables_manquantes.length > 1 ? 's' : ''} à compléter
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                      Ces contrats sont générés, mais des informations manquaient sur la fiche — complétez-les avant envoi.
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {result.variables_manquantes.map((m, i) => (
                        <div key={i} style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>
                          <b style={{ color: 'var(--text-strong)' }}>{[m.prenom, m.nom].filter(Boolean).join(' ') || `Candidat ${m.candidate_id}`}</b>
                          {' — '}{(m.variables || []).map(libelleVar).join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.variables_partagees?.length > 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '9px', background: 'rgba(226,35,26,0.08)', border: '1px solid rgba(226,35,26,0.28)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--brand)', marginBottom: '4px' }}>
                      <BI icon="exclamation-octagon" style={{ marginRight: '6px' }} />Même valeur sur plusieurs contrats
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                      {result.variables_partagees.map((p2, i) => (
                        <div key={i}>
                          {libelleVar(p2.variable)} — écrite à l'identique sur {p2.candidats ?? p2.nombre ?? '?'} contrats. À vérifier avant envoi.
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {recadragesAMontrer(result.position_recadree).map((r, i) => (
                  <div key={i} style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)', color: '#b45309' }}>
                    <BI icon="arrows-collapse" style={{ marginRight: '6px' }} />
                    {NOM_BLOC[r.bloc] ? <>Position de {NOM_BLOC[r.bloc]} ajustée</> : 'Position ajustée'} à{' '}
                    <span style={mono}>x {r.applique?.x_pct}% · y {r.applique?.y_pct}%</span>.
                    {r.raison ? ` ${r.raison}` : ''}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '280px', overflowY: 'auto' }}>
              {result.results.map((x, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 11px', borderRadius: '8px', border: '1px solid var(--hair)', fontSize: '12.5px' }}>
                  <BI icon={x.error ? 'x-circle-fill' : 'check-circle-fill'} style={{ color: x.error ? 'var(--danger-fg)' : 'var(--erp-success)', flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {x.prenom} {x.nom}
                  </span>
                  {x.auto_signed && (
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'var(--erp-success-soft)', color: 'var(--erp-success)' }}>
                      <BI icon="patch-check" /> Authentifié
                    </span>
                  )}
                  {x.a_transferer && (
                    <span title={`Contrat adressé à ${x.email_repli || 'l’adresse de repli'} : à transférer au candidat`}
                      style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}>
                      <BI icon="forward" /> À transférer
                    </span>
                  )}
                  {x.verify_url && (
                    <a href={x.verify_url} target="_blank" rel="noreferrer"
                       style={{ fontSize: '11px', color: 'var(--text-2)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <BI icon="box-arrow-up-right" /> Vérifier
                    </a>
                  )}
                  {x.error ? (
                    <span style={{ color: 'var(--danger-fg)', fontSize: '11.5px' }}>{x.error}</span>
                  ) : x.envelope_id && (
                    <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-3)' }}>{x.envelope_id}</code>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              {/* Les salaires, dates de démarrage et nationalités enregistrés
                  partent dans le classeur Excel du client : on enchaîne. */}
              {onRecap && (
                <button onClick={onRecap} className="erp-btn erp-btn--secondary"
                  title="Remplir le classeur Excel du client avec les salaires, dates de démarrage et nationalités des candidats">
                  <BI icon="file-earmark-spreadsheet" /> Remplir le récapitulatif client
                </button>
              )}
              <button onClick={close} style={btnPrimary}><BI icon="check-lg" /> Fermer</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
